import { useState, useEffect, createContext, useContext, useRef, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation, useParams } from "react-router-dom";
import axios from "axios";
import { Eye, Shield, Target, Zap, Building2, Landmark, Factory, Cpu, MapPin, Award, Languages, Briefcase, GraduationCap, X } from "lucide-react";
import LoadingDemo from "./LoadingDemo";
import { Wordmark } from "@/components/Wordmark";

// Lazy wrapper for Loading Demo
const LoadingDemoPage = () => <LoadingDemo />;

// ScrollToTop - scrollt bei Seitenwechsel automatisch nach oben
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

// ============================================
// SEO / Document-Metadaten
// Imperatives Upsert vorhandener <head>-Tags (mutiert die statischen
// Defaults aus index.html statt sie zu duplizieren). Bewusst nicht das
// native React-19-Hoisting, weil das doppelte <title> neben den
// index.html-Defaults erzeugen würde.
// ============================================
const SITE_URL = "https://clavis-cyber.de";

const _enToDe = (p) => {
  if (p === "/en") return "/";
  if (p.startsWith("/en/")) return p.slice(3);
  return p;
};

const _upsertMeta = (attr, key, content) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const _upsertLink = (rel, href, hreflang) => {
  const sel = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]`;
  let el = document.head.querySelector(sel);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (hreflang) el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const Seo = ({ title, description, type = "website", image, jsonLd = null, noindex = false }) => {
  const { lang } = useLanguage();
  const { pathname } = useLocation();
  const jsonLdStr = jsonLd ? JSON.stringify(jsonLd) : null;

  useEffect(() => {
    const dePath = _enToDe(pathname);
    const enPath = dePath === "/" ? "/en" : "/en" + dePath;
    const canonical = SITE_URL + pathname;
    const ogImage = SITE_URL + (image || "/og-default.png");
    const desc = (description || "").replace(/\s+/g, " ").trim().slice(0, 300);

    document.title = title;
    document.documentElement.lang = lang;

    _upsertMeta("name", "description", desc);
    _upsertMeta("property", "og:type", type);
    _upsertMeta("property", "og:site_name", "clavis/cyber");
    _upsertMeta("property", "og:title", title);
    _upsertMeta("property", "og:description", desc);
    _upsertMeta("property", "og:url", canonical);
    _upsertMeta("property", "og:image", ogImage);
    _upsertMeta("property", "og:locale", lang === "de" ? "de_DE" : "en_US");
    _upsertMeta("name", "twitter:card", "summary_large_image");
    _upsertMeta("name", "twitter:title", title);
    _upsertMeta("name", "twitter:description", desc);
    _upsertMeta("name", "twitter:image", ogImage);

    if (noindex) {
      _upsertMeta("name", "robots", "noindex, nofollow");
    } else {
      const robots = document.head.querySelector('meta[name="robots"]');
      if (robots) robots.remove();
    }

    _upsertLink("canonical", canonical);
    _upsertLink("alternate", SITE_URL + dePath, "de");
    _upsertLink("alternate", SITE_URL + enPath, "en");
    _upsertLink("alternate", SITE_URL + dePath, "x-default");

    const LD_ID = "seo-jsonld";
    let ld = document.getElementById(LD_ID);
    if (jsonLdStr) {
      if (!ld) {
        ld = document.createElement("script");
        ld.type = "application/ld+json";
        ld.id = LD_ID;
        document.head.appendChild(ld);
      }
      ld.textContent = jsonLdStr;
    } else if (ld) {
      ld.remove();
    }
  }, [title, description, type, image, jsonLdStr, noindex, pathname, lang]);

  return null;
};

// ============================================
// PREMIUM ANIMATION HOOKS & COMPONENTS
// ============================================

// 1. SCROLL REVEAL HOOK
const useScrollReveal = (options = {}) => {
  const ref = useRef(null);
  const [isRevealed, setIsRevealed] = useState(false);
  
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsRevealed(true);
          observer.unobserve(element);
        }
      },
      { threshold: options.threshold || 0.1, rootMargin: options.rootMargin || '0px' }
    );
    
    observer.observe(element);
    return () => observer.disconnect();
  }, [options.threshold, options.rootMargin]);
  
  return [ref, isRevealed];
};

// Scroll Reveal Wrapper Component
const ScrollReveal = ({ children, className = '', direction = 'up', delay = 0 }) => {
  const [ref, isRevealed] = useScrollReveal();
  
  const directionClass = {
    up: 'scroll-reveal',
    left: 'scroll-reveal-left',
    right: 'scroll-reveal-right',
    scale: 'scroll-reveal-scale'
  }[direction] || 'scroll-reveal';
  
  return (
    <div 
      ref={ref} 
      className={`${directionClass} ${isRevealed ? 'revealed' : ''} ${className}`}
      style={{ transitionDelay: `${delay}s` }}
    >
      {children}
    </div>
  );
};

// 3. COUNTER ANIMATION HOOK
const useCountUp = (end, duration = 2000, startOnView = true) => {
  const [count, setCount] = useState(0);
  const [ref, isVisible] = useScrollReveal();
  const hasAnimated = useRef(false);
  
  useEffect(() => {
    if (!startOnView || !isVisible || hasAnimated.current) return;
    hasAnimated.current = true;
    
    let startTime;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }, [end, duration, isVisible, startOnView]);
  
  return [ref, count];
};

// Counter Component
const AnimatedCounter = ({ end, suffix = '', prefix = '', duration = 2000, className = '' }) => {
  const [ref, count] = useCountUp(end, duration);
  return <span ref={ref} className={className}>{prefix}{count}{suffix}</span>;
};

// 4. TYPING EFFECT COMPONENT
const TypingText = ({ text, speed = 50, delay = 0, className = '', onComplete }) => {
  const [displayText, setDisplayText] = useState('');
  const [showCursor, setShowCursor] = useState(true);
  const [isComplete, setIsComplete] = useState(false);
  
  useEffect(() => {
    setDisplayText('');
    setIsComplete(false);
    setShowCursor(true);
    
    const timeout = setTimeout(() => {
      let i = 0;
      const interval = setInterval(() => {
        if (i < text.length) {
          setDisplayText(text.slice(0, i + 1));
          i++;
        } else {
          clearInterval(interval);
          setIsComplete(true);
          if (onComplete) onComplete();
          // Hide cursor after completion
          setTimeout(() => setShowCursor(false), 2000);
        }
      }, speed);
      
      return () => clearInterval(interval);
    }, delay);
    
    return () => clearTimeout(timeout);
  }, [text, speed, delay, onComplete]);
  
  return (
    <span className={className}>
      {displayText}
      {showCursor && <span className="typing-cursor" />}
    </span>
  );
};

// 5. PARALLAX HOOK
const useParallax = (speed = 0.5) => {
  const ref = useRef(null);
  
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const rect = element.getBoundingClientRect();
      const elementTop = rect.top + scrollY;
      const offset = (scrollY - elementTop) * speed;
      element.style.transform = `translateY(${offset}px)`;
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [speed]);
  
  return ref;
};

// 7. 3D TILT CARD COMPONENT
const TiltCard = ({ children, className = '', intensity = 10 }) => {
  const cardRef = useRef(null);
  
  const handleMouseMove = useCallback((e) => {
    const card = cardRef.current;
    if (!card) return;
    
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    
    const rotateX = ((y - centerY) / centerY) * -intensity;
    const rotateY = ((x - centerX) / centerX) * intensity;
    
    card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateZ(10px)`;
    
    // Update CSS variable for glow effect
    const percentX = (x / rect.width) * 100;
    const percentY = (y / rect.height) * 100;
    card.style.setProperty('--mouse-x', `${percentX}%`);
    card.style.setProperty('--mouse-y', `${percentY}%`);
  }, [intensity]);
  
  const handleMouseLeave = useCallback(() => {
    const card = cardRef.current;
    if (!card) return;
    card.style.transform = 'perspective(1000px) rotateX(0) rotateY(0) translateZ(0)';
  }, []);
  
  return (
    <div 
      ref={cardRef}
      className={`tilt-card ${className}`}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{ transition: 'transform 0.1s ease-out' }}
    >
      {children}
    </div>
  );
};

// 8. INTERACTIVE PARTICLE BACKGROUND
const ParticleBackground = ({ particleCount = 50 }) => {
  const canvasRef = useRef(null);
  const mouseRef = useRef({ x: 0, y: 0 });
  const particlesRef = useRef([]);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    let animationId;
    
    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);
    
    // Initialize particles
    particlesRef.current = Array.from({ length: particleCount }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.5,
      vy: (Math.random() - 0.5) * 0.5,
      radius: Math.random() * 2 + 1,
      baseRadius: Math.random() * 2 + 1,
    }));
    
    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouseRef.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      };
    };
    
    canvas.addEventListener('mousemove', handleMouseMove);
    
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      const particles = particlesRef.current;
      const mouse = mouseRef.current;
      
      particles.forEach((p, i) => {
        // Mouse interaction
        const dx = mouse.x - p.x;
        const dy = mouse.y - p.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        
        if (dist < 150) {
          const force = (150 - dist) / 150;
          p.vx -= (dx / dist) * force * 0.02;
          p.vy -= (dy / dist) * force * 0.02;
          p.radius = p.baseRadius + force * 3;
        } else {
          p.radius = p.baseRadius;
        }
        
        // Update position
        p.x += p.vx;
        p.y += p.vy;
        
        // Boundaries
        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;
        
        // Damping
        p.vx *= 0.99;
        p.vy *= 0.99;
        
        // Draw particle - Enhanced visibility for light background
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(184, 134, 11, ${0.5 + p.radius * 0.1})`;
        ctx.fill();
        
        // Draw connections - Darker gold for light background
        particles.slice(i + 1).forEach(p2 => {
          const dx2 = p.x - p2.x;
          const dy2 = p.y - p2.y;
          const dist2 = Math.sqrt(dx2 * dx2 + dy2 * dy2);
          
          if (dist2 < 120) {
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.strokeStyle = `rgba(184, 134, 11, ${0.25 * (1 - dist2 / 120)})`;
            ctx.stroke();
          }
        });
      });
      
      animationId = requestAnimationFrame(animate);
    };
    
    animate();
    
    return () => {
      window.removeEventListener('resize', resize);
      canvas.removeEventListener('mousemove', handleMouseMove);
      cancelAnimationFrame(animationId);
    };
  }, [particleCount]);
  
  return (
    <canvas 
      ref={canvasRef}
      className="absolute inset-0 w-full h-full"
      style={{ pointerEvents: 'auto' }}
    />
  );
};

// 9. GLITCH TEXT COMPONENT
const GlitchText = ({ text, className = '', as: Component = 'span' }) => {
  const [showGlitch, setShowGlitch] = useState(true);
  
  useEffect(() => {
    // Initial glitch effect
    const timeout = setTimeout(() => {
      setShowGlitch(false);
    }, 500);
    
    // Random glitch intervals
    const interval = setInterval(() => {
      setShowGlitch(true);
      setTimeout(() => setShowGlitch(false), 200);
    }, 5000 + Math.random() * 5000);
    
    return () => {
      clearTimeout(timeout);
      clearInterval(interval);
    };
  }, []);
  
  return (
    <Component 
      className={`${className} ${showGlitch ? 'glitch glitch-intro' : ''}`}
      data-text={text}
    >
      {text}
    </Component>
  );
};

// LOADING SCREEN WITH PREMIUM LIGHT REVEAL
const LoadingScreen = () => {
  const [show, setShow] = useState(true);
  
  useEffect(() => {
    const timeout = setTimeout(() => setShow(false), 2500);
    return () => clearTimeout(timeout);
  }, []);
  
  if (!show) return null;
  
  return (
    <div className="loading-screen-premium">
      <div className="light-reveal-wrapper flex flex-col items-center">
        <Wordmark className="text-6xl md:text-7xl mb-4 animate-pulse" />
      </div>
      <p className="loading-tagline mt-4 text-clavis-textGray">Cybersecurity für Entscheider</p>
    </div>
  );
};

// Pillar Icons Mapping
const pillarIcons = {
  "Detect": Eye,
  "Protect": Shield,
  "Test": Target,
  "Respond": Zap
};

const getTeamInitials = (fullName) => {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// Team Data (bewusst keine Fotos im Web – siehe t.team.photoNote)
const teamMembers = [
  {
    id: "abdelkader-cornelius",
    name: "Abdelkader Cornelius",
    rolle: { de: "Gründer & Geschäftsführer", en: "Founder & Managing Director" },
    standort: "Berlin",
    kontakt: "cornelius@clavis-cyber.de",
    zitat: {
      de: "Cybersecurity ist Handwerk, nicht Vertrieb.",
      en: "Cybersecurity is craft, not sales."
    },
    zertifikate: [
      "OSCP – Offensive Security Certified Professional",
      "GCIH – GIAC Certified Incident Handler",
      "GCTI – GIAC Cyber Threat Intelligence",
      "CTIA – Certified Threat Intelligence Analyst"
    ],
    sprachen: {
      de: ["Deutsch (Muttersprache)", "Englisch (Verhandlungssicher)"],
      en: ["German (Native)", "English (Fluent)"]
    }
  }
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = BACKEND_URL ? `${BACKEND_URL.replace(/\/$/, "")}/api` : "/api";

// Asset URLs - Direkte Links ohne JS-Gating
const ASSETS = {
  de: {
    irRetainer: "/assets/ir-retainer-scope.html",
    nis2Readiness: "/assets/nis2-dora-readiness.html",
    threatLandscape: "/assets/threat-landscape-brief.html",
    pentestScoping: "/assets/pentest-scoping-checklist.html"
  },
  en: {
    irRetainer: "/assets/ir-retainer-scope-en.html",
    nis2Readiness: "/assets/nis2-dora-readiness-en.html",
    threatLandscape: "/assets/threat-landscape-brief-en.html",
    pentestScoping: "/assets/pentest-scoping-checklist-en.html"
  }
};
const ASSETS_DE = ASSETS.de;
const ASSETS_EN = ASSETS.en;

// Language Context
const LanguageContext = createContext();

// Offer-spezifische Modal-Konfigurationen
const offerConfigs = {
  de: {
    general: {
      title: "Gespräch vereinbaren",
      subtitle: "Lassen Sie uns über Ihre Sicherheitsanforderungen sprechen.",
      buttonText: "Anfrage senden",
      copy: "Wir melden uns innerhalb von 24 Stunden bei Ihnen."
    },
    readiness_check: {
      title: "15-min NIS2 Readiness Check",
      subtitle: "Kurzes Gespräch zur Einschätzung Ihres aktuellen NIS2/DORA-Status.",
      buttonText: "Readiness Check anfragen",
      copy: "In 15 Minuten erhalten Sie eine erste Einschätzung Ihrer Compliance-Lage."
    },
    briefing_call: {
      title: "30-min Threat Posture Briefing",
      subtitle: "Erfahren Sie, was Angreifer über Ihr Unternehmen wissen.",
      buttonText: "Briefing anfragen",
      copy: "Wir zeigen Ihnen in 30 Minuten Ihre externe Angriffsfläche."
    },
    scoping_call: {
      title: "Scoping Call vereinbaren",
      subtitle: "Gemeinsam definieren wir den optimalen Testumfang für Ihre Umgebung.",
      buttonText: "Scoping Call anfragen",
      copy: "Im Scoping-Gespräch klären wir Ziele, Scope und Rahmenbedingungen."
    },
    ki_strategie: {
      title: "Über Ihre KI-Strategie sprechen",
      subtitle: "Erstgespräch mit einem Praktiker – nicht mit Vertrieb.",
      buttonText: "Gespräch anfragen",
      copy: "Wir melden uns innerhalb von 24 Stunden. Ohne Verkaufsdruck, mit belastbaren Antworten."
    }
  },
  en: {
    general: {
      title: "Schedule a Call",
      subtitle: "Let's discuss your security requirements.",
      buttonText: "Send Request",
      copy: "We will get back to you within 24 hours."
    },
    readiness_check: {
      title: "15-min NIS2 Readiness Check",
      subtitle: "Brief call to assess your current NIS2/DORA status.",
      buttonText: "Request Readiness Check",
      copy: "In 15 minutes you'll get an initial assessment of your compliance posture."
    },
    briefing_call: {
      title: "30-min Threat Posture Briefing",
      subtitle: "Learn what attackers know about your organization.",
      buttonText: "Request Briefing",
      copy: "In 30 minutes we'll show you your external attack surface."
    },
    scoping_call: {
      title: "Schedule Scoping Call",
      subtitle: "Together we define the optimal test scope for your environment.",
      buttonText: "Request Scoping Call",
      copy: "In the scoping call we clarify objectives, scope, and parameters."
    },
    ki_strategie: {
      title: "Talk about your AI strategy",
      subtitle: "An initial conversation with a practitioner – not with sales.",
      buttonText: "Request conversation",
      copy: "We get back to you within 24 hours. No sales pressure, just substantive answers."
    }
  }
};

const translations = {
  de: {
    nav: {
      ki: "KI",
      leistungen: "Leistungen",
      szenarien: "Szenarien",
      insights: "Insights",
      philosophie: "Philosophie",
      team: "Team",
      kontakt: "Kontakt",
      incidentMelden: "INCIDENT MELDEN",
      backToCyber: "Cybersecurity"
    },
    ki: {
      seoTitle: "Clavis KI — KI entwickeln, betreiben und absichern",
      seoDesc: "Clavis Cyber ist ein KI-Unternehmen mit Wurzeln in der Cybersicherheit. Wir entwickeln eigene KI, betreiben sie auditfähig im Enterprise-Einsatz und sichern KI-Systeme so, wie Angreifer sie prüfen würden.",
      classification: "KI-Kompetenz · Berlin",
      hero: {
        tagline: "KI-Unternehmen mit Wurzeln in der Cybersicherheit",
        headline: "Wir reden nicht über KI. Wir bauen sie.",
        subline: "Cybersicherheit ist für uns kein Thema neben der KI, sondern der Herkunftsnachweis – die härteste Disziplin, in der KI-Systeme bestehen müssen. Dort, wo Fehler Angreifern nützen und jede Aussage einer Prüfung standhält.",
        sequence: [
          { key: "ENTWICKELN", sentence: "Ein eigenes, auf Cybersicherheit spezialisiertes KI-Modell und eine vollständig KI-native Plattform." },
          { key: "BETREIBEN", sentence: "KI im Produktivbetrieb – zentral gesteuert, auditfähig, mit dem Menschen in der Verantwortung." },
          { key: "ABSICHERN", sentence: "Wir prüfen KI-Systeme offensiv – wie Angreifer es tun würden." }
        ],
        lead: "Seit Mitte 2026 ist KI-Souveränität keine Theoriefrage mehr. Souveränität heißt für uns nicht Abschottung, sondern Wahlfreiheit: Wir nutzen führende Modelle, wo sie die besten sind, und bauen eigene Fähigkeiten – damit Abhängigkeit eine Entscheidung bleibt und kein Schicksal wird.",
        ctaPrimary: "Sprechen wir über Ihre KI-Strategie",
        ctaSecondary: "Herkunft: Cybersecurity"
      },
      haltung: {
        kicker: "Haltung",
        title: "Drei Überzeugungen",
        claims: [
          { n: "a", title: "KI-Kompetenz zeigt sich im Betrieb", text: "Kompetenz beweist man durch Systeme im Produktivbetrieb, nicht durch Foliensätze. Was wir behaupten, läuft." },
          { n: "b", title: "Souveränität ist Wahlfreiheit", text: "Der Juni 2026 hat gezeigt, dass der Zugang zu Modellen über Nacht zur Verhandlungsmasse werden kann. Wahlfreiheit entsteht, wenn man Alternativen selbst beherrscht." },
          { n: "c", title: "Vertrauen braucht Kontrollpunkte", text: "Vertrauen entsteht durch Kontrollpunkte, die man vorzeigen kann. Der Mensch bleibt verantwortlich – als Architektur, nicht als Absichtserklärung." }
        ]
      },
      fields: [
        {
          nummer: "01",
          kicker: "Kompetenzfeld 01",
          title: "KI entwickeln",
          lead: "Wir sind ein Engineering-Haus. Wir bauen täglich.",
          blocks: [
            { title: "Eigenes KI-Modell", badge: "In Entwicklung", text: "Ein auf Cybersicherheit spezialisiertes, deutsches KI-Modell – trainiert für eine Domäne, in der jede Aussage einer Prüfung standhalten muss.", note: "Mehr dazu auf dem Handelsblatt KI-Summit im November 2026 in München." },
            { title: "TACITUS PRINCIPIA", text: "Unsere vollständig KI-native Plattform: KI-gestützte Analyse, Berichtserstellung mit Quellenbewertung nach dem Admiralty Code, zwölf Qualitätssicherungsstufen und menschliche Freigabe vor jedem Kundenkontakt." }
          ]
        },
        {
          nummer: "02",
          kicker: "Kompetenzfeld 02",
          title: "KI betreiben",
          lead: "Human Oversight als Architektur, nicht als Absichtserklärung.",
          badge: "Produktiv im Enterprise-Einsatz",
          blocks: [
            { title: "AI Control Tower", text: "Zentrale Sicht auf alle KI-Agenten: Freigabe-Workflows nach Kritikalität, Eskalationslogik sowie revisionssichere, auditfähige Protokollierung." },
            { title: "KI-Operating-Model", text: "Beratung zu Governance, EU-AI-Act-Readiness, Exit-Strategien, Multi-Modell-Strategie und Datenresidenz – damit KI im Unternehmen tragfähig und verantwortbar bleibt." }
          ]
        },
        {
          nummer: "03",
          kicker: "Kompetenzfeld 03",
          title: "KI absichern",
          lead: "Wir prüfen KI offensiv – wie Angreifer es tun würden.",
          blocks: [
            { title: "Security Assessments", text: "Für LLM-Anwendungen und Agenten – von der Architektur bis zur produktiven Nutzung." },
            { title: "Prompt Injection & Jailbreaks", text: "Wir testen die Widerstandsfähigkeit gegen Manipulation der Modell- und Agentensteuerung." },
            { title: "Tool-Zugriffe & Datenflüsse", text: "Absicherung der Schnittstellen, über die Agenten handeln – dort entstehen die realen Risiken." },
            { title: "Bedrohungsmodellierung", text: "Threat Modeling für agentische Systeme, die eigenständig entscheiden und Werkzeuge nutzen." }
          ],
          note: "Hier wird die Cyber-Herkunft zur Differenzierung: Wir bewerten KI nicht aus der Distanz, sondern aus der Perspektive des Angreifers."
        }
      ],
      oekosystem: {
        kicker: "Ökosystem",
        title: "Wir bauen nicht allein",
        lead: "Souveränität heißt Wahlfreiheit – und Wahlfreiheit entsteht in einem Netz aus starken Partnern.",
        points: [
          "Partnerschaften mit führenden KI-Entwicklungs-, Modell- und Infrastrukturunternehmen in den USA und Europa.",
          "Souveräne Rechenzentrumskapazität in Deutschland im Aufbau.",
          "Führende Modelle dort, wo sie am besten sind – eigene Fähigkeiten dort, wo Unabhängigkeit zählt."
        ],
        partnersNote: "Mehrere Partnerschaften sind noch nicht öffentlich angekündigt. Details folgen nach der offiziellen Ankündigung."
      },
      cta: {
        kicker: "Kontakt",
        title: "Sprechen wir über Ihre KI-Strategie.",
        text: "Erstgespräch mit einem Praktiker, nicht mit Vertrieb. Ohne Verkaufsdruck, mit belastbaren Antworten.",
        primary: "Gespräch vereinbaren",
        secondary: "Zur Cybersecurity-Seite"
      }
    },
    hero: {
      tagline: "Cybersecurity für Entscheider",
      headline: "Angriffe untersuchen. Lücken schließen. Lagebilder liefern.",
      headlineSub: "Senior-only-Beratung aus Berlin für Incident Response, NIS2/DORA-Readiness und Threat Intelligence. Erstes Lagebild in 48 Stunden – direkter Draht zu den Operatoren, kein Account-Management.",
      mechanism: "Senior-only. Intelligence-first. Erstes Lagebild in 48h.",
      proofHint: "Beispiele unserer Arbeit:",
      proofItems: [
        { label: "Threat Landscape Briefing", url: ASSETS_DE.threatLandscape },
        { label: "IR-Retainer Runbook", url: ASSETS_DE.irRetainer },
        { label: "NIS2/DORA Readiness Map", url: ASSETS_DE.nis2Readiness }
      ],
      ctaPrimary: "Gespräch vereinbaren",
      ctaSecondary: "15-min Readiness Check"
    },
    leadership: {
      kicker: "Leitung",
      role: "Gründer & Geschäftsführer",
      name: "Abdelkader Cornelius",
      summary: "Clavis wird vom Gründer direkt geführt – als Boutique mit einem Verbund erfahrener Senior-Praktiker auf Mandatsbasis. Kein Account-Management, keine Junior-Triage.",
      cta: "Profil & Verbund"
    },
    clarity: {
      kicker: "Profil",
      columns: [
        {
          label: "Was wir tun",
          items: [
            "Incident Response & Digital Forensics",
            "NIS2/DORA & BSI-Compliance-Readiness",
            "Threat Intelligence & Darknet Monitoring",
            "Penetrationstests & Red Teaming"
          ]
        },
        {
          label: "Wann man uns ruft",
          items: [
            "24/7, wenn ein Angriff läuft oder vermutet wird",
            "Vor regulatorischen Fristen und Audits",
            "Wenn der Vorstand belastbare Lagebilder verlangt",
            "Wenn ein Test-Report Erkenntnisse liefern soll"
          ]
        },
        {
          label: "Was uns unterscheidet",
          items: [
            "Nur Senior-Praktiker. Kein Junior-Triage.",
            "Kein Tool-Verkauf, keine Lizenz-Provisionen.",
            "Direkter Draht zu den Operatoren, kein Account-Management.",
            "Boutique-Größe, ohne Beratungs-Bürokratie."
          ]
        }
      ]
    },
    pillars: {
      title: "Unsere vier Säulen",
      items: [
        {
          nummer: "01",
          titel: "Detect",
          outcome: "Sie erfahren von kompromittierten Credentials und Bedrohungen, BEVOR sie ausgenutzt werden.",
          deliverable: "Monatliches Threat Landscape Briefing, priorisierte Empfehlungen, Alerting bei kritischen Funden.",
          services: ["Threat Intelligence", "Darknet Monitoring", "Attack Surface Management", "Narrative Intelligence"]
        },
        {
          nummer: "02",
          titel: "Protect",
          outcome: "Sie wissen genau, wo Sie bei NIS2/DORA stehen und können das intern kommunizieren.",
          deliverable: "Gap-Assessment mit Priorisierung, 30/60/90-Tage Maßnahmenplan, dokumentierte Tabletop Exercise.",
          services: ["NIS2/DORA Readiness", "BCM", "Security Assessments", "Tabletop Exercises"]
        },
        {
          nummer: "03",
          titel: "Test",
          outcome: "Sie wissen, welche Schwachstellen echte Business-Risiken sind - nicht nur CVE-Nummern.",
          deliverable: "Technischer Bericht mit Business-Impact, Debriefing mit Ihrem Team, priorisierte Empfehlungen.",
          services: ["Penetration Tests", "Red Teaming", "Social Engineering", "OT Security Assessments"]
        },
        {
          nummer: "04",
          titel: "Respond",
          outcome: "Sie haben innerhalb von Stunden Klarheit über Scope und Schwere.",
          deliverable: "Forensischer Analysebericht (gerichtsverwertbar), Timeline mit IOCs, Dokumentation für Versicherung.",
          services: ["Incident Response", "Digital Forensics", "Krisenmanagement", "Zahlungsverfolgung"]
        }
      ]
    },
    differenzierung: {
      title: "Was uns unterscheidet",
      negativ: [
        "Wir verkaufen keine Tool-Lizenzen.",
        "Wir liefern keine PDF-Pentests ohne Erkenntnisse.",
        "Wir betreiben kein Ticket-System - weil Incidents keine Tickets sind.",
        "Wir haben keine Junior-Analysten, die Ihre Anfrage weiterleiten."
      ],
      positiv: [
        { title: "Senior-Only", desc: "Kunden sprechen mit den Leuten, die die Arbeit machen." },
        { title: "Intelligence-First", desc: "Wir verkaufen Verstehen, nicht nur Reaktion." },
        { title: "Direkter Draht", desc: "Zu den Operators, ohne Umwege." },
        { title: "Deutsche Präsenz", desc: "Mit Verständnis für lokale Regulatorik." }
      ]
    },
    szenarien: {
      title: "Typische Szenarien",
      items: [
        { title: "Incident Response", link: "/szenario/incident", desc: "Akutfall: Angreifer im Netzwerk" },
        { title: "NIS2/Compliance", link: "/szenario/nis2", desc: "Regulatorik: NIS2, DORA, BSI" },
        { title: "Threat Intelligence", link: "/szenario/threat-intelligence", desc: "Erkennen bevor es passiert" },
        { title: "Offensive Security", link: "/szenario/pentest", desc: "Pentests mit Erkenntnissen" }
      ]
    },
    teamTeaser: {
      title: "Senior-Only bedeutet:",
      desc: "Clavis Cyber wurde von Praktikern gegründet – nicht von Beratern, die Folien bauen, sondern von Menschen, die Incidents bearbeitet und Unternehmen durch Krisen geführt haben.",
      stats: ["80+ Jahre kombinierte Erfahrung", "200+ Incidents", "6 Experten"],
      linkText: "Mehr über unser Team"
    },
    kontakt: {
      title: "Kontakt",
      ctaPrimary: "Gespräch vereinbaren",
      ctaSecondary: "15-min Readiness Check"
    },
    notfallBanner: {
      text: "Incident? Sofort anrufen:",
      phone: "030 75439203",
      linkText: "Incident melden"
    },
    incidentIntake: {
      title: "Incident melden",
      phoneLabel: "Sofort anrufen:",
      phone: "030 75439203",
      bullets: [
        "Rückruf und Lageeinschätzung i.d.R. innerhalb von 15-30 Minuten.",
        "Forensische Sicherung kritischer Systeme.",
        "Klare Handlungsempfehlungen für die nächsten Stunden."
      ],
      formTitle: "Oder kurzes Formular ausfüllen:",
      firma: "Firma",
      rueckruf: "Rückrufnummer",
      beschreibung: "Was passiert gerade?",
      verschluesselt: "Sind Systeme verschlüsselt?",
      ja: "Ja",
      nein: "Nein",
      submit: "Incident melden",
      hinweis: "Kein Ticket-System. Rückruf i.d.R. innerhalb von 15-30 Minuten.",
      success: "Ihre Meldung wurde übermittelt. Wir rufen Sie umgehend zurück."
    },
    szenarioIncident: {
      headline: "Ein Angreifer war in Ihrem Netzwerk.",
      subline: "Vielleicht ist er noch da.",
      risk: "Jede Stunde ohne Klarheit ist eine Stunde, in der der Angreifer Daten exfiltriert oder Backdoors installiert.",
      whatWeDo: "Was wir in den ersten 72 Stunden tun:",
      steps: [
        "Sofortiger Rückruf und Lageeinschätzung (15-30 Min)",
        "Remote-Triage oder Vor-Ort-Einsatz innerhalb von 4 Stunden",
        "Forensische Sicherung kritischer Systeme",
        "Identifikation des Angriffspfads und der Lateral Movement",
        "Tägliche Lagebriefings für Ihr Management",
        "Koordination mit Behörden und Versicherung (auf Wunsch)"
      ],
      timeline: {
        title: "Typischer Ablauf",
        items: [
          { time: "0-30 Min", desc: "Rückruf, Ersteinschätzung, Go/No-Go für Vor-Ort" },
          { time: "2-4 Std", desc: "Team vor Ort oder Remote-Zugang etabliert" },
          { time: "24 Std", desc: "Erstes Lagebild: Scope, Angriffsvektoren, Empfehlungen" },
          { time: "48-72 Std", desc: "Forensischer Bericht, Remediation-Plan, Wiederanlauf" }
        ]
      },
      deliverables: {
        title: "Was Sie bekommen",
        items: [
          "Forensischer Analysebericht (gerichtsverwertbar)",
          "Timeline mit allen IOCs (Indicators of Compromise)",
          "Dokumentation für Cyber-Versicherung",
          "Remediation-Playbook für Ihr IT-Team",
          "Optional: Unterstützung bei Behördenmeldungen (BSI, Datenschutz)"
        ]
      },
      caseStudy: {
        label: "Aus der Praxis",
        title: "Ransomware bei Automobilzulieferer",
        text: "Freitag 22:15 Uhr: Verschlüsselte Systeme. Samstag 02:00 Uhr: Unser Team vor Ort. Montag 06:00 Uhr: Produktion läuft wieder. Kein Lösegeld bezahlt, 100% Daten wiederhergestellt.",
        stats: ["< 4h bis Vor-Ort", "72h bis Wiederanlauf", "0€ Lösegeld"]
      },
      faqs: [
        { q: "Was kostet ein IR-Einsatz?", a: "Wir arbeiten mit Tagessätzen. Bei Retainer-Kunden sind die ersten 24h inkludiert. Für Neukunden: transparente Kostenaufstellung nach Erstgespräch." },
        { q: "Arbeiten Sie mit unserer Cyber-Versicherung?", a: "Ja. Wir kennen die Anforderungen der gängigen Versicherer und liefern Dokumentation, die Ihre Ansprüche unterstützt." },
        { q: "Können Sie auch präventiv helfen?", a: "Ja. Unser IR-Retainer gibt Ihnen garantierte Reaktionszeiten und regelmäßige Readiness-Checks." }
      ],
      cta: "Incident melden",
      asset: { label: "IR-Retainer Scope & Engagement Model", url: ASSETS_DE.irRetainer }
    },
    szenarioNis2: {
      headline: "NIS2 steht an. Ihr Readiness-Level: unklar.",
      subline: "Die Frist läuft. Die Anforderungen sind komplex.",
      risk: "Compliance auf dem Papier schützt nicht vor Bußgeldern, wenn die operative Realität anders aussieht. Geschäftsführer haften persönlich.",
      whatWeDo: "Was wir in den ersten 30 Tagen tun:",
      steps: [
        "Gap-Assessment gegen NIS2/DORA-Anforderungen",
        "Interviews mit IT, Security und Fachbereichen",
        "Bewertung Ihrer aktuellen Maßnahmen",
        "Priorisierter 30/60/90-Tage Maßnahmenplan",
        "Management-Summary für Vorstand/Geschäftsführung",
        "Dokumentierte Tabletop Exercise für Ihr Krisenteam"
      ],
      timeline: {
        title: "Typischer Ablauf",
        items: [
          { time: "Woche 1", desc: "Kick-off, Dokumentensichtung, Stakeholder-Interviews" },
          { time: "Woche 2", desc: "Gap-Assessment, Risikobewertung" },
          { time: "Woche 3", desc: "Maßnahmenplan, Priorisierung mit Ihrem Team" },
          { time: "Woche 4", desc: "Tabletop Exercise, Abschlussbericht, Übergabe" }
        ]
      },
      deliverables: {
        title: "Was Sie bekommen",
        items: [
          "NIS2/DORA Gap-Assessment Report",
          "Priorisierte Maßnahmenliste mit Aufwandsschätzung",
          "Management-Präsentation für Vorstand",
          "30/60/90-Tage Roadmap",
          "Dokumentierte Tabletop Exercise",
          "Optional: Begleitung der Umsetzung"
        ]
      },
      caseStudy: {
        label: "Aus der Praxis",
        title: "KRITIS-Betreiber im Gesundheitswesen",
        text: "Ein Klinikverbund wusste nicht, wo er bei NIS2 steht. Nach 4 Wochen: Klares Lagebild, priorisierter Plan, und ein Management, das weiß, was zu tun ist.",
        stats: ["4 Wochen", "47 Gaps identifiziert", "Roadmap bis Q2"]
      },
      faqs: [
        { q: "Gilt NIS2 für uns?", a: "Wenn Sie in einem der 18 kritischen Sektoren tätig sind und bestimmte Schwellenwerte überschreiten, sehr wahrscheinlich. Wir klären das im Erstgespräch." },
        { q: "Wie unterscheidet sich DORA von NIS2?", a: "DORA ist spezifisch für den Finanzsektor und hat eigene Anforderungen an ICT-Risikomanagement. Wir decken beides ab." },
        { q: "Können Sie auch bei der Umsetzung helfen?", a: "Ja. Nach dem Assessment können wir einzelne Workstreams begleiten oder als Sparringspartner fungieren." }
      ],
      cta: "15-min NIS2 Readiness Check",
      asset: { label: "NIS2/DORA 30/60/90 Tage Plan", url: ASSETS_DE.nis2Readiness }
    },
    szenarioThreat: {
      headline: "Sie wollen wissen, was Angreifer über Sie wissen.",
      subline: "Bevor sie es gegen Sie verwenden.",
      risk: "Kompromittierte Credentials werden oft erst bemerkt, wenn sie genutzt werden. Darknet-Leaks können Monate unentdeckt bleiben.",
      whatWeDo: "Was wir tun:",
      steps: [
        "Systematische Suche in Darknet, Breach-Datenbanken, Paste-Sites",
        "Analyse Ihrer externen Angriffsfläche (Attack Surface)",
        "Identifikation kompromittierter Credentials und Accounts",
        "Bewertung nach Business-Impact und Kritikalität",
        "Priorisierte Handlungsempfehlungen",
        "Monatliches Monitoring (optional)"
      ],
      timeline: {
        title: "Typischer Ablauf",
        items: [
          { time: "Tag 1", desc: "Scope-Definition, Übergabe relevanter Domains/Assets" },
          { time: "Tag 2-5", desc: "Intelligence Collection, Darknet-Recherche" },
          { time: "Tag 6-7", desc: "Analyse, Priorisierung, Report-Erstellung" },
          { time: "Tag 8", desc: "Briefing mit Ihrem Team, Q&A" }
        ]
      },
      deliverables: {
        title: "Was Sie bekommen",
        items: [
          "Threat Landscape Report für Ihre Organisation",
          "Liste kompromittierter Credentials mit Quelle",
          "Attack Surface Assessment",
          "Priorisierte Handlungsempfehlungen",
          "Executive Summary für Management",
          "Optional: Monatliches Threat Briefing"
        ]
      },
      caseStudy: {
        label: "Aus der Praxis",
        title: "DAX-Unternehmen entdeckt Breach",
        text: "Bei einem Threat Assessment fanden wir 340 kompromittierte Mitarbeiter-Credentials aus einem 2 Jahre alten Breach. 12 davon waren noch aktiv und hatten VPN-Zugang.",
        stats: ["340 Credentials", "12 aktive Accounts", "2 mit Admin-Rechten"]
      },
      faqs: [
        { q: "Was ist der Unterschied zu einem Pentest?", a: "Threat Intelligence schaut, was Angreifer bereits über Sie wissen. Pentests prüfen, wie sie einbrechen könnten. Beides ergänzt sich." },
        { q: "Wie oft sollte man das machen?", a: "Einmal jährlich als Baseline, idealerweise mit monatlichem Monitoring für neue Leaks." },
        { q: "Finden Sie wirklich alles?", a: "Wir durchsuchen systematisch alle relevanten Quellen. 100% Garantie gibt es nicht, aber unsere Analysten haben Erfahrung aus hunderten Assessments." }
      ],
      cta: "30-min Threat Posture Briefing",
      asset: { label: "Threat Landscape Brief (Beispiel)", url: ASSETS_DE.threatLandscape }
    },
    szenarioPentest: {
      headline: "Offensive Security braucht die Besten.",
      subline: "Deshalb arbeiten wir mit spezialisierten Partnern.",
      risk: "Ein ehrliches Wort: Offensive Security erfordert hochspezialisierte Expertise, die man nicht nebenbei aufbaut. Statt Ihnen etwas vorzumachen, vermitteln wir Sie an handverlesene Partner – die Besten ihres Fachs.",
      whatWeDo: "Warum dieser Ansatz:",
      steps: [
        "Wir kennen unsere Stärken – und unsere Grenzen",
        "Unsere Partner sind auf Offensive Security spezialisiert, nicht diversifiziert",
        "Sie bekommen echte Experten, keine Generalisten mit Pentest-Zertifikat",
        "Wir bleiben Ihr Ansprechpartner und koordinieren das Projekt",
        "Nahtlose Integration mit unseren Leistungen (IR, Threat Intel, Compliance)",
        "Ehrlichkeit schafft Vertrauen – und bessere Ergebnisse"
      ],
      timeline: {
        title: "So läuft es ab",
        items: [
          { time: "Schritt 1", desc: "Sie schildern uns Ihren Bedarf – wir verstehen Ihren Kontext" },
          { time: "Schritt 2", desc: "Wir empfehlen den passenden Partner aus unserem Netzwerk" },
          { time: "Schritt 3", desc: "Gemeinsames Scoping – Sie, wir, der Partner" },
          { time: "Schritt 4", desc: "Der Partner führt den Test durch, wir bleiben eingebunden" }
        ]
      },
      deliverables: {
        title: "Was Sie bekommen",
        items: [
          "Zugang zu Top-Tier Offensive-Security-Spezialisten",
          "Ein Ansprechpartner bei CLAVIS CYBER für das gesamte Projekt",
          "Nahtlose Verzahnung mit Ihren anderen Security-Initiativen",
          "Technischer Report mit Business-Kontext",
          "Optional: Remediation-Begleitung durch CLAVIS CYBER",
          "Langfristige Partnerschaft statt einmaliger Transaktion"
        ]
      },
      caseStudy: {
        label: "Unsere Philosophie",
        title: "Integrität vor Umsatz",
        text: "Wir könnten Pentests selbst anbieten und mehr verdienen. Aber unsere Kunden vertrauen uns, weil wir ehrlich sind. Dieses Vertrauen ist mehr wert als jeder Auftrag.",
        stats: ["100% Ehrlichkeit", "Handverlesene Partner", "Ihr Erfolg zählt"]
      },
      faqs: [
        { q: "Warum macht CLAVIS CYBER keine eigenen Pentests?", a: "Offensive Security erfordert permanente Spezialisierung. Unsere Stärke liegt in Incident Response, Threat Intelligence und Compliance. Für Pentests arbeiten wir mit den Besten zusammen." },
        { q: "Wer sind Ihre Partner?", a: "Spezialisierte Offensive-Security-Boutiquen mit nachgewiesener Expertise. Keine großen Beratungshäuser, sondern echte Practitioner. Wir stellen den Kontakt gerne her." },
        { q: "Kostet das mehr?", a: "Nein. Sie zahlen den Partner direkt. Wir nehmen keine Vermittlungsprovision – unser Gewinn ist Ihr Vertrauen und eine langfristige Zusammenarbeit." }
      ],
      cta: "Beratungsgespräch anfragen",
      asset: { label: "Offensive Security: Unser Partner-Ansatz", url: ASSETS_DE.pentestScoping }
    },
    philosophie: {
      title: "Philosophie",
      headline: "Integrität vor Umsatz",
      subline: "Warum wir anders sind – und es auch bleiben werden.",
      intro: "In einer Branche, die oft von Angst und Übertreibung lebt, haben wir uns für einen anderen Weg entschieden: Ehrlichkeit. Das bedeutet manchmal, Aufträge abzulehnen. Aber es bedeutet auch, dass unsere Kunden uns vertrauen – und das ist mehr wert als jeder kurzfristige Gewinn.",
      sections: {
        whoWeAre: {
          title: "Wer wir sind",
          text: "Clavis Cyber wurde von Praktikern gegründet – nicht von Beratern, die Folien bauen, sondern von Menschen, die Incidents bearbeitet und Unternehmen durch Krisen geführt haben. Wir kennen den Unterschied zwischen Theorie und dem, was um 3 Uhr nachts wirklich zählt."
        },
        whatWeStandFor: {
          title: "Wofür wir stehen",
          items: [
            { title: "Ehrlichkeit", text: "Wir sagen, was wir können – und was nicht. Wenn ein anderer Anbieter besser zu Ihnen passt, sagen wir das." },
            { title: "Fokus", text: "Wir machen, was wir beherrschen: Incident Response, Threat Intelligence, Compliance. Nicht mehr, nicht weniger." },
            { title: "Qualität vor Quantität", text: "Wir wachsen langsam und bewusst. Jeder neue Mitarbeiter muss unsere Werte teilen – nicht nur seine Skills." },
            { title: "Langfristige Beziehungen", text: "Wir optimieren nicht auf den nächsten Auftrag, sondern auf Vertrauen. Das zahlt sich aus – für beide Seiten." }
          ]
        },
        whatWeDontDo: {
          title: "Was wir bewusst nicht machen",
          intro: "Genauso wichtig wie das, was wir tun, ist das, was wir lassen:",
          items: [
            { title: "Offensive Security / Pentests", text: "Das erfordert Spezialisten, die nichts anderes machen. Wir vermitteln Sie an handverlesene Partner." },
            { title: "Managed SOC / 24/7 Monitoring", text: "Wir sind Berater und Incident Responder, kein Managed Security Service Provider." },
            { title: "Produkte verkaufen", text: "Wir sind herstellerunabhängig. Wir empfehlen, was zu Ihnen passt – nicht, was uns Provision bringt." },
            { title: "Angst schüren", text: "Cybersecurity ist komplex genug. Wir erklären Risiken sachlich, ohne Panikmache." }
          ]
        },
        partnerApproach: {
          title: "Unser Partner-Ansatz",
          text: "Statt alles selbst anzubieten und mittelmäßig zu sein, arbeiten wir mit spezialisierten Partnern zusammen. Für Penetrationstests vermitteln wir Sie an Experten, die nichts anderes machen – weil wir ehrlich sind und Sie das Beste verdienen.",
          quote: "Wir könnten Pentests selbst anbieten. Es wäre profitabel. Aber unsere Kunden vertrauen uns, weil wir ehrlich sind. Dieses Vertrauen ist mehr wert als jeder Auftrag."
        },
        howWeWork: {
          title: "Wie wir arbeiten",
          items: [
            { title: "Senior-only", text: "Bei uns arbeiten Sie vom ersten Tag an mit erfahrenen Experten. Keine Junioren, die lernen, während Sie zahlen." },
            { title: "Direkte Kommunikation", text: "Sie erreichen die Menschen, die an Ihrem Fall arbeiten. Kein Account Manager dazwischen." },
            { title: "Transparente Preise", text: "Klare Tagessätze, keine versteckten Kosten. Sie wissen vorher, was es kostet." },
            { title: "Ergebnisse, keine Reports", text: "Wir liefern Erkenntnisse und Empfehlungen, nicht 100-seitige Dokumente, die niemand liest." }
          ]
        }
      },
      cta: {
        headline: "Klingt das nach dem Partner, den Sie suchen?",
        text: "Lassen Sie uns sprechen. Unverbindlich, ohne Verkaufsdruck.",
        button: "Gespräch vereinbaren"
      }
    },
    team: {
      kicker: "Leitung & Verbund",
      title: "Wer Clavis führt – und wer mitarbeitet.",
      philosophie: "Clavis Cyber wird von einem Praktiker geführt und arbeitet als Boutique mit einem Verbund erfahrener Senior-Spezialisten auf Mandatsbasis. Keine Folien-Berater, keine Junior-Triage – die Personen, die am Tisch sitzen, machen die Arbeit selbst.",
      sections: {
        founder: "Gründer & Geschäftsführer",
        bio: "Profil",
        werdegang: "Werdegang",
        schwerpunkte: "Fachliche Schwerpunkte",
        behoerden: "Behördenerfahrung",
        speaking: "Speaking & Veröffentlichungen (Auswahl)",
        zertifikate: "Qualifikation",
        sprachen: "Sprachen",
        kontakt: "Direktkontakt"
      },
      verbund: {
        kicker: "Praxis-Verbund",
        title: "Boutique, kein One-Man-Show.",
        body: "Für Mandate, die mehr als eine Disziplin oder Kapazität erfordern, arbeitet Clavis mit einem festen Kreis erfahrener Senior-Praktiker zusammen – Forensiker, Threat-Intelligence-Analysten, Pentester, Krisenkommunikatoren. Der Kreis wird mandatsbezogen zusammengestellt: kein Account-Management, kein internes Routing, sondern direkt verantwortliche Operatoren. Aus Diskretionsgründen werden Mitwirkende nicht öffentlich gelistet, sondern im Kontext eines konkreten Mandats persönlich vorgestellt.",
        kompetenzenLabel: "Abgedeckte Disziplinen im Verbund",
        kompetenzen: [
          "Incident Response & Digital Forensics (DFIR)",
          "Cyber Threat Intelligence & Darknet-Monitoring",
          "Malware-Analyse & Reverse Engineering",
          "Penetration Testing & Red Teaming",
          "Krisen- und Vorstandskommunikation",
          "NIS-2, DORA, BSI-Grundschutz, KRITIS, DSGVO",
          "AI Security & KI-Governance",
          "Forensische Beweissicherung nach deutschen Prozessstandards"
        ],
        prinzipienLabel: "Verbund-Prinzip",
        prinzipien: [
          "Jedes Mandat wird von einem Senior-Praktiker direkt geführt.",
          "Keine Junior-Triage, kein Account-Manager als Schnittstelle.",
          "Mitwirkende werden mandatsbezogen ausgewählt – nicht ressourcenfüllend platziert.",
          "Sprachliche Abdeckung: Deutsch und Englisch durchgehend, weitere Sprachen mandatsabhängig."
        ]
      }
    },
    impressum: {
      title: "Impressum",
      content: `CLAVIS CYBER GmbH
Kurfürstendamm 194
10707 Berlin
Deutschland

Vertreten durch:
Abdelkader Cornelius (Geschäftsführer)

Kontakt:
Telefon: +49 30 75439203
E-Mail: info@clavis-cyber.de
Incident-Hotline (24/7): +49 30 75439203

Registereintrag:
Eingetragen im Handelsregister.
Registergericht: Amtsgericht Charlottenburg (Berlin)
Registernummer: HRB 288051 B

Umsatzsteuer-ID:
Umsatzsteuer-Identifikationsnummer gemäß §27a UStG: beantragt

Verantwortlicher i.S.d. § 18 Abs. 2 MStV:
Abdelkader Cornelius
Kurfürstendamm 194
10707 Berlin

Streitschlichtung:
Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit: https://ec.europa.eu/consumers/odr/
Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.

Haftung für Inhalte:
Als Diensteanbieter sind wir gemäß § 7 Abs. 1 TMG für eigene Inhalte auf diesen Seiten nach den allgemeinen Gesetzen verantwortlich. Nach §§ 8 bis 10 TMG sind wir als Diensteanbieter jedoch nicht verpflichtet, übermittelte oder gespeicherte fremde Informationen zu überwachen oder nach Umständen zu forschen, die auf eine rechtswidrige Tätigkeit hinweisen.

Haftung für Links:
Unser Angebot enthält Links zu externen Websites Dritter, auf deren Inhalte wir keinen Einfluss haben. Deshalb können wir für diese fremden Inhalte auch keine Gewähr übernehmen. Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der Seiten verantwortlich.`
    },
    datenschutz: {
      title: "Datenschutzerklärung",
      content: `1. Datenschutz auf einen Blick

Allgemeine Hinweise
Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen Daten passiert, wenn Sie diese Website besuchen. Personenbezogene Daten sind alle Daten, mit denen Sie persönlich identifiziert werden können. Ausführliche Informationen zum Thema Datenschutz entnehmen Sie unserer unter diesem Text aufgeführten Datenschutzerklärung.

2. Verantwortlicher

Verantwortlich für die Datenverarbeitung auf dieser Website ist:

CLAVIS CYBER GmbH
Kurfürstendamm 194
10707 Berlin
Deutschland

Geschäftsführer: Abdelkader Cornelius
Telefon: +49 30 75439203
E-Mail: info@clavis-cyber.de

3. Wie erfassen wir Ihre Daten?

Ihre Daten werden zum einen dadurch erhoben, dass Sie uns diese mitteilen, z.\u00A0B. durch Eingabe in ein Kontaktformular oder per E-Mail. Andere Daten werden automatisch oder nach Ihrer Einwilligung beim Besuch der Website durch unsere IT-Systeme erfasst. Das sind vor allem technische Daten (z.\u00A0B. Internetbrowser, Betriebssystem oder Uhrzeit des Seitenaufrufs). Die Erfassung dieser Daten erfolgt automatisch, sobald Sie diese Website betreten.

4. Wofür nutzen wir Ihre Daten?

Ein Teil der Daten wird erhoben, um eine fehlerfreie Bereitstellung der Website zu gewährleisten. Eine Analyse Ihres Nutzerverhaltens findet nicht statt. Es werden keine Tracking- oder Analyse-Tools eingesetzt.

5. Welche Rechte haben Sie bezüglich Ihrer Daten?

Sie haben jederzeit das Recht auf unentgeltliche Auskunft über Herkunft, Empfänger und Zweck Ihrer gespeicherten personenbezogenen Daten. Sie haben außerdem ein Recht, die Berichtigung oder Löschung dieser Daten zu verlangen. Wenn Sie eine Einwilligung zur Datenverarbeitung erteilt haben, können Sie diese jederzeit für die Zukunft widerrufen. Außerdem haben Sie das Recht, unter bestimmten Umständen die Einschränkung der Verarbeitung Ihrer personenbezogenen Daten zu verlangen. Des Weiteren steht Ihnen ein Beschwerderecht bei der zuständigen Aufsichtsbehörde zu.

Hierzu sowie zu weiteren Fragen zum Thema Datenschutz können Sie sich jederzeit an uns wenden.

6. Hosting

Diese Website wird bei der Hetzner Online GmbH, Industriestr. 25, 91710 Gunzenhausen, Deutschland gehostet. Details entnehmen Sie der Datenschutzerklärung von Hetzner: https://www.hetzner.com/de/legal/privacy-policy/

Der Einsatz von Hetzner erfolgt auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO. Wir haben ein berechtigtes Interesse an einer möglichst zuverlässigen Darstellung unserer Website. Sofern eine entsprechende Einwilligung abgefragt wurde, erfolgt die Verarbeitung ausschließlich auf Grundlage von Art. 6 Abs. 1 lit. a DSGVO. Die Einwilligung ist jederzeit widerrufbar.

Wir haben einen Vertrag über Auftragsverarbeitung (AVV) mit Hetzner geschlossen.

7. Server-Log-Dateien

Der Provider der Seiten erhebt und speichert automatisch Informationen in sogenannten Server-Log-Dateien, die Ihr Browser automatisch an uns übermittelt. Dies sind:
– Browsertyp und Browserversion
– verwendetes Betriebssystem
– Referrer URL
– Hostname des zugreifenden Rechners
– Uhrzeit der Serveranfrage
– IP-Adresse

Eine Zusammenführung dieser Daten mit anderen Datenquellen wird nicht vorgenommen. Die Erfassung dieser Daten erfolgt auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO. Der Websitebetreiber hat ein berechtigtes Interesse an der technisch fehlerfreien Darstellung und der Optimierung seiner Website – hierzu müssen die Server-Log-Dateien erfasst werden.

8. SSL- bzw. TLS-Verschlüsselung

Diese Seite nutzt aus Sicherheitsgründen und zum Schutz der Übertragung vertraulicher Inhalte eine SSL- bzw. TLS-Verschlüsselung. Eine verschlüsselte Verbindung erkennen Sie daran, dass die Adresszeile des Browsers von „http://" auf „https://" wechselt und an dem Schloss-Symbol in Ihrer Browserzeile. Wenn die SSL- bzw. TLS-Verschlüsselung aktiviert ist, können die Daten, die Sie an uns übermitteln, nicht von Dritten mitgelesen werden.

9. Kontaktformular

Wenn Sie uns per Kontaktformular Anfragen zukommen lassen, werden Ihre Angaben aus dem Anfrageformular inklusive der von Ihnen dort angegebenen Kontaktdaten zwecks Bearbeitung der Anfrage und für den Fall von Anschlussfragen bei uns gespeichert. Diese Daten geben wir nicht ohne Ihre Einwilligung weiter.

Die Verarbeitung dieser Daten erfolgt auf Grundlage von Art. 6 Abs. 1 lit. b DSGVO, sofern Ihre Anfrage mit der Erfüllung eines Vertrags zusammenhängt oder zur Durchführung vorvertraglicher Maßnahmen erforderlich ist. In allen übrigen Fällen beruht die Verarbeitung auf unserem berechtigten Interesse an der effektiven Bearbeitung der an uns gerichteten Anfragen (Art. 6 Abs. 1 lit. f DSGVO) oder auf Ihrer Einwilligung (Art. 6 Abs. 1 lit. a DSGVO), sofern diese abgefragt wurde.

Die von Ihnen im Kontaktformular eingegebenen Daten verbleiben bei uns, bis Sie uns zur Löschung auffordern, Ihre Einwilligung zur Speicherung widerrufen oder der Zweck für die Datenspeicherung entfällt. Zwingende gesetzliche Bestimmungen – insbesondere Aufbewahrungsfristen – bleiben unberührt.

10. Anfrage per E-Mail oder Telefon

Wenn Sie uns per E-Mail oder Telefon kontaktieren, wird Ihre Anfrage inklusive aller daraus hervorgehenden personenbezogenen Daten (Name, Anfrage) zum Zwecke der Bearbeitung Ihres Anliegens bei uns gespeichert und verarbeitet. Diese Daten geben wir nicht ohne Ihre Einwilligung weiter.

Die Verarbeitung dieser Daten erfolgt auf Grundlage von Art. 6 Abs. 1 lit. b DSGVO, sofern Ihre Anfrage mit der Erfüllung eines Vertrags zusammenhängt oder zur Durchführung vorvertraglicher Maßnahmen erforderlich ist. In allen übrigen Fällen beruht die Verarbeitung auf unserem berechtigten Interesse an der effektiven Bearbeitung der an uns gerichteten Anfragen (Art. 6 Abs. 1 lit. f DSGVO) oder auf Ihrer Einwilligung (Art. 6 Abs. 1 lit. a DSGVO), sofern diese abgefragt wurde.

11. Cookies

Diese Website verwendet ausschließlich technisch notwendige Cookies. Ein technisch notwendiger Cookie speichert Ihre Cookie-Consent-Einstellung im lokalen Speicher (localStorage) Ihres Browsers. Es werden keine Tracking-, Analyse- oder Marketing-Cookies eingesetzt.

Rechtsgrundlage für die Verwendung technisch notwendiger Cookies ist Art. 6 Abs. 1 lit. f DSGVO. Wir haben ein berechtigtes Interesse an der Speicherung von technisch notwendigen Cookies zur technisch fehlerfreien und optimierten Bereitstellung unserer Dienste.

12. Schriftarten (lokal gehostet)

Diese Website nutzt die Schriftarten „Inter" und „Cormorant Garamond" für die einheitliche Darstellung. Sämtliche Schriftarten sind lokal auf unserem Server eingebunden. Es findet keine Verbindung zu externen Servern (z.\u00A0B. Google) statt.

13. Ihre Rechte im Detail

Gemäß der DSGVO stehen Ihnen folgende Rechte zu:

a) Recht auf Auskunft (Art. 15 DSGVO): Sie haben das Recht, eine Bestätigung darüber zu verlangen, ob betreffende Daten verarbeitet werden, und auf Auskunft über diese Daten.
b) Recht auf Berichtigung (Art. 16 DSGVO): Sie haben das Recht, die Berichtigung unrichtiger oder die Vervollständigung Ihrer bei uns gespeicherten personenbezogenen Daten zu verlangen.
c) Recht auf Löschung (Art. 17 DSGVO): Sie haben das Recht, die Löschung Ihrer bei uns gespeicherten personenbezogenen Daten zu verlangen, soweit die Verarbeitung nicht zur Ausübung des Rechts auf freie Meinungsäußerung, zur Erfüllung einer rechtlichen Verpflichtung, aus Gründen des öffentlichen Interesses oder zur Geltendmachung, Ausübung oder Verteidigung von Rechtsansprüchen erforderlich ist.
d) Recht auf Einschränkung der Verarbeitung (Art. 18 DSGVO): Sie haben das Recht, die Einschränkung der Verarbeitung Ihrer personenbezogenen Daten zu verlangen.
e) Recht auf Datenübertragbarkeit (Art. 20 DSGVO): Sie haben das Recht, Ihre personenbezogenen Daten in einem gängigen, maschinenlesbaren Format zu erhalten oder die Übermittlung an einen anderen Verantwortlichen zu verlangen.
f) Recht auf Widerspruch (Art. 21 DSGVO): Sie haben das Recht, aus Gründen, die sich aus Ihrer besonderen Situation ergeben, jederzeit gegen die Verarbeitung Ihrer personenbezogenen Daten Widerspruch einzulegen.
g) Recht auf Widerruf der Einwilligung (Art. 7 Abs. 3 DSGVO): Sie haben das Recht, eine einmal erteilte Einwilligung jederzeit zu widerrufen.
h) Beschwerderecht bei einer Aufsichtsbehörde (Art. 77 DSGVO): Sie haben das Recht, sich bei einer Aufsichtsbehörde zu beschweren, wenn Sie der Ansicht sind, dass die Verarbeitung Ihrer personenbezogenen Daten gegen die DSGVO verstößt.

Zuständige Aufsichtsbehörde:
Berliner Beauftragte für Datenschutz und Informationsfreiheit
Friedrichstr. 219
10969 Berlin
Telefon: +49 30 13889-0
E-Mail: mailbox@datenschutz-berlin.de
https://www.datenschutz-berlin.de

14. Aktualität und Änderung dieser Datenschutzerklärung

Diese Datenschutzerklärung ist aktuell gültig und hat den Stand April 2026. Durch die Weiterentwicklung unserer Website oder aufgrund geänderter gesetzlicher bzw. behördlicher Vorgaben kann es notwendig werden, diese Datenschutzerklärung zu ändern.`
    },
    footer: {
      impressum: "Impressum",
      datenschutz: "Datenschutz"
    },
    contact: {
      name: "Name",
      email: "E-Mail",
      unternehmen: "Unternehmen",
      nachricht: "Ihre Nachricht (optional)",
      success: "Vielen Dank für Ihre Anfrage. Wir melden uns zeitnah bei Ihnen."
    },
    assetDownload: "Asset herunterladen"
  },
  en: {
    nav: {
      ki: "AI",
      leistungen: "Services",
      szenarien: "Scenarios",
      insights: "Insights",
      philosophie: "Philosophy",
      team: "Team",
      kontakt: "Contact",
      incidentMelden: "REPORT INCIDENT",
      backToCyber: "Cybersecurity"
    },
    ki: {
      seoTitle: "Clavis AI — Building, operating and securing AI",
      seoDesc: "Clavis Cyber is an AI company with roots in cybersecurity. We build our own AI, operate it in audit-proof enterprise use, and secure AI systems the way attackers would probe them.",
      classification: "AI Capability · Berlin",
      hero: {
        tagline: "An AI company with roots in cybersecurity",
        headline: "We don't talk about AI. We build it.",
        subline: "For us, cybersecurity isn't a topic alongside AI – it is the proof of origin: the hardest discipline in which AI systems have to hold up. Where mistakes serve the attacker and every statement must withstand scrutiny.",
        sequence: [
          { key: "BUILD", sentence: "Our own AI model specialised in cybersecurity, and a fully AI-native platform." },
          { key: "OPERATE", sentence: "AI in production – centrally governed, audit-proof, with humans accountable." },
          { key: "SECURE", sentence: "We probe AI systems offensively – the way attackers would." }
        ],
        lead: "Since mid-2026, AI sovereignty is no longer a theoretical question. For us, sovereignty doesn't mean isolation but freedom of choice: we use leading models where they are best, and build our own capabilities – so that dependency remains a decision, not a fate.",
        ctaPrimary: "Let's talk about your AI strategy",
        ctaSecondary: "Origin: Cybersecurity"
      },
      haltung: {
        kicker: "Stance",
        title: "Three convictions",
        claims: [
          { n: "a", title: "AI competence shows in operation", text: "Competence is proven by systems in production, not by slide decks. What we claim, runs." },
          { n: "b", title: "Sovereignty is freedom of choice", text: "June 2026 showed that access to models can become a bargaining chip overnight. Freedom of choice exists when you master the alternatives yourself." },
          { n: "c", title: "Trust needs control points", text: "Trust comes from control points you can show. The human stays accountable – as architecture, not as a statement of intent." }
        ]
      },
      fields: [
        {
          nummer: "01",
          kicker: "Capability 01",
          title: "Build AI",
          lead: "We are an engineering house. We build every day.",
          blocks: [
            { title: "Our own AI model", badge: "In development", text: "A German AI model specialised in cybersecurity – trained for a domain where every statement has to withstand scrutiny.", note: "More at the Handelsblatt AI Summit in Munich in November 2026." },
            { title: "TACITUS PRINCIPIA", text: "Our fully AI-native platform: AI-assisted analysis, reporting with source grading per the Admiralty Code, twelve quality-assurance stages, and human sign-off before every client contact." }
          ]
        },
        {
          nummer: "02",
          kicker: "Capability 02",
          title: "Operate AI",
          lead: "Human oversight as architecture, not as a statement of intent.",
          badge: "Live in enterprise use",
          blocks: [
            { title: "AI Control Tower", text: "A central view of all AI agents: approval workflows by criticality, escalation logic, and tamper-evident, audit-proof logging." },
            { title: "AI operating model", text: "Advisory on governance, EU AI Act readiness, exit strategies, multi-model strategy and data residency – so AI stays viable and accountable in the enterprise." }
          ]
        },
        {
          nummer: "03",
          kicker: "Capability 03",
          title: "Secure AI",
          lead: "We probe AI offensively – the way attackers would.",
          blocks: [
            { title: "Security assessments", text: "For LLM applications and agents – from architecture to production use." },
            { title: "Prompt injection & jailbreaks", text: "We test resilience against manipulation of model and agent control." },
            { title: "Tool access & data flows", text: "Securing the interfaces through which agents act – where the real risks emerge." },
            { title: "Threat modelling", text: "Threat modelling for agentic systems that decide autonomously and use tools." }
          ],
          note: "Here the cyber origin becomes the differentiator: we don't assess AI from a distance, but from the attacker's perspective."
        }
      ],
      oekosystem: {
        kicker: "Ecosystem",
        title: "We don't build alone",
        lead: "Sovereignty means freedom of choice – and freedom of choice emerges within a network of strong partners.",
        points: [
          "Partnerships with leading AI development, model and infrastructure companies in the US and Europe.",
          "Sovereign data-centre capacity in Germany under construction.",
          "Leading models where they are best – our own capabilities where independence matters."
        ],
        partnersNote: "Several partnerships have not yet been publicly announced. Details will follow after the official announcement."
      },
      cta: {
        kicker: "Contact",
        title: "Let's talk about your AI strategy.",
        text: "An initial conversation with a practitioner, not with sales. No sales pressure, just substantive answers.",
        primary: "Schedule a call",
        secondary: "Go to the cybersecurity site"
      }
    },
    hero: {
      tagline: "Cybersecurity for Decision Makers",
      headline: "Investigate attacks. Close compliance gaps. Deliver intelligence.",
      headlineSub: "Senior-only practice from Berlin for incident response, NIS2/DORA readiness and threat intelligence. First situation report within 48 hours – direct line to the operators, no account-management layer.",
      mechanism: "Senior-only. Intelligence-first. Initial assessment in 48h.",
      proofHint: "Examples of our work:",
      proofItems: [
        { label: "Threat Landscape Briefing", url: ASSETS_EN.threatLandscape },
        { label: "IR-Retainer Runbook", url: ASSETS_EN.irRetainer },
        { label: "NIS2/DORA Readiness Map", url: ASSETS_EN.nis2Readiness }
      ],
      ctaPrimary: "Schedule a call",
      ctaSecondary: "15-min Readiness Check"
    },
    leadership: {
      kicker: "Leadership",
      role: "Founder & Managing Director",
      name: "Abdelkader Cornelius",
      summary: "Clavis is led directly by its founder – a boutique with a network of senior practitioners on a per-mandate basis. No account management, no junior triage.",
      cta: "Profile & network"
    },
    clarity: {
      kicker: "Profile",
      columns: [
        {
          label: "What we do",
          items: [
            "Incident Response & Digital Forensics",
            "NIS2/DORA & BSI compliance readiness",
            "Threat Intelligence & Darknet Monitoring",
            "Penetration Testing & Red Teaming"
          ]
        },
        {
          label: "When you call us",
          items: [
            "24/7, when an attack is unfolding or suspected",
            "Ahead of regulatory deadlines and audits",
            "When the board needs intelligence it can act on",
            "When a test report should yield real findings"
          ]
        },
        {
          label: "What sets us apart",
          items: [
            "Senior practitioners only. No junior triage.",
            "No tool sales, no licence commissions.",
            "Direct line to operators, no account management.",
            "Boutique size, no consulting bureaucracy."
          ]
        }
      ]
    },
    pillars: {
      title: "Our Four Pillars",
      items: [
        {
          nummer: "01",
          titel: "Detect",
          outcome: "You learn about compromised credentials and threats BEFORE they are exploited.",
          deliverable: "Monthly Threat Landscape Briefing, prioritized recommendations, alerting on critical findings.",
          services: ["Threat Intelligence", "Darknet Monitoring", "Attack Surface Management", "Narrative Intelligence"]
        },
        {
          nummer: "02",
          titel: "Protect",
          outcome: "You know exactly where you stand with NIS2/DORA and can communicate it internally.",
          deliverable: "Gap assessment with prioritization, 30/60/90-day action plan, documented tabletop exercise.",
          services: ["NIS2/DORA Readiness", "BCM", "Security Assessments", "Tabletop Exercises"]
        },
        {
          nummer: "03",
          titel: "Test",
          outcome: "You know which vulnerabilities are real business risks - not just CVE numbers.",
          deliverable: "Technical report with business impact, debriefing with your team, prioritized recommendations.",
          services: ["Penetration Tests", "Red Teaming", "Social Engineering", "OT Security Assessments"]
        },
        {
          nummer: "04",
          titel: "Respond",
          outcome: "You have clarity about scope and severity within hours.",
          deliverable: "Forensic analysis report (court-admissible), timeline with IOCs, documentation for insurance.",
          services: ["Incident Response", "Digital Forensics", "Crisis Management", "Payment Tracing"]
        }
      ]
    },
    differenzierung: {
      title: "What Sets Us Apart",
      negativ: [
        "We don't sell tool licenses.",
        "We don't deliver PDF pentests without insights.",
        "We don't run a ticket system - because incidents aren't tickets.",
        "We don't have junior analysts forwarding your request."
      ],
      positiv: [
        { title: "Senior-Only", desc: "Clients speak with the people doing the work." },
        { title: "Intelligence-First", desc: "We sell understanding, not just reaction." },
        { title: "Direct Line", desc: "To the operators, no detours." },
        { title: "German Presence", desc: "With understanding of local regulations." }
      ]
    },
    szenarien: {
      title: "Typical Scenarios",
      items: [
        { title: "Incident Response", link: "/en/szenario/incident", desc: "Emergency: Attacker in network" },
        { title: "NIS2/Compliance", link: "/en/szenario/nis2", desc: "Regulatory: NIS2, DORA, BSI" },
        { title: "Threat Intelligence", link: "/en/szenario/threat-intelligence", desc: "Detect before it happens" },
        { title: "Offensive Security", link: "/en/szenario/pentest", desc: "Pentests with insights" }
      ]
    },
    teamTeaser: {
      title: "Senior-Only means:",
      desc: "Clavis Cyber was founded by practitioners – not consultants who build slides, but people who have handled incidents and led companies through crises.",
      stats: ["80+ years combined experience", "200+ incidents", "6 experts"],
      linkText: "More about our team"
    },
    kontakt: {
      title: "Contact",
      ctaPrimary: "Schedule a call",
      ctaSecondary: "15-min Readiness Check"
    },
    notfallBanner: {
      text: "Incident? Call now:",
      phone: "+49 30 75439203",
      linkText: "Report incident"
    },
    incidentIntake: {
      title: "Report Incident",
      phoneLabel: "Call now:",
      phone: "+49 30 75439203",
      bullets: [
        "Callback and assessment typically within 15-30 minutes.",
        "Forensic preservation of critical systems.",
        "Clear action recommendations for the next hours."
      ],
      formTitle: "Or fill out short form:",
      firma: "Company",
      rueckruf: "Callback number",
      beschreibung: "What is happening?",
      verschluesselt: "Are systems encrypted?",
      ja: "Yes",
      nein: "No",
      submit: "Report Incident",
      hinweis: "No ticket system. Callback typically within 15-30 minutes.",
      success: "Your report has been submitted. We will call you back immediately."
    },
    szenarioIncident: {
      headline: "An attacker was in your network.",
      subline: "Maybe they still are.",
      risk: "Every hour without clarity is an hour in which the attacker exfiltrates data or installs backdoors.",
      whatWeDo: "What we do in the first 72 hours:",
      steps: [
        "Immediate callback and situation assessment (15-30 min)",
        "Remote triage or on-site deployment within 4 hours",
        "Forensic preservation of critical systems",
        "Identification of attack path and lateral movement",
        "Daily situation briefings for your management",
        "Coordination with authorities and insurance (on request)"
      ],
      timeline: {
        title: "Typical Timeline",
        items: [
          { time: "0-30 min", desc: "Callback, initial assessment, Go/No-Go for on-site" },
          { time: "2-4 hrs", desc: "Team on-site or remote access established" },
          { time: "24 hrs", desc: "First situation report: Scope, attack vectors, recommendations" },
          { time: "48-72 hrs", desc: "Forensic report, remediation plan, recovery" }
        ]
      },
      deliverables: {
        title: "What You Get",
        items: [
          "Forensic analysis report (court-admissible)",
          "Timeline with all IOCs (Indicators of Compromise)",
          "Documentation for cyber insurance",
          "Remediation playbook for your IT team",
          "Optional: Support with authority notifications (BSI, Data Protection)"
        ]
      },
      caseStudy: {
        label: "From Practice",
        title: "Ransomware at Automotive Supplier",
        text: "Friday 10:15 PM: Encrypted systems. Saturday 2:00 AM: Our team on-site. Monday 6:00 AM: Production running again. No ransom paid, 100% data recovered.",
        stats: ["< 4h to on-site", "72h to recovery", "€0 ransom"]
      },
      faqs: [
        { q: "What does an IR engagement cost?", a: "We work with daily rates. For retainer clients, the first 24h are included. For new clients: transparent cost breakdown after initial call." },
        { q: "Do you work with our cyber insurance?", a: "Yes. We know the requirements of common insurers and deliver documentation that supports your claims." },
        { q: "Can you help preventively?", a: "Yes. Our IR retainer gives you guaranteed response times and regular readiness checks." }
      ],
      cta: "Report Incident",
      asset: { label: "IR-Retainer Scope & Engagement Model", url: ASSETS_EN.irRetainer }
    },
    szenarioNis2: {
      headline: "NIS2 is coming. Your readiness level: unclear.",
      subline: "The deadline is approaching. The requirements are complex.",
      risk: "Paper compliance doesn't protect against fines if operational reality looks different. Managing directors are personally liable.",
      whatWeDo: "What we do in the first 30 days:",
      steps: [
        "Gap assessment against NIS2/DORA requirements",
        "Interviews with IT, Security and business units",
        "Evaluation of your current measures",
        "Prioritized 30/60/90-day action plan",
        "Management summary for board/executives",
        "Documented tabletop exercise for your crisis team"
      ],
      timeline: {
        title: "Typical Timeline",
        items: [
          { time: "Week 1", desc: "Kick-off, document review, stakeholder interviews" },
          { time: "Week 2", desc: "Gap assessment, risk evaluation" },
          { time: "Week 3", desc: "Action plan, prioritization with your team" },
          { time: "Week 4", desc: "Tabletop exercise, final report, handover" }
        ]
      },
      deliverables: {
        title: "What You Get",
        items: [
          "NIS2/DORA Gap Assessment Report",
          "Prioritized action list with effort estimates",
          "Management presentation for board",
          "30/60/90-day roadmap",
          "Documented tabletop exercise",
          "Optional: Implementation support"
        ]
      },
      caseStudy: {
        label: "From Practice",
        title: "Critical Infrastructure Healthcare Provider",
        text: "A hospital group didn't know where they stood with NIS2. After 4 weeks: Clear situation overview, prioritized plan, and a management team that knows what to do.",
        stats: ["4 weeks", "47 gaps identified", "Roadmap to Q2"]
      },
      faqs: [
        { q: "Does NIS2 apply to us?", a: "If you operate in one of the 18 critical sectors and exceed certain thresholds, very likely. We clarify this in the initial call." },
        { q: "How is DORA different from NIS2?", a: "DORA is specific to the financial sector and has its own ICT risk management requirements. We cover both." },
        { q: "Can you help with implementation?", a: "Yes. After the assessment, we can support individual workstreams or act as a sparring partner." }
      ],
      cta: "15-min NIS2 Readiness Check",
      asset: { label: "NIS2/DORA 30/60/90 Day Plan", url: ASSETS_EN.nis2Readiness }
    },
    szenarioThreat: {
      headline: "You want to know what attackers know about you.",
      subline: "Before they use it against you.",
      risk: "Compromised credentials are often only noticed when they are used. Darknet leaks can remain undetected for months.",
      whatWeDo: "What we do:",
      steps: [
        "Systematic search in darknet, breach databases, paste sites",
        "Analysis of your external attack surface",
        "Identification of compromised credentials and accounts",
        "Assessment by business impact and criticality",
        "Prioritized action recommendations",
        "Monthly monitoring (optional)"
      ],
      timeline: {
        title: "Typical Timeline",
        items: [
          { time: "Day 1", desc: "Scope definition, handover of relevant domains/assets" },
          { time: "Day 2-5", desc: "Intelligence collection, darknet research" },
          { time: "Day 6-7", desc: "Analysis, prioritization, report creation" },
          { time: "Day 8", desc: "Briefing with your team, Q&A" }
        ]
      },
      deliverables: {
        title: "What You Get",
        items: [
          "Threat Landscape Report for your organization",
          "List of compromised credentials with source",
          "Attack Surface Assessment",
          "Prioritized action recommendations",
          "Executive summary for management",
          "Optional: Monthly Threat Briefing"
        ]
      },
      caseStudy: {
        label: "From Practice",
        title: "DAX Company Discovers Breach",
        text: "During a threat assessment, we found 340 compromised employee credentials from a 2-year-old breach. 12 of them were still active and had VPN access.",
        stats: ["340 credentials", "12 active accounts", "2 with admin rights"]
      },
      faqs: [
        { q: "What's the difference to a pentest?", a: "Threat intelligence looks at what attackers already know about you. Pentests test how they could break in. Both complement each other." },
        { q: "How often should this be done?", a: "Once a year as baseline, ideally with monthly monitoring for new leaks." },
        { q: "Do you really find everything?", a: "We systematically search all relevant sources. No 100% guarantee, but our analysts have experience from hundreds of assessments." }
      ],
      cta: "30-min Threat Posture Briefing",
      asset: { label: "Threat Landscape Brief (Example)", url: ASSETS_EN.threatLandscape }
    },
    szenarioPentest: {
      headline: "Offensive Security requires the best.",
      subline: "That's why we work with specialized partners.",
      risk: "An honest word: Offensive security requires highly specialized expertise that you don't build on the side. Instead of pretending, we connect you with hand-picked partners – the best in their field.",
      whatWeDo: "Why this approach:",
      steps: [
        "We know our strengths – and our limits",
        "Our partners specialize in offensive security, not diversified",
        "You get real experts, not generalists with a pentest certificate",
        "We remain your point of contact and coordinate the project",
        "Seamless integration with our services (IR, Threat Intel, Compliance)",
        "Honesty builds trust – and better results"
      ],
      timeline: {
        title: "How it works",
        items: [
          { time: "Step 1", desc: "You describe your needs – we understand your context" },
          { time: "Step 2", desc: "We recommend the right partner from our network" },
          { time: "Step 3", desc: "Joint scoping – you, us, the partner" },
          { time: "Step 4", desc: "The partner conducts the test, we stay involved" }
        ]
      },
      deliverables: {
        title: "What You Get",
        items: [
          "Access to top-tier offensive security specialists",
          "One point of contact at CLAVIS CYBER for the entire project",
          "Seamless integration with your other security initiatives",
          "Technical report with business context",
          "Optional: Remediation support by CLAVIS CYBER",
          "Long-term partnership instead of one-time transaction"
        ]
      },
      caseStudy: {
        label: "Our Philosophy",
        title: "Integrity over revenue",
        text: "We could offer pentests ourselves and earn more. But our clients trust us because we're honest. This trust is worth more than any contract.",
        stats: ["100% Honesty", "Hand-picked partners", "Your success matters"]
      },
      faqs: [
        { q: "Why doesn't CLAVIS CYBER do pentests themselves?", a: "Offensive security requires permanent specialization. Our strength lies in incident response, threat intelligence, and compliance. For pentests, we work with the best." },
        { q: "Who are your partners?", a: "Specialized offensive security boutiques with proven expertise. Not big consulting firms, but real practitioners. We're happy to make the introduction." },
        { q: "Does this cost more?", a: "No. You pay the partner directly. We don't take referral fees – our profit is your trust and a long-term collaboration." }
      ],
      cta: "Request Consultation",
      asset: { label: "Offensive Security: Our Partner Approach", url: ASSETS_EN.pentestScoping }
    },
    philosophie: {
      title: "Philosophy",
      headline: "Integrity over revenue",
      subline: "Why we're different – and why we'll stay that way.",
      intro: "In an industry that often thrives on fear and exaggeration, we've chosen a different path: honesty. Sometimes that means turning down contracts. But it also means our clients trust us – and that's worth more than any short-term gain.",
      sections: {
        whoWeAre: {
          title: "Who we are",
          text: "Clavis Cyber was founded by practitioners – not consultants who build slides, but people who have handled incidents and led companies through crises. We know the difference between theory and what really matters at 3 AM."
        },
        whatWeStandFor: {
          title: "What we stand for",
          items: [
            { title: "Honesty", text: "We tell you what we can do – and what we can't. If another provider is a better fit, we'll say so." },
            { title: "Focus", text: "We do what we're good at: Incident Response, Threat Intelligence, Compliance. Nothing more, nothing less." },
            { title: "Quality over quantity", text: "We grow slowly and deliberately. Every new employee must share our values – not just their skills." },
            { title: "Long-term relationships", text: "We don't optimize for the next contract, but for trust. That pays off – for both sides." }
          ]
        },
        whatWeDontDo: {
          title: "What we deliberately don't do",
          intro: "Just as important as what we do is what we don't:",
          items: [
            { title: "Offensive Security / Pentests", text: "This requires specialists who do nothing else. We connect you with hand-picked partners." },
            { title: "Managed SOC / 24/7 Monitoring", text: "We're consultants and incident responders, not a managed security service provider." },
            { title: "Sell products", text: "We're vendor-independent. We recommend what fits you – not what earns us commission." },
            { title: "Spread fear", text: "Cybersecurity is complex enough. We explain risks objectively, without scaremongering." }
          ]
        },
        partnerApproach: {
          title: "Our partner approach",
          text: "Instead of offering everything ourselves and being mediocre, we work with specialized partners. For penetration tests, we connect you with experts who do nothing else – because we're honest and you deserve the best.",
          quote: "We could offer pentests ourselves. It would be profitable. But our clients trust us because we're honest. That trust is worth more than any contract."
        },
        howWeWork: {
          title: "How we work",
          items: [
            { title: "Senior-only", text: "From day one, you work with experienced experts. No juniors learning while you pay." },
            { title: "Direct communication", text: "You reach the people working on your case. No account manager in between." },
            { title: "Transparent pricing", text: "Clear daily rates, no hidden costs. You know what it costs upfront." },
            { title: "Results, not reports", text: "We deliver insights and recommendations, not 100-page documents nobody reads." }
          ]
        }
      },
      cta: {
        headline: "Does this sound like the partner you're looking for?",
        text: "Let's talk. No obligations, no sales pressure.",
        button: "Schedule a conversation"
      }
    },
    team: {
      kicker: "Leadership & Practice",
      title: "Who runs Clavis – and who works alongside.",
      philosophie: "Clavis Cyber is led by a practitioner and operates as a boutique with a network of senior specialists engaged on a per-mandate basis. No slide-deck consultants, no junior triage – the people at the table do the work themselves.",
      sections: {
        founder: "Founder & Managing Director",
        bio: "Profile",
        werdegang: "Career",
        schwerpunkte: "Areas of Practice",
        behoerden: "Public-Sector Engagement",
        speaking: "Speaking & Publications (Selected)",
        zertifikate: "Qualifications",
        sprachen: "Languages",
        kontakt: "Direct contact"
      },
      verbund: {
        kicker: "Practice Network",
        title: "Boutique, not a one-man show.",
        body: "For engagements that require more than one discipline or more capacity, Clavis works with a defined circle of senior practitioners – forensic specialists, threat intelligence analysts, pentesters, crisis communicators. The group is assembled per mandate: no account management, no internal routing, just directly accountable operators. For discretion reasons, contributors are not listed publicly but introduced personally in the context of a specific mandate.",
        kompetenzenLabel: "Disciplines covered in the network",
        kompetenzen: [
          "Incident Response & Digital Forensics (DFIR)",
          "Cyber Threat Intelligence & darknet monitoring",
          "Malware analysis & reverse engineering",
          "Penetration testing & red teaming",
          "Crisis and board-level communication",
          "NIS-2, DORA, BSI baseline protection, KRITIS, GDPR",
          "AI security & AI governance",
          "Forensic evidence collection to German procedural standards"
        ],
        prinzipienLabel: "Network principle",
        prinzipien: [
          "Every engagement is led directly by a senior practitioner.",
          "No junior triage, no account manager as interface.",
          "Contributors are selected per mandate – not slotted to fill capacity.",
          "Language coverage: German and English throughout, additional languages depending on mandate."
        ]
      }
    },
    impressum: {
      title: "Legal Notice",
      content: `CLAVIS CYBER GmbH
Kurfürstendamm 194
10707 Berlin
Germany

Represented by:
Abdelkader Cornelius (Managing Director)

Contact:
Phone: +49 30 75439203
Email: info@clavis-cyber.de
Incident hotline (24/7): +49 30 75439203

Registry Entry:
Registered in the Commercial Register.
Registry Court: Amtsgericht Charlottenburg (Berlin)
Registration Number: HRB 288051 B

VAT ID:
VAT identification number according to §27a UStG: applied for

Responsible for content according to § 18 Abs. 2 MStV:
Abdelkader Cornelius
Kurfürstendamm 194
10707 Berlin

Dispute Resolution:
The European Commission provides a platform for online dispute resolution (OS): https://ec.europa.eu/consumers/odr/
We are not willing or obliged to participate in dispute resolution proceedings before a consumer arbitration board.

Liability for Content:
As a service provider, we are responsible for our own content on these pages in accordance with § 7 para. 1 TMG under general law. According to §§ 8 to 10 TMG, however, we are not obliged to monitor transmitted or stored third-party information or to investigate circumstances that indicate illegal activity.

Liability for Links:
Our offer contains links to external third-party websites over whose content we have no influence. Therefore, we cannot accept any liability for this third-party content. The respective provider or operator of the pages is always responsible for the content of the linked pages.`
    },
    datenschutz: {
      title: "Privacy Policy",
      content: `1. Privacy at a Glance

General Information
The following notes provide a simple overview of what happens to your personal data when you visit this website. Personal data is any data that can be used to personally identify you. Detailed information on the subject of data protection can be found in our privacy policy listed below.

2. Controller

The controller responsible for data processing on this website is:

CLAVIS CYBER GmbH
Kurfürstendamm 194
10707 Berlin
Germany

Managing Director: Abdelkader Cornelius
Phone: +49 30 75439203
Email: info@clavis-cyber.de

3. How Do We Collect Your Data?

Your data is collected in part by you providing it to us, e.g., by entering data in a contact form or by email. Other data is automatically collected by our IT systems when you visit the website, with your consent where required. This is primarily technical data (e.g., internet browser, operating system, or time of page access). This data is collected automatically as soon as you enter this website.

4. What Do We Use Your Data For?

Part of the data is collected to ensure error-free provision of the website. No analysis of your user behavior takes place. No tracking or analytics tools are used.

5. What Rights Do You Have Regarding Your Data?

You have the right at any time to receive information free of charge about the origin, recipient and purpose of your stored personal data. You also have the right to request the correction or deletion of this data. If you have given consent to data processing, you can revoke this consent at any time for the future. You also have the right, under certain circumstances, to request the restriction of the processing of your personal data. Furthermore, you have the right to lodge a complaint with the competent supervisory authority.

You can contact us at any time regarding this and other questions on the subject of data protection.

6. Hosting

This website is hosted by Hetzner Online GmbH, Industriestr. 25, 91710 Gunzenhausen, Germany. For details, please refer to Hetzner's privacy policy: https://www.hetzner.com/de/legal/privacy-policy/

The use of Hetzner is based on Art. 6(1)(f) GDPR. We have a legitimate interest in the most reliable presentation of our website possible. Where consent has been obtained, processing is carried out exclusively on the basis of Art. 6(1)(a) GDPR. Consent can be revoked at any time.

We have concluded a data processing agreement (DPA) with Hetzner.

7. Server Log Files

The provider of the pages automatically collects and stores information in so-called server log files, which your browser automatically transmits to us. These are:
– Browser type and version
– Operating system used
– Referrer URL
– Host name of the accessing computer
– Time of the server request
– IP address

This data is not merged with other data sources. The collection of this data is based on Art. 6(1)(f) GDPR. The website operator has a legitimate interest in the technically error-free presentation and optimization of its website – for this purpose, server log files must be collected.

8. SSL/TLS Encryption

This site uses SSL/TLS encryption for security reasons and to protect the transmission of confidential content. You can recognize an encrypted connection by the fact that the address line of the browser changes from "http://" to "https://" and by the lock symbol in your browser line. When SSL/TLS encryption is activated, the data you transmit to us cannot be read by third parties.

9. Contact Form

If you send us inquiries via the contact form, your details from the form, including the contact data you provide there, will be stored by us for the purpose of processing the inquiry and in case of follow-up questions. We do not pass on this data without your consent.

The processing of this data is based on Art. 6(1)(b) GDPR if your inquiry is related to the performance of a contract or is necessary for the implementation of pre-contractual measures. In all other cases, the processing is based on our legitimate interest in the effective processing of inquiries addressed to us (Art. 6(1)(f) GDPR) or on your consent (Art. 6(1)(a) GDPR) if this has been requested.

The data you enter in the contact form will remain with us until you request deletion, revoke your consent to storage, or the purpose for data storage no longer applies. Mandatory statutory provisions – in particular retention periods – remain unaffected.

10. Inquiry by Email or Phone

If you contact us by email or telephone, your inquiry including all resulting personal data (name, inquiry) will be stored and processed by us for the purpose of handling your request. We do not pass on this data without your consent.

The processing of this data is based on Art. 6(1)(b) GDPR if your inquiry is related to the performance of a contract or is necessary for the implementation of pre-contractual measures. In all other cases, the processing is based on our legitimate interest in the effective processing of inquiries addressed to us (Art. 6(1)(f) GDPR) or on your consent (Art. 6(1)(a) GDPR) if this has been requested.

11. Cookies

This website only uses technically necessary cookies. A technically necessary cookie stores your cookie consent setting in your browser's local storage (localStorage). No tracking, analytics or marketing cookies are used.

The legal basis for the use of technically necessary cookies is Art. 6(1)(f) GDPR. We have a legitimate interest in storing technically necessary cookies for the technically error-free and optimized provision of our services.

12. Fonts (Locally Hosted)

This website uses the fonts "Inter" and "Cormorant Garamond" for consistent display. All fonts are hosted locally on our server. No connection to external servers (e.g., Google) takes place.

13. Your Rights in Detail

Under the GDPR, you have the following rights:

a) Right of access (Art. 15 GDPR): You have the right to obtain confirmation as to whether personal data concerning you is being processed, and to access such data.
b) Right to rectification (Art. 16 GDPR): You have the right to request the rectification of inaccurate or the completion of your personal data stored by us.
c) Right to erasure (Art. 17 GDPR): You have the right to request the erasure of your personal data stored by us, unless the processing is necessary for exercising the right of freedom of expression, for compliance with a legal obligation, for reasons of public interest, or for the establishment, exercise or defense of legal claims.
d) Right to restriction of processing (Art. 18 GDPR): You have the right to request the restriction of the processing of your personal data.
e) Right to data portability (Art. 20 GDPR): You have the right to receive your personal data in a commonly used, machine-readable format or to request the transfer to another controller.
f) Right to object (Art. 21 GDPR): You have the right to object at any time to the processing of your personal data on grounds relating to your particular situation.
g) Right to withdraw consent (Art. 7(3) GDPR): You have the right to withdraw consent once given at any time.
h) Right to lodge a complaint with a supervisory authority (Art. 77 GDPR): You have the right to lodge a complaint with a supervisory authority if you believe that the processing of your personal data infringes the GDPR.

Competent supervisory authority:
Berliner Beauftragte für Datenschutz und Informationsfreiheit
Friedrichstr. 219
10969 Berlin
Phone: +49 30 13889-0
Email: mailbox@datenschutz-berlin.de
https://www.datenschutz-berlin.de

14. Currency and Amendments to this Privacy Policy

This privacy policy is currently valid as of April 2026. Due to the further development of our website or due to changed legal or regulatory requirements, it may become necessary to amend this privacy policy.`
    },
    footer: {
      impressum: "Legal Notice",
      datenschutz: "Privacy Policy"
    },
    contact: {
      name: "Name",
      email: "Email",
      unternehmen: "Company",
      nachricht: "Your message (optional)",
      success: "Thank you for your inquiry. We will get back to you shortly."
    },
    assetDownload: "Download Asset"
  }
};

const useLanguage = () => useContext(LanguageContext);

// Navigation Component
const Navigation = ({ onIncidentClick, onContactClick }) => {
  const { lang, setLang, t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isKi = location.pathname === "/ki" || location.pathname === "/en/ki";

  const scrollToSection = (id) => {
    const isHome = location.pathname === "/" || location.pathname === "/en";
    if (!isHome) {
      navigate(lang === "de" ? "/" : "/en");
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
    setMobileMenuOpen(false);
  };

  const switchLanguage = () => {
    const newLang = lang === "de" ? "en" : "de";
    setLang(newLang);
    const currentPath = location.pathname;
    if (newLang === "en") {
      if (currentPath === "/") navigate("/en");
      else if (!currentPath.startsWith("/en")) navigate("/en" + currentPath);
    } else {
      if (currentPath === "/en") navigate("/");
      else if (currentPath.startsWith("/en")) navigate(currentPath.replace("/en", ""));
    }
  };

  const navLink = "text-ink hover:text-brass-deep transition-colors text-[14px] font-medium";

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-paper/95 backdrop-blur-sm border-b border-hairline">
      <div className="w-full px-6 lg:px-10">
        <div className="flex items-center justify-between h-[68px]">
          <Link to={lang === "de" ? "/" : "/en"} className="flex items-center" data-testid="nav-logo" aria-label="clavis/cyber – Startseite">
            <Wordmark className="text-[26px]" />
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-x-8">
            <Link
              to={lang === "de" ? "/ki" : "/en/ki"}
              className={`${navLink} ${isKi ? "text-brass-deep" : ""}`}
              data-testid="nav-ki"
              aria-current={isKi ? "page" : undefined}
            >
              {t.nav.ki}
            </Link>
            <button onClick={() => scrollToSection("leistungen")} className={navLink} data-testid="nav-leistungen">
              {t.nav.leistungen}
            </button>
            <button onClick={() => scrollToSection("szenarien")} className={navLink} data-testid="nav-szenarien">
              {t.nav.szenarien}
            </button>
            <Link to={lang === "de" ? "/insights" : "/en/insights"} className={navLink} data-testid="nav-insights">
              {t.nav.insights}
            </Link>
            <Link to={lang === "de" ? "/philosophie" : "/en/philosophie"} className={navLink} data-testid="nav-philosophie">
              {t.nav.philosophie}
            </Link>
            <Link to={lang === "de" ? "/team" : "/en/team"} className={navLink} data-testid="nav-team">
              {t.nav.team}
            </Link>
            <button onClick={() => onContactClick("general")} className={navLink} data-testid="nav-kontakt">
              {t.nav.kontakt}
            </button>
            <button
              onClick={switchLanguage}
              className="font-mono text-[11px] tracking-classification uppercase text-mid-text hover:text-ink transition-colors pl-2 border-l border-hairline ml-1"
              data-testid="nav-lang-switch"
              aria-label={lang === "de" ? "Switch to English" : "Auf Deutsch wechseln"}
            >
              {lang === "de" ? "EN" : "DE"}
            </button>
            {!isKi && (
              <button
                onClick={onIncidentClick}
                className="inline-flex items-center justify-center bg-signal-red text-paper font-semibold uppercase tracking-label text-[12px] px-5 py-[10px] hover:bg-signal-red-deep transition-colors"
                data-testid="nav-incident-btn"
              >
                {t.nav.incidentMelden}
              </button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-x-5">
            <button
              onClick={switchLanguage}
              className="font-mono text-[11px] tracking-classification uppercase text-mid-text hover:text-ink"
              aria-label={lang === "de" ? "Switch to English" : "Auf Deutsch wechseln"}
            >
              {lang === "de" ? "EN" : "DE"}
            </button>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-ink hover:text-brass-deep transition-colors"
              data-testid="mobile-menu-btn"
              aria-label="Menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden pb-6 pt-2 border-t border-hairline">
            <div className="flex flex-col gap-y-4 pt-4">
              <Link to={lang === "de" ? "/ki" : "/en/ki"} className={`${navLink} text-left ${isKi ? "text-brass-deep" : ""}`} onClick={() => setMobileMenuOpen(false)}>
                {t.nav.ki}
              </Link>
              <button onClick={() => scrollToSection("leistungen")} className={`${navLink} text-left`}>
                {t.nav.leistungen}
              </button>
              <button onClick={() => scrollToSection("szenarien")} className={`${navLink} text-left`}>
                {t.nav.szenarien}
              </button>
              <Link to={lang === "de" ? "/insights" : "/en/insights"} className={`${navLink} text-left`} onClick={() => setMobileMenuOpen(false)}>
                {t.nav.insights}
              </Link>
              <Link to={lang === "de" ? "/philosophie" : "/en/philosophie"} className={`${navLink} text-left`} onClick={() => setMobileMenuOpen(false)}>
                {t.nav.philosophie}
              </Link>
              <Link to={lang === "de" ? "/team" : "/en/team"} className={`${navLink} text-left`} onClick={() => setMobileMenuOpen(false)}>
                {t.nav.team}
              </Link>
              <button onClick={() => { onContactClick("general"); setMobileMenuOpen(false); }} className={`${navLink} text-left`}>
                {t.nav.kontakt}
              </button>
              {!isKi && (
                <button
                  onClick={() => { onIncidentClick(); setMobileMenuOpen(false); }}
                  className="inline-flex items-center justify-center bg-signal-red text-paper font-semibold uppercase tracking-label text-[12px] px-5 py-3 mt-2 hover:bg-signal-red-deep transition-colors"
                >
                  {t.nav.incidentMelden}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Emergency Banner Component with Pulsing Effect
const NotfallBanner = ({ onIncidentClick }) => {
  const { t } = useLanguage();
  
  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 bg-paper border-t border-hairline px-6 py-3" data-testid="notfall-banner">
      <div className="max-w-6xl mx-auto flex items-center justify-center md:justify-between gap-4 flex-wrap">
        <div className="flex items-baseline gap-3">
          <span className="font-mono text-[11px] tracking-classification uppercase text-signal-red-deep">24/7</span>
          <span className="text-ink text-sm">{t.notfallBanner.text}</span>
        </div>
        <div className="flex items-center gap-5">
          <a
            href="tel:+493075439203"
            className="text-ink text-sm font-semibold underline decoration-brass decoration-1 underline-offset-[4px] hover:text-brass-deep transition-colors"
            data-testid="notfall-phone"
          >
            {t.notfallBanner.phone}
          </a>
          <button
            onClick={onIncidentClick}
            className="inline-flex items-center justify-center bg-signal-red text-paper font-semibold uppercase tracking-label text-[11px] px-4 py-[7px] hover:bg-signal-red-deep transition-colors"
            data-testid="notfall-link"
          >
            {t.notfallBanner.linkText}
          </button>
        </div>
      </div>
    </div>
  );
};

// Incident Intake Modal - Enhanced with structured triage
const IncidentIntakeModal = ({ isOpen, onClose }) => {
  const { t, lang } = useLanguage();
  const [formData, setFormData] = useState({
    // Stufe 1 - Pflicht
    dringlichkeit: "",
    firma: "",
    kontaktperson: "",
    rolle: "",
    rueckrufnummer: "",
    email: "",
    // Stufe 2 - Triage
    zeitpunkt_bemerkt: "",
    symptome: {
      verschluesselt: false,
      loesegeldforderung: false,
      verdaechtige_logins: false,
      systeme_nicht_erreichbar: false,
      datenleck: false,
      andere: false
    },
    anzahl_systeme: "",
    beschreibung: ""
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showTriage, setShowTriage] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const handleSymptomChange = (symptom) => {
    setFormData({
      ...formData,
      symptome: {
        ...formData.symptome,
        [symptom]: !formData.symptome[symptom]
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    if (!formData.dringlichkeit) {
      setErrorMsg(lang === 'de'
        ? 'Bitte oben eine Dringlichkeit auswählen.'
        : 'Please choose an urgency level above.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/incident`, formData);
      setSubmitted(true);
    } catch (error) {
      console.error("Error submitting incident:", error);
      setErrorMsg(lang === 'de'
        ? 'Senden fehlgeschlagen. Bitte direkt an incident@clavis-cyber.de schreiben.'
        : 'Submission failed. Please email incident@clavis-cyber.de directly.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-ink/60 backdrop-blur-sm" data-testid="incident-modal">
      <div className="bg-paper border border-hairline max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 sm:p-8">
          <div className="flex justify-between items-start mb-6">
            <h2 className="font-sans text-2xl md:text-3xl text-ink font-extrabold tracking-tightest">{t.incidentIntake.title}</h2>
            <button onClick={onClose} className="text-clavis-textGray hover:text-clavis-text transition-colors" data-testid="incident-modal-close">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Phone Section */}
          <div className="mb-8 p-6 border border-hairline bg-paper-mid rounded-lg">
            <p className="text-clavis-textGray mb-2">{t.incidentIntake.phoneLabel}</p>
            <a href="tel:+493075439203" className="text-ink text-2xl md:text-3xl font-bold hover:text-brass-deep block mb-4 underline decoration-brass decoration-1 underline-offset-[6px]" data-testid="incident-phone">
              {t.incidentIntake.phone}
            </a>
            <ul className="space-y-2">
              {t.incidentIntake.bullets.map((bullet, i) => (
                <li key={i} className="text-clavis-text text-sm flex items-start gap-2">
                  <span className="text-clavis-gold mt-1">•</span>
                  {bullet}
                </li>
              ))}
            </ul>
          </div>

          {submitted ? (
            <div className="text-center py-8">
              <div className="text-clavis-gold text-5xl mb-4">✓</div>
              <p className="text-clavis-text text-lg">{t.incidentIntake.success}</p>
            </div>
          ) : (
            <>
              <h3 className="text-clavis-text font-semibold mb-4">{t.incidentIntake.formTitle}</h3>
              <form onSubmit={handleSubmit} className="space-y-6">
                
                {/* STUFE 1 - SOFORT (Pflicht) */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-mono text-[11px] tracking-classification uppercase text-signal-red-deep">01</span>
                    <span className="text-clavis-gold font-semibold text-sm uppercase tracking-wide">
                      {lang === 'de' ? 'Sofort benötigt' : 'Required immediately'}
                    </span>
                  </div>
                  
                  {/* Dringlichkeit */}
                  <div>
                    <label className="block text-clavis-textGray text-sm mb-2">
                      {lang === 'de' ? 'Dringlichkeit' : 'Urgency'} *
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { value: 'aktiv', de: '🔴 Aktiver Angriff', en: '🔴 Active Attack' },
                        { value: 'verdacht', de: '🟡 Verdacht', en: '🟡 Suspicion' },
                        { value: 'post', de: '🟢 Post-Incident', en: '🟢 Post-Incident' }
                      ].map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => setFormData({ ...formData, dringlichkeit: option.value })}
                          className={`p-3 border text-sm transition-all rounded ${
                            formData.dringlichkeit === option.value
                              ? 'border-clavis-gold bg-clavis-gold/10 text-clavis-text'
                              : 'border-clavis-border text-clavis-textGray hover:border-brass'
                          }`}
                          data-testid={`incident-dringlichkeit-${option.value}`}
                        >
                          {lang === 'de' ? option.de : option.en}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Firma */}
                  <div>
                    <label className="block text-clavis-textGray text-sm mb-1">
                      {lang === 'de' ? 'Unternehmen' : 'Company'} *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.firma}
                      onChange={(e) => setFormData({ ...formData, firma: e.target.value })}
                      className="w-full bg-white border border-clavis-border px-4 py-2 text-clavis-text focus:border-clavis-gold focus:outline-none"
                      data-testid="incident-firma"
                    />
                  </div>
                  
                  {/* Kontaktperson + Rolle */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-clavis-textGray text-sm mb-1">
                        {lang === 'de' ? 'Kontaktperson' : 'Contact Person'} *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.kontaktperson}
                        onChange={(e) => setFormData({ ...formData, kontaktperson: e.target.value })}
                        className="w-full bg-white border border-clavis-border px-4 py-2 text-clavis-text focus:border-clavis-gold focus:outline-none"
                        placeholder={lang === 'de' ? 'Max Mustermann' : 'John Doe'}
                        data-testid="incident-kontaktperson"
                      />
                    </div>
                    <div>
                      <label className="block text-clavis-textGray text-sm mb-1">
                        {lang === 'de' ? 'Rolle/Position' : 'Role/Position'} *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.rolle}
                        onChange={(e) => setFormData({ ...formData, rolle: e.target.value })}
                        className="w-full bg-white border border-clavis-border px-4 py-2 text-clavis-text focus:border-clavis-gold focus:outline-none"
                        placeholder={lang === 'de' ? 'IT-Leiter, CISO, ...' : 'IT Manager, CISO, ...'}
                        data-testid="incident-rolle"
                      />
                    </div>
                  </div>
                  
                  {/* Telefon + E-Mail */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-clavis-textGray text-sm mb-1">
                        {lang === 'de' ? 'Rückrufnummer' : 'Callback Number'} *
                      </label>
                      <input
                        type="tel"
                        required
                        value={formData.rueckrufnummer}
                        onChange={(e) => setFormData({ ...formData, rueckrufnummer: e.target.value })}
                        className="w-full bg-white border border-clavis-border px-4 py-2 text-clavis-text focus:border-clavis-gold focus:outline-none"
                        data-testid="incident-rueckruf"
                      />
                    </div>
                    <div>
                      <label className="block text-clavis-textGray text-sm mb-1">
                        E-Mail *
                      </label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full bg-white border border-clavis-border px-4 py-2 text-clavis-text focus:border-clavis-gold focus:outline-none"
                        data-testid="incident-email"
                      />
                    </div>
                  </div>
                </div>
                
                {/* STUFE 2 - TRIAGE (Optional) */}
                <div className="border-t border-clavis-gray/20 pt-6">
                  <button
                    type="button"
                    onClick={() => setShowTriage(!showTriage)}
                    className="flex items-center gap-2 text-clavis-textGray hover:text-clavis-gold transition-colors mb-4"
                  >
                    <span className="bg-clavis-gold/20 text-clavis-gold text-xs px-2 py-0.5 font-bold">2</span>
                    <span className="text-sm uppercase tracking-wide">
                      {lang === 'de' ? 'Triage-Informationen (optional, hilft bei Vorbereitung)' : 'Triage Information (optional, helps preparation)'}
                    </span>
                    <svg className={`w-4 h-4 transition-transform ${showTriage ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {showTriage && (
                    <div className="space-y-4 animate-fadeIn">
                      {/* Zeitpunkt */}
                      <div>
                        <label className="block text-clavis-textGray text-sm mb-1">
                          {lang === 'de' ? 'Wann wurde der Vorfall bemerkt?' : 'When was the incident discovered?'}
                        </label>
                        <input
                          type="datetime-local"
                          value={formData.zeitpunkt_bemerkt}
                          onChange={(e) => setFormData({ ...formData, zeitpunkt_bemerkt: e.target.value })}
                          className="w-full bg-white border border-clavis-border px-4 py-2 text-clavis-text focus:border-clavis-gold focus:outline-none"
                          data-testid="incident-zeitpunkt"
                        />
                      </div>
                      
                      {/* Symptome Checkboxen */}
                      <div>
                        <label className="block text-clavis-textGray text-sm mb-2">
                          {lang === 'de' ? 'Beobachtete Symptome' : 'Observed Symptoms'}
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                          {[
                            { key: 'verschluesselt', de: 'Verschlüsselte Dateien', en: 'Encrypted files' },
                            { key: 'loesegeldforderung', de: 'Lösegeldforderung', en: 'Ransom demand' },
                            { key: 'verdaechtige_logins', de: 'Verdächtige Logins', en: 'Suspicious logins' },
                            { key: 'systeme_nicht_erreichbar', de: 'Systeme nicht erreichbar', en: 'Systems unreachable' },
                            { key: 'datenleck', de: 'Datenleck vermutet', en: 'Data leak suspected' },
                            { key: 'andere', de: 'Andere', en: 'Other' }
                          ].map((symptom) => (
                            <label key={symptom.key} className="flex items-center gap-2 cursor-pointer p-2 border border-hairline hover:border-brass transition-colors">
                              <input
                                type="checkbox"
                                checked={formData.symptome[symptom.key]}
                                onChange={() => handleSymptomChange(symptom.key)}
                                className="accent-clavis-gold"
                                data-testid={`incident-symptom-${symptom.key}`}
                              />
                              <span className="text-clavis-text text-sm">{lang === 'de' ? symptom.de : symptom.en}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      
                      {/* Anzahl Systeme */}
                      <div>
                        <label className="block text-clavis-textGray text-sm mb-1">
                          {lang === 'de' ? 'Ungefähre Anzahl betroffener Systeme' : 'Approximate number of affected systems'}
                        </label>
                        <select
                          value={formData.anzahl_systeme}
                          onChange={(e) => setFormData({ ...formData, anzahl_systeme: e.target.value })}
                          className="w-full bg-clavis-black border border-clavis-gray/30 px-4 py-2 text-clavis-text focus:border-clavis-gold focus:outline-none"
                          data-testid="incident-anzahl"
                        >
                          <option value="">{lang === 'de' ? 'Bitte wählen...' : 'Please select...'}</option>
                          <option value="1-5">1-5</option>
                          <option value="6-20">6-20</option>
                          <option value="21-100">21-100</option>
                          <option value="100+">100+</option>
                          <option value="unbekannt">{lang === 'de' ? 'Unbekannt' : 'Unknown'}</option>
                        </select>
                      </div>
                      
                      {/* Beschreibung */}
                      <div>
                        <label className="block text-clavis-textGray text-sm mb-1">
                          {lang === 'de' ? 'Was ist passiert? (Freitext)' : 'What happened? (Free text)'}
                        </label>
                        <textarea
                          rows={3}
                          value={formData.beschreibung}
                          onChange={(e) => setFormData({ ...formData, beschreibung: e.target.value })}
                          className="w-full bg-white border border-clavis-border px-4 py-2 text-clavis-text focus:border-clavis-gold focus:outline-none resize-none"
                          placeholder={lang === 'de' ? 'Chronologie, erste Beobachtungen, bereits getroffene Maßnahmen...' : 'Timeline, first observations, actions already taken...'}
                          data-testid="incident-beschreibung"
                        />
                      </div>
                    </div>
                  )}
                </div>
                
                {errorMsg && (
                  <div className="p-3 rounded border border-red-400 bg-red-50 text-red-700 text-sm" data-testid="incident-error">
                    {errorMsg}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full inline-flex items-center justify-center bg-signal-red text-paper py-[14px] font-semibold uppercase tracking-label text-[13px] hover:bg-signal-red-deep transition-colors disabled:opacity-50"
                  data-testid="incident-submit"
                >
                  {loading ? "..." : t.incidentIntake.submit}
                </button>
                <p className="text-clavis-textGray text-sm text-center">{t.incidentIntake.hinweis}</p>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Contact Modal with Offer-specific content - Enhanced with context fields and bot protection
const ContactModal = ({ isOpen, onClose, requestType = "general" }) => {
  const { lang, t } = useLanguage();
  const offerConfig = offerConfigs[lang][requestType] || offerConfigs[lang].general;
  
  const [formData, setFormData] = useState({
    // Basis
    name: "",
    email: "",
    telefon: "",
    unternehmen: "",
    branche: "",
    unternehmensgroesse: "",
    // Kontextbezogen
    interessengebiete: [],
    regulatorik: [],
    pentest_scope: [],
    // Termin
    kontaktzeit: "",
    zeitrahmen: "",
    nachricht: "",
    request_type: requestType,
    // Bot Protection (Honeypot)
    website: "", // Honeypot - should stay empty
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formStartTime] = useState(Date.now()); // Bot protection - time check
  const [error, setError] = useState("");

  useEffect(() => {
    setFormData(prev => ({ ...prev, request_type: requestType }));
  }, [requestType]);

  const handleCheckboxChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(v => v !== value)
        : [...prev[field], value]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Bot Protection: Honeypot check
    if (formData.website) {
      // Bot detected - silently "succeed"
      setSubmitted(true);
      return;
    }
    
    // Bot Protection: Time check (form filled in < 3 seconds = likely bot)
    const timeTaken = Date.now() - formStartTime;
    if (timeTaken < 3000) {
      setSubmitted(true);
      return;
    }
    
    setLoading(true);
    setError("");
    try {
      // Remove honeypot field before sending
      const { website, ...submitData } = formData;
      await axios.post(`${API}/contact`, submitData);
      setSubmitted(true);
    } catch (error) {
      console.error("Error submitting contact:", error);
      setError(lang === 'de' ? 'Fehler beim Senden. Bitte versuchen Sie es erneut.' : 'Error sending. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Branche options
  const branchenOptions = lang === 'de' 
    ? ['Energie & Versorgung', 'Finanzdienstleistungen', 'Gesundheitswesen', 'Produktion & Industrie', 'Öffentlicher Sektor', 'IT & Technologie', 'Transport & Logistik', 'Andere']
    : ['Energy & Utilities', 'Financial Services', 'Healthcare', 'Manufacturing & Industry', 'Public Sector', 'IT & Technology', 'Transport & Logistics', 'Other'];

  // Unternehmensgroesse options
  const groesseOptions = lang === 'de'
    ? ['1-50 Mitarbeiter', '51-250 Mitarbeiter', '251-1000 Mitarbeiter', '1000+ Mitarbeiter']
    : ['1-50 employees', '51-250 employees', '251-1000 employees', '1000+ employees'];

  // Context-specific options
  const interessenOptions = lang === 'de'
    ? ['Incident Response', 'Threat Intelligence', 'Penetration Testing', 'NIS2/DORA Compliance', 'Security Assessment', 'BCM/Krisenmanagement']
    : ['Incident Response', 'Threat Intelligence', 'Penetration Testing', 'NIS2/DORA Compliance', 'Security Assessment', 'BCM/Crisis Management'];

  const regulatorikOptions = ['NIS2', 'DORA', 'ISO 27001', 'BSI IT-Grundschutz', 'KRITIS'];

  const pentestScopeOptions = lang === 'de'
    ? ['Web-Applikationen', 'Interne Netzwerke', 'Externe Infrastruktur', 'Mobile Apps', 'Social Engineering', 'OT/ICS-Systeme']
    : ['Web Applications', 'Internal Networks', 'External Infrastructure', 'Mobile Apps', 'Social Engineering', 'OT/ICS Systems'];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-ink/60 backdrop-blur-sm" data-testid="contact-modal" onClick={onClose}>
      <div className="bg-white border border-clavis-border shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto rounded-lg" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 sm:p-8">
          <div className="flex justify-between items-start mb-2">
            <h2 className="font-sans text-xl md:text-2xl text-ink font-extrabold tracking-tightest">{offerConfig.title}</h2>
            <button onClick={onClose} className="text-clavis-textGray hover:text-clavis-text transition-colors" data-testid="contact-modal-close">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <p className="text-clavis-textGray mb-6">{offerConfig.subtitle}</p>

          {submitted ? (
            <div className="text-center py-8">
              <div className="text-clavis-gold text-5xl mb-4">✓</div>
              <p className="text-clavis-text">{t.contact.success}</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Honeypot - hidden from users, visible to bots */}
              <div style={{ display: 'none' }} aria-hidden="true">
                <label htmlFor="website_hp">Website</label>
                <input
                  type="text"
                  id="website_hp"
                  name="website"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>
              
              {/* SECTION 1: Kontaktdaten */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-mono text-[11px] tracking-classification uppercase text-brass-deep">01</span>
                  <span className="text-clavis-gold font-semibold text-sm uppercase tracking-wide">
                    {lang === 'de' ? 'Ihre Kontaktdaten' : 'Your Contact Details'}
                  </span>
                </div>
                
                {/* Name + E-Mail */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-clavis-textGray text-sm mb-1">{t.contact.name} *</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full bg-white border border-clavis-border px-4 py-2 text-clavis-text focus:border-clavis-gold focus:outline-none rounded"
                      data-testid="contact-name"
                    />
                  </div>
                  <div>
                    <label className="block text-clavis-textGray text-sm mb-1">{t.contact.email} *</label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full bg-white border border-clavis-border px-4 py-2 text-clavis-text focus:border-clavis-gold focus:outline-none rounded"
                      data-testid="contact-email"
                    />
                  </div>
                </div>
                
                {/* Telefon + Unternehmen */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-clavis-textGray text-sm mb-1">
                      {lang === 'de' ? 'Telefon (für Rückruf)' : 'Phone (for callback)'}
                    </label>
                    <input
                      type="tel"
                      value={formData.telefon}
                      onChange={(e) => setFormData({ ...formData, telefon: e.target.value })}
                      className="w-full bg-white border border-clavis-border px-4 py-2 text-clavis-text focus:border-clavis-gold focus:outline-none"
                      data-testid="contact-telefon"
                    />
                  </div>
                  <div>
                    <label className="block text-clavis-textGray text-sm mb-1">{t.contact.unternehmen} *</label>
                    <input
                      type="text"
                      required
                      value={formData.unternehmen}
                      onChange={(e) => setFormData({ ...formData, unternehmen: e.target.value })}
                      className="w-full bg-white border border-clavis-border px-4 py-2 text-clavis-text focus:border-clavis-gold focus:outline-none"
                      data-testid="contact-unternehmen"
                    />
                  </div>
                </div>
                
                {/* Branche + Größe */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-clavis-textGray text-sm mb-1">
                      {lang === 'de' ? 'Branche' : 'Industry'} *
                    </label>
                    <select
                      required
                      value={formData.branche}
                      onChange={(e) => setFormData({ ...formData, branche: e.target.value })}
                      className="w-full bg-clavis-black border border-clavis-gray/30 px-4 py-2 text-clavis-text focus:border-clavis-gold focus:outline-none"
                      data-testid="contact-branche"
                    >
                      <option value="">{lang === 'de' ? 'Bitte wählen...' : 'Please select...'}</option>
                      {branchenOptions.map((opt, i) => (
                        <option key={i} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-clavis-textGray text-sm mb-1">
                      {lang === 'de' ? 'Unternehmensgröße' : 'Company Size'}
                    </label>
                    <select
                      value={formData.unternehmensgroesse}
                      onChange={(e) => setFormData({ ...formData, unternehmensgroesse: e.target.value })}
                      className="w-full bg-clavis-black border border-clavis-gray/30 px-4 py-2 text-clavis-text focus:border-clavis-gold focus:outline-none"
                      data-testid="contact-groesse"
                    >
                      <option value="">{lang === 'de' ? 'Bitte wählen...' : 'Please select...'}</option>
                      {groesseOptions.map((opt, i) => (
                        <option key={i} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              
              {/* SECTION 2: Kontext-spezifisch */}
              <div className="border-t border-clavis-gray/20 pt-5 space-y-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="bg-clavis-gold/20 text-clavis-gold text-xs px-2 py-0.5 font-bold">2</span>
                  <span className="text-clavis-gold font-semibold text-sm uppercase tracking-wide">
                    {lang === 'de' ? 'Ihr Anliegen' : 'Your Request'}
                  </span>
                </div>
                
                {/* General / Scoping Call: Interessengebiete */}
                {(requestType === 'general' || requestType === 'scoping_call') && (
                  <div>
                    <label className="block text-clavis-textGray text-sm mb-2">
                      {lang === 'de' ? 'Welche Themen interessieren Sie?' : 'Which topics interest you?'}
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {interessenOptions.map((option, i) => (
                        <label key={i} className="flex items-center gap-2 cursor-pointer p-2 border border-hairline hover:border-brass transition-colors">
                          <input
                            type="checkbox"
                            checked={formData.interessengebiete.includes(option)}
                            onChange={() => handleCheckboxChange('interessengebiete', option)}
                            className="accent-clavis-gold"
                          />
                          <span className="text-clavis-text text-sm">{option}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Readiness Check: Regulatorik */}
                {requestType === 'readiness_check' && (
                  <div>
                    <label className="block text-clavis-textGray text-sm mb-2">
                      {lang === 'de' ? 'Welche Regulatorik ist relevant?' : 'Which regulations are relevant?'}
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {regulatorikOptions.map((option, i) => (
                        <label key={i} className="flex items-center gap-2 cursor-pointer p-2 border border-hairline hover:border-brass transition-colors">
                          <input
                            type="checkbox"
                            checked={formData.regulatorik.includes(option)}
                            onChange={() => handleCheckboxChange('regulatorik', option)}
                            className="accent-clavis-gold"
                          />
                          <span className="text-clavis-text text-sm">{option}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Pentest: Scope */}
                {requestType === 'pentest' && (
                  <div>
                    <label className="block text-clavis-textGray text-sm mb-2">
                      {lang === 'de' ? 'Gewünschter Pentest-Scope' : 'Desired Pentest Scope'}
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {pentestScopeOptions.map((option, i) => (
                        <label key={i} className="flex items-center gap-2 cursor-pointer p-2 border border-hairline hover:border-brass transition-colors">
                          <input
                            type="checkbox"
                            checked={formData.pentest_scope.includes(option)}
                            onChange={() => handleCheckboxChange('pentest_scope', option)}
                            className="accent-clavis-gold"
                          />
                          <span className="text-clavis-text text-sm">{option}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Nachricht */}
                <div>
                  <label className="block text-clavis-textGray text-sm mb-1">
                    {lang === 'de' ? 'Weitere Details / Nachricht' : 'Additional Details / Message'}
                  </label>
                  <textarea
                    rows={3}
                    value={formData.nachricht}
                    onChange={(e) => setFormData({ ...formData, nachricht: e.target.value })}
                    className="w-full bg-white border border-clavis-border px-4 py-2 text-clavis-text focus:border-clavis-gold focus:outline-none resize-none"
                    placeholder={lang === 'de' ? 'Kontext, Zeitrahmen, spezielle Anforderungen...' : 'Context, timeline, special requirements...'}
                    data-testid="contact-nachricht"
                  />
                </div>
              </div>
              
              {/* SECTION 3: Terminwunsch */}
              <div className="border-t border-clavis-gray/20 pt-5 space-y-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="bg-clavis-gold/20 text-clavis-gold text-xs px-2 py-0.5 font-bold">3</span>
                  <span className="text-clavis-gold font-semibold text-sm uppercase tracking-wide">
                    {lang === 'de' ? 'Terminwunsch' : 'Scheduling Preference'}
                  </span>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-clavis-textGray text-sm mb-1">
                      {lang === 'de' ? 'Bevorzugte Kontaktzeit' : 'Preferred Contact Time'}
                    </label>
                    <select
                      value={formData.kontaktzeit}
                      onChange={(e) => setFormData({ ...formData, kontaktzeit: e.target.value })}
                      className="w-full bg-clavis-black border border-clavis-gray/30 px-4 py-2 text-clavis-text focus:border-clavis-gold focus:outline-none"
                      data-testid="contact-kontaktzeit"
                    >
                      <option value="">{lang === 'de' ? 'Keine Präferenz' : 'No preference'}</option>
                      <option value="vormittag">{lang === 'de' ? 'Vormittag (9-12 Uhr)' : 'Morning (9 AM - 12 PM)'}</option>
                      <option value="nachmittag">{lang === 'de' ? 'Nachmittag (13-17 Uhr)' : 'Afternoon (1 PM - 5 PM)'}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-clavis-textGray text-sm mb-1">
                      {lang === 'de' ? 'Zeitrahmen' : 'Timeline'}
                    </label>
                    <select
                      value={formData.zeitrahmen}
                      onChange={(e) => setFormData({ ...formData, zeitrahmen: e.target.value })}
                      className="w-full bg-clavis-black border border-clavis-gray/30 px-4 py-2 text-clavis-text focus:border-clavis-gold focus:outline-none"
                      data-testid="contact-zeitrahmen"
                    >
                      <option value="">{lang === 'de' ? 'Flexibel' : 'Flexible'}</option>
                      <option value="diese_woche">{lang === 'de' ? 'Diese Woche' : 'This week'}</option>
                      <option value="naechste_2_wochen">{lang === 'de' ? 'Nächste 2 Wochen' : 'Next 2 weeks'}</option>
                      <option value="naechster_monat">{lang === 'de' ? 'Nächster Monat' : 'Next month'}</option>
                    </select>
                  </div>
                </div>
              </div>
              
              {error && (
                <p className="text-red-500 text-sm">{error}</p>
              )}
              
              <p className="text-mid-text text-sm">{offerConfig.copy}</p>
              <button
                type="submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center bg-ink text-paper py-[14px] font-semibold uppercase tracking-label text-[13px] hover:bg-graphite transition-colors disabled:opacity-50"
                data-testid="contact-submit"
              >
                {loading ? "..." : offerConfig.buttonText}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

// Footer — editorial colophon. Hairline rules, mono labels, no signal-noise.
const Footer = () => {
  const { lang, t } = useLanguage();

  const colHeader = "font-mono text-[11px] tracking-classification uppercase text-mid-text mb-5";
  const linkSubdued = "text-graphite hover:text-brass-deep transition-colors text-sm";

  return (
    <footer className="bg-paper border-t border-hairline pt-section pb-20 mb-14" data-testid="footer">
      <div className="max-w-6xl mx-auto px-6">

        <div className="grid grid-cols-1 md:grid-cols-12 gap-x-8 gap-y-12 pb-16 border-b border-hairline">

          {/* Brand + Notfall */}
          <div className="md:col-span-5">
            <Link to={lang === "de" ? "/" : "/en"} className="block mb-5" aria-label="clavis/cyber – Startseite">
              <Wordmark className="text-[26px]" />
            </Link>
            <p className="text-mid-text text-sm leading-relaxed max-w-xs mb-8">
              {lang === 'de'
                ? 'Boutique-Beratung für Cybersecurity. Senior-only, Intelligence-first, Berlin.'
                : 'Boutique cybersecurity practice. Senior-only, intelligence-first, Berlin.'}
            </p>
            <div className="flex flex-col gap-2 text-sm">
              <div className="flex items-baseline gap-3">
                <span className="font-mono text-[11px] tracking-classification uppercase text-signal-red-deep w-16 shrink-0">24/7</span>
                <a href="tel:+493075439203" className="text-ink underline decoration-brass decoration-1 underline-offset-[3px] hover:text-brass-deep transition-colors font-semibold tracking-tight">
                  {lang === 'de' ? '030 75439203' : '+49 30 75439203'}
                </a>
              </div>
              <div className="flex items-baseline gap-3">
                <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text w-16 shrink-0">Email</span>
                <a href="mailto:incident@clavis-cyber.de" className="text-graphite hover:text-brass-deep transition-colors">
                  incident@clavis-cyber.de
                </a>
              </div>
            </div>
          </div>

          {/* Standort */}
          <div className="md:col-span-3">
            <h4 className={colHeader}>{lang === 'de' ? 'Standort' : 'Location'}</h4>
            <address className="text-graphite text-sm not-italic leading-relaxed">
              CLAVIS CYBER GmbH<br />
              Kurfürstendamm 194<br />
              10707 Berlin<br />
              {lang === 'de' ? 'Deutschland' : 'Germany'}
            </address>
          </div>

          {/* Leistungen */}
          <div className="md:col-span-2">
            <h4 className={colHeader}>{lang === 'de' ? 'Leistungen' : 'Services'}</h4>
            <ul className="flex flex-col gap-y-3">
              <li><Link to={lang === 'de' ? '/szenario/incident' : '/en/szenario/incident'} className={linkSubdued}>Incident Response</Link></li>
              <li><Link to={lang === 'de' ? '/szenario/nis2' : '/en/szenario/nis2'} className={linkSubdued}>NIS2/DORA</Link></li>
              <li><Link to={lang === 'de' ? '/szenario/threat-intelligence' : '/en/szenario/threat-intelligence'} className={linkSubdued}>Threat Intelligence</Link></li>
              <li><Link to={lang === 'de' ? '/szenario/pentest' : '/en/szenario/pentest'} className={linkSubdued}>Penetration Testing</Link></li>
            </ul>
          </div>

          {/* Briefing */}
          <div className="md:col-span-2">
            <h4 className={colHeader}>{lang === 'de' ? 'Briefing' : 'Briefing'}</h4>
            <p className="text-mid-text text-sm leading-relaxed mb-3">
              {lang === 'de'
                ? 'Monatliches Threat-Lagebild für Entscheider.'
                : 'Monthly threat-landscape briefing for decision-makers.'}
            </p>
            <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text">
              {lang === 'de' ? 'Bald verfügbar' : 'Coming soon'}
            </span>
          </div>
        </div>

        {/* Colophon row */}
        <div className="pt-8 flex flex-col md:flex-row md:items-center md:justify-between gap-y-5 gap-x-8">
          <p className="font-mono text-[11px] tracking-classification uppercase text-mid-text">
            © {new Date().getFullYear()} CLAVIS CYBER GmbH · Berlin
          </p>

          <div className="flex items-center gap-x-8">
            <Link to={lang === "de" ? "/impressum" : "/en/impressum"} className={linkSubdued} data-testid="footer-impressum">
              {t.footer.impressum}
            </Link>
            <Link to={lang === "de" ? "/datenschutz" : "/en/datenschutz"} className={linkSubdued} data-testid="footer-datenschutz">
              {t.footer.datenschutz}
            </Link>
            <a href="https://linkedin.com/company/clavis-security" target="_blank" rel="noopener noreferrer" className="text-graphite hover:text-brass-deep transition-colors" aria-label="LinkedIn">
              <svg className="w-[18px] h-[18px]" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>
            </a>
            <a href="https://twitter.com/claviscyber" target="_blank" rel="noopener noreferrer" className="text-graphite hover:text-brass-deep transition-colors" aria-label="X/Twitter">
              <svg className="w-[16px] h-[16px]" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Animated Network Background Component
const NetworkBackground = () => {
  const canvasRef = useRef(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let particles = [];
    
    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    
    const createParticles = () => {
      particles = [];
      const particleCount = Math.floor((canvas.width * canvas.height) / 15000);
      for (let i = 0; i < particleCount; i++) {
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          vx: (Math.random() - 0.5) * 0.5,
          vy: (Math.random() - 0.5) * 0.5,
          radius: Math.random() * 2 + 1
        });
      }
    };
    
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Draw particles
      particles.forEach((p, i) => {
        p.x += p.vx;
        p.y += p.vy;
        
        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;
        
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(201, 162, 39, 0.4)';
        ctx.fill();
        
        // Draw connections
        particles.slice(i + 1).forEach(p2 => {
          const dx = p.x - p2.x;
          const dy = p.y - p2.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          
          if (dist < 120) {
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.strokeStyle = `rgba(201, 162, 39, ${0.15 * (1 - dist / 120)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        });
      });
      
      animationFrameId = requestAnimationFrame(draw);
    };
    
    resize();
    createParticles();
    draw();
    
    window.addEventListener('resize', () => {
      resize();
      createParticles();
    });
    
    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', resize);
    };
  }, []);
  
  return (
    <canvas 
      ref={canvasRef} 
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{ opacity: 0.6 }}
    />
  );
};

// Hero Section — editorial, Tacitus-Brevitas, no decorative animation.
// Layout: classification stripe / kicker / display headline / lead / mechanism / one primary CTA + tertiary text link / hairline + proof row.
const HeroSection = ({ onContactClick }) => {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();

  const d = new Date();
  const quarterLabel = `${d.getFullYear()} Q${Math.floor(d.getMonth() / 3) + 1}`;

  return (
    <section
      className="relative bg-paper border-b border-hairline pt-28 pb-20 md:pt-32 md:pb-24 px-6"
      data-testid="hero-section"
    >
      <div className="max-w-6xl mx-auto">

        {/* Classification stripe */}
        <div className="border-t border-hairline pt-6 mb-12 flex items-baseline justify-between gap-6">
          <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text">
            Lagebild · Berlin · {quarterLabel}
          </span>
          <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text hidden md:inline">
            clavis / cyber · N° 01
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-y-12 md:gap-x-10">
          <div className="md:col-span-9 lg:col-span-8">
            <p className="font-mono text-[11px] tracking-classification uppercase text-brass-deep mb-6">
              {t.hero.tagline}
            </p>

            <h1
              className="font-sans text-ink font-extrabold leading-[1] tracking-tightest mb-6"
              style={{ fontSize: 'clamp(2.5rem, 6vw, 4.5rem)' }}
            >
              {t.hero.headline}
            </h1>

            <p
              className="font-sans text-graphite font-medium leading-tight tracking-tighter mb-8 max-w-prose-wide"
              style={{ fontSize: 'clamp(1.5rem, 3.2vw, 2.125rem)' }}
            >
              {t.hero.headlineSub}
            </p>

            <p className="text-mid-text text-base md:text-lg max-w-prose mb-10 leading-relaxed">
              {t.hero.mechanism}
            </p>

            <div className="flex flex-col sm:flex-row sm:items-center gap-5">
              <button
                onClick={() => onContactClick('general')}
                className="inline-flex items-center justify-center bg-ink text-paper font-semibold uppercase tracking-label text-[13px] px-8 py-[14px] hover:bg-graphite focus-visible:bg-graphite transition-colors"
                data-testid="hero-cta-primary"
              >
                {t.hero.ctaPrimary}
              </button>
              <button
                onClick={() => navigate(lang === 'de' ? '/readiness' : '/en/readiness')}
                className="text-mid-text text-[13px] uppercase tracking-label font-semibold underline decoration-hairline decoration-1 underline-offset-[6px] hover:text-ink hover:decoration-brass transition-colors"
                data-testid="hero-cta-secondary"
              >
                {t.hero.ctaSecondary}
              </button>
            </div>
          </div>
        </div>

        <div className="mt-16 md:mt-20 border-t border-hairline pt-6">
          <div className="flex flex-col md:flex-row md:items-baseline md:justify-between gap-4">
            <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text">
              {t.hero.proofHint}
            </span>
            <ul className="flex flex-wrap items-baseline gap-x-6 gap-y-3">
              {t.hero.proofItems.map((item, i) => (
                <li key={i} className="text-sm">
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-ink hover:text-brass-deep transition-colors"
                    data-testid={`hero-asset-${i}`}
                  >
                    <span className="underline decoration-brass decoration-1 underline-offset-[4px]">
                      {item.label}
                    </span>
                    <span aria-hidden="true" className="ml-1.5 text-brass">↗</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

// Leadership — names the founder, anchors credibility on the home page,
// and explicitly states that Clavis operates with a senior practitioner network.
const LeadershipSection = () => {
  const { t, lang } = useLanguage();
  const teamHref = lang === 'de' ? '/team' : '/en/team';

  return (
    <section className="py-section px-6 bg-paper border-t border-hairline" data-testid="leadership-section">
      <div className="max-w-6xl mx-auto">

        <div className="border-t border-hairline pt-6 mb-12">
          <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text">
            {t.leadership.kicker}
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-y-10 lg:gap-x-10 border-t border-hairline pt-10 items-baseline">

          <div className="lg:col-span-5">
            <span className="font-mono text-[11px] tracking-classification uppercase text-brass-deep block mb-3">
              {t.leadership.role}
            </span>
            <h2 className="font-sans text-ink font-extrabold tracking-tightest leading-[1.05]"
                style={{ fontSize: 'clamp(1.75rem, 3.4vw, 2.5rem)' }}>
              {t.leadership.name}
            </h2>
          </div>

          <div className="lg:col-span-7 lg:border-l lg:border-hairline lg:pl-10">
            <p className="text-graphite text-[15.5px] leading-[1.75] max-w-prose-wide mb-8">
              {t.leadership.summary}
            </p>
            <Link
              to={teamHref}
              className="font-mono text-[12px] tracking-classification uppercase text-ink underline decoration-brass decoration-1 underline-offset-[5px] hover:text-brass-deep transition-colors inline-flex items-center gap-2"
              data-testid="leadership-cta"
            >
              <span>{t.leadership.cta}</span>
              <span aria-hidden="true" className="text-brass">↗</span>
            </Link>
          </div>

        </div>
      </div>
    </section>
  );
};

// Clarity — answers the three questions visitors ask in the first 8 seconds:
// what we do, when they call us, what sets us apart. Editorial 3-column index, no claims, no buzzwords.
const ClaritySection = () => {
  const { t } = useLanguage();

  return (
    <section className="py-section px-6 bg-paper border-t border-hairline" data-testid="clarity-section">
      <div className="max-w-6xl mx-auto">

        <div className="border-t border-hairline pt-6 mb-12">
          <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text">
            {t.clarity.kicker}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-y-12 md:gap-x-10 border-t border-hairline pt-10">
          {t.clarity.columns.map((col, i) => (
            <ScrollReveal key={i} delay={i * 0.05}>
              <div className="md:border-l md:border-hairline md:pl-6 first:md:border-l-0 first:md:pl-0">
                <span className="font-mono text-[11px] tracking-classification uppercase text-brass-deep block mb-6">
                  {col.label}
                </span>
                <ul className="space-y-4">
                  {col.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-3">
                      <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text w-5 shrink-0 pt-[5px]">
                        {String(j + 1).padStart(2, '0')}
                      </span>
                      <span className="text-ink text-[15px] leading-relaxed">
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
};

// Leistungen / Pillars — editorial register with hairline rules.
// No big icons, no card lift, no tilt. Numbered index + heavy Switzer title + outcome lead + deliverable body + service tags.
const PillarsSection = () => {
  const { t } = useLanguage();

  return (
    <section id="leistungen" className="py-section px-6 scroll-mt-24 bg-paper" data-testid="pillars-section">
      <div className="max-w-6xl mx-auto">

        <div className="border-t border-hairline pt-6 mb-12 flex items-baseline justify-between gap-6">
          <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text">
            Leistungen · 01–04
          </span>
          <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text hidden md:inline">
            Detect / Protect / Test / Respond
          </span>
        </div>

        <ScrollReveal>
          <h2 className="font-sans text-ink font-extrabold leading-[1.1] tracking-tightest mb-16 max-w-prose-wide"
              style={{ fontSize: 'clamp(1.875rem, 3.5vw, 2.5rem)' }}>
            {t.pillars.title}
          </h2>
        </ScrollReveal>

        <div className="border-t border-hairline">
          {t.pillars.items.map((pillar, i) => (
            <ScrollReveal key={i} delay={i * 0.05}>
              <article
                className="grid grid-cols-1 md:grid-cols-12 gap-x-8 gap-y-4 py-12 border-b border-hairline group"
                data-testid={`pillar-${i}`}
              >
                <div className="md:col-span-2">
                  <span className="font-mono text-[11px] tracking-classification uppercase text-brass-deep">
                    {pillar.nummer}
                  </span>
                </div>
                <div className="md:col-span-6">
                  <h3 className="font-sans text-ink font-bold leading-tight tracking-tighter mb-4"
                      style={{ fontSize: 'clamp(1.5rem, 2.5vw, 2rem)' }}>
                    {pillar.titel}
                  </h3>
                  <p className="text-ink text-base md:text-lg leading-relaxed mb-3 max-w-prose">
                    {pillar.outcome}
                  </p>
                  <p className="text-mid-text text-sm leading-relaxed max-w-prose">
                    {pillar.deliverable}
                  </p>
                </div>
                <div className="md:col-span-4">
                  <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text mb-3 block">
                    Services
                  </span>
                  <ul className="flex flex-col gap-y-2">
                    {pillar.services.map((service, j) => (
                      <li key={j} className="text-sm text-graphite border-l border-hairline pl-3 group-hover:border-brass transition-colors">
                        {service}
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
};

// Trusted By Section with TiltCards - LIGHT VERSION
const TrustedBySection = () => {
  const { lang } = useLanguage();
  
  const references = lang === 'de' ? [
    { text: "DAX-40 Energieversorger", subtext: "Incident Response" },
    { text: "Bundesbehörde", subtext: "Threat Intelligence" },
    { text: "Top-10 Versicherung", subtext: "NIS2 Readiness" },
    { text: "Mittelstand Automotive", subtext: "Red Teaming" },
    { text: "Klinikverbund (KRITIS)", subtext: "Ransomware Response" },
  ] : [
    { text: "DAX-40 Energy Provider", subtext: "Incident Response" },
    { text: "Federal Authority", subtext: "Threat Intelligence" },
    { text: "Top-10 Insurance", subtext: "NIS2 Readiness" },
    { text: "Mid-Size Automotive", subtext: "Red Teaming" },
    { text: "Hospital Group (Critical)", subtext: "Ransomware Response" },
  ];
  
  return (
    <section className="py-16 px-6 bg-paper-mid" data-testid="trusted-by-section">
      <div className="max-w-6xl mx-auto">
        <ScrollReveal>
          <p className="font-mono text-[11px] tracking-classification uppercase text-mid-text mb-8">
            {lang === 'de' ? 'Ausgewählte Referenzen — anonymisiert' : 'Selected references — anonymised'}
          </p>
        </ScrollReveal>
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-x-6 gap-y-4 border-t border-hairline pt-6">
          {references.map((ref, i) => (
            <li key={i} className="border-l border-hairline pl-4">
              <p className="text-ink text-sm font-semibold leading-tight">{ref.text}</p>
              <p className="font-mono text-[11px] tracking-classification uppercase text-brass-deep mt-1">{ref.subtext}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

// Case Study Teaser Section - Enhanced with more detail
const CaseStudySection = () => {
  const { lang } = useLanguage();
  
  const caseStudy = {
    de: {
      label: "FALLBEISPIEL · AUTOMOBILZULIEFERER · Q4 2024",
      title: "Ransomware-Angriff: Von Stillstand zu Produktion in 72 Stunden",
      challenge: "Ein bayerischer Automobilzulieferer (500 MA, 120 Mio. € Umsatz) entdeckte am Freitagabend um 22:15 Uhr verschlüsselte Systeme. LockBit 3.0 hatte sich über einen kompromittierten VPN-Zugang lateral durch das Netzwerk bewegt. Die Produktion stand still, drei OEM-Kunden drohten mit Konventionalstrafen.",
      result: "Unser Team war Samstag 02:00 Uhr vor Ort. Innerhalb von 72 Stunden: Angriffspfad vollständig rekonstruiert, saubere Backups identifiziert und validiert, Active Directory bereinigt, kritische Produktionssysteme wiederhergestellt. Montag 06:00 Uhr lief die erste Schicht.",
      stats: [
        { value: "< 4h", label: "Erstkontakt bis Vor-Ort" },
        { value: "72h", label: "Bis Produktionswiederanlauf" },
        { value: "0€", label: "Lösegeld bezahlt" },
        { value: "100%", label: "Daten wiederhergestellt" }
      ]
    },
    en: {
      label: "CASE STUDY · AUTOMOTIVE SUPPLIER · Q4 2024",
      title: "Ransomware Attack: From Standstill to Production in 72 Hours",
      challenge: "A Bavarian automotive supplier (500 employees, €120M revenue) discovered encrypted systems on Friday evening at 10:15 PM. LockBit 3.0 had moved laterally through the network via a compromised VPN access. Production was at a standstill, three OEM customers threatened contractual penalties.",
      result: "Our team was on-site Saturday 2:00 AM. Within 72 hours: Attack path fully reconstructed, clean backups identified and validated, Active Directory cleaned up, critical production systems restored. Monday 6:00 AM the first shift was running.",
      stats: [
        { value: "< 4h", label: "First contact to on-site" },
        { value: "72h", label: "To production restart" },
        { value: "€0", label: "Ransom paid" },
        { value: "100%", label: "Data recovered" }
      ]
    }
  };
  
  const content = caseStudy[lang];
  
  return (
    <section className="py-section px-6 bg-paper" data-testid="case-study-section">
      <div className="max-w-6xl mx-auto">

        <div className="border-t border-hairline pt-6 mb-12">
          <span className="font-mono text-[11px] tracking-classification uppercase text-brass-deep">
            {content.label}
          </span>
        </div>

        <ScrollReveal>
          <h2 className="font-sans text-ink font-extrabold leading-[1.05] tracking-tightest mb-12 max-w-prose-wide"
              style={{ fontSize: 'clamp(1.875rem, 3.6vw, 2.75rem)' }}>
            {content.title}
          </h2>
        </ScrollReveal>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-x-8 gap-y-10 mb-16">
          <ScrollReveal className="md:col-span-6">
            <div className="border-l border-signal-red pl-6">
              <p className="font-mono text-[11px] tracking-classification uppercase text-mid-text mb-3">
                {lang === 'de' ? 'Situation' : 'Challenge'}
              </p>
              <p className="text-ink text-base leading-relaxed">{content.challenge}</p>
            </div>
          </ScrollReveal>
          <ScrollReveal className="md:col-span-6" delay={0.1}>
            <div className="border-l border-brass pl-6">
              <p className="font-mono text-[11px] tracking-classification uppercase text-mid-text mb-3">
                {lang === 'de' ? 'Ergebnis' : 'Result'}
              </p>
              <p className="text-ink text-base leading-relaxed">{content.result}</p>
            </div>
          </ScrollReveal>
        </div>

        <ScrollReveal delay={0.2}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-8 pt-10 border-t border-hairline">
            {content.stats.map((stat, i) => (
              <div key={i}>
                <p className="font-sans text-ink font-extrabold leading-none tracking-tightest"
                   style={{ fontSize: 'clamp(2rem, 4vw, 3rem)' }}>
                  {stat.value}
                </p>
                <p className="font-mono text-[11px] tracking-classification uppercase text-mid-text mt-3">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
};

// News/Insights Section — lädt täglich aktualisierte Themen aus /data/insights.json.
// Klick führt nicht zum Original, sondern auf die interne Detailseite mit Clavis-Einordnung.
const InsightsSection = () => {
  const { lang } = useLanguage();
  const [items, setItems] = useState([]);

  useEffect(() => {
    let active = true;
    fetch("/data/insights.json", { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (active && d && Array.isArray(d.items)) setItems(d.items); })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  if (!items.length) return null;

  const slugBase = lang === 'de' ? '/insights' : '/en/insights';

  return (
    <section className="py-section px-6 bg-paper-mid" data-testid="insights-section">
      <div className="max-w-6xl mx-auto">

        <div className="border-t border-hairline pt-6 mb-12 flex items-baseline justify-between gap-6">
          <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text">
            Insights
          </span>
          <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text hidden md:inline">
            {lang === 'de' ? 'Aktualisiert täglich' : 'Updated daily'}
          </span>
        </div>

        <h2 className="font-sans text-ink font-extrabold leading-snug tracking-tightest mb-12 max-w-prose-wide"
            style={{ fontSize: 'clamp(1.875rem, 3.5vw, 2.5rem)' }}>
          {lang === 'de' ? 'Aktuelle Analysen' : 'Latest analyses'}
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-8">
          {items.map((item, i) => (
            <Link
              key={item.slug || i}
              to={`${slugBase}/${item.slug}`}
              className="bg-paper border border-hairline p-7 hover:border-brass transition-colors duration-200 group cursor-pointer flex flex-col"
              data-testid={`insight-card-${i}`}
            >
              <div className="flex items-baseline gap-3 mb-5">
                <span className="font-mono text-[11px] tracking-classification uppercase text-brass-deep">{item.category}</span>
                <span className="text-ash text-xs">·</span>
                <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text">
                  {lang === 'de' ? item.date_de : item.date_en}
                </span>
              </div>
              <h3 className="font-sans text-ink font-bold text-base md:text-lg mb-3 leading-snug tracking-tighter group-hover:text-brass-deep transition-colors">
                {lang === 'de' ? item.title_de : item.title_en}
              </h3>
              <p className="text-mid-text text-sm leading-relaxed">
                {lang === 'de' ? item.excerpt_de : item.excerpt_en}
              </p>
              <p className="mt-auto pt-6 font-mono text-[11px] tracking-classification uppercase text-ink underline decoration-brass decoration-1 underline-offset-[4px] group-hover:text-brass-deep transition-colors">
                {lang === 'de' ? 'Weiterlesen ↗' : 'Read more ↗'}
              </p>
            </Link>
          ))}
        </div>
        <div className="mt-12 border-t border-hairline pt-6 flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-4">
          <p className="text-mid-text text-xs italic max-w-prose-wide">
            {lang === 'de'
              ? 'Themen folgen der täglichen öffentlichen Lage. Originalquellen verlinken wir auf der jeweiligen Detailseite.'
              : 'Topics follow the daily public threat picture. Original sources are linked on each detail page.'}
          </p>
          <Link
            to={slugBase}
            className="shrink-0 font-mono text-[11px] tracking-classification uppercase text-ink underline decoration-brass decoration-1 underline-offset-[4px] hover:text-brass-deep transition-colors"
            data-testid="insights-archive-link"
          >
            {lang === 'de' ? 'Alle Analysen →' : 'All analyses →'}
          </Link>
        </div>
      </div>
    </section>
  );
};

// Insight Detail Page — interne Seite mit Clavis-Einordnung + CTA + Quellenlink.
const InsightDetailPage = ({ onContactClick }) => {
  const { lang } = useLanguage();
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let active = true;
    const findIn = (d) => (d && Array.isArray(d.items) ? d.items.find((x) => x.slug === slug) : null);
    // Archiv zuerst (enthält alle je veröffentlichten Beiträge), danach das Live-Top-3-Set
    fetch("/data/insights_archive.json", { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!active) return null;
        const item = findIn(d);
        if (item) { setData(item); return null; }
        return fetch("/data/insights.json", { cache: "no-store" }).then((r) => (r.ok ? r.json() : null));
      })
      .then((d) => {
        if (!active || d === null) return;
        const item = findIn(d);
        if (item) setData(item);
        else setNotFound(true);
      })
      .catch(() => active && setNotFound(true));
    return () => { active = false; };
  }, [slug]);

  if (notFound) {
    return (
      <main className="pt-[88px] pb-section min-h-screen bg-paper" data-testid="insight-detail-page-notfound">
        <div className="max-w-3xl mx-auto px-6">
          <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text">
            404 · Insights
          </span>
          <h1 className="font-sans text-ink font-extrabold tracking-tightest text-3xl md:text-4xl mt-4 mb-6 leading-[1.05]">
            {lang === 'de' ? 'Beitrag nicht gefunden' : 'Article not found'}
          </h1>
          <Link to={lang === 'de' ? '/insights' : '/en/insights'} className="text-ink underline decoration-brass decoration-1 underline-offset-[4px] hover:text-brass-deep transition-colors">
            {lang === 'de' ? '← Alle Analysen' : '← All analyses'}
          </Link>
        </div>
      </main>
    );
  }
  if (!data) {
    return (
      <main className="pt-[88px] pb-section min-h-screen bg-paper" data-testid="insight-detail-page-loading">
        <div className="max-w-3xl mx-auto px-6">
          <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text">
            {lang === 'de' ? 'Lade…' : 'Loading…'}
          </span>
        </div>
      </main>
    );
  }

  const title = lang === 'de' ? data.title_de : data.title_en;
  const excerpt = lang === 'de' ? data.excerpt_de : data.excerpt_en;
  const commentary = lang === 'de' ? data.commentary_de : data.commentary_en;
  const date = lang === 'de' ? data.date_de : data.date_en;

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    description: excerpt,
    datePublished: data.iso_date,
    dateModified: data.iso_date,
    inLanguage: lang === 'de' ? 'de-DE' : 'en-US',
    articleSection: data.category,
    author: { "@type": "Organization", name: "Clavis Cyber GmbH", url: SITE_URL },
    publisher: {
      "@type": "Organization",
      name: "Clavis Cyber GmbH",
      logo: { "@type": "ImageObject", url: SITE_URL + "/favicon-192x192.png" },
    },
    mainEntityOfPage:
      SITE_URL + (lang === 'de' ? '/insights/' : '/en/insights/') + data.slug,
  };

  return (
    <main className="pt-[88px] pb-section min-h-screen bg-paper" data-testid="insight-detail-page">
      <Seo
        title={`${title} — clavis/cyber`}
        description={excerpt}
        type="article"
        jsonLd={articleJsonLd}
      />
      <article className="max-w-3xl mx-auto px-6">

        <Link
          to={lang === 'de' ? '/insights' : '/en/insights'}
          className="inline-flex font-mono text-[11px] tracking-classification uppercase text-mid-text hover:text-brass-deep transition-colors"
        >
          ← {lang === 'de' ? 'Alle Analysen' : 'All analyses'}
        </Link>

        <div className="mt-12 border-t border-hairline pt-6 flex items-baseline justify-between gap-6">
          <span className="font-mono text-[11px] tracking-classification uppercase text-brass-deep">
            {data.category}
          </span>
          <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text">
            {date}
          </span>
        </div>

        <h1 className="font-sans text-ink font-extrabold tracking-tightest leading-[1.1] mt-8 mb-8"
            style={{ fontSize: 'clamp(1.875rem, 4vw, 2.75rem)' }}>
          {title}
        </h1>

        <p className="text-graphite text-lg md:text-xl leading-relaxed mb-12 max-w-prose-wide">
          {excerpt}
        </p>

        {commentary && (
          <div className="border-t border-hairline pt-10 mb-16">
            <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text mb-6 block">
              {lang === 'de' ? 'Clavis-Einordnung' : 'Clavis perspective'}
            </span>
            <div className="text-ink text-base md:text-lg leading-relaxed space-y-5 max-w-prose-wide">
              {commentary.split(/\n+/).filter(Boolean).map((p, idx) => (
                <p key={idx}>{p}</p>
              ))}
            </div>
          </div>
        )}

        <section className="border-t border-hairline pt-10 mb-16">
          <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text mb-4 block">
            {lang === 'de' ? 'Wenn dies Sie betrifft' : 'If this concerns you'}
          </span>
          <h2 className="font-sans text-ink font-bold text-xl md:text-2xl mb-4 max-w-prose-wide tracking-tighter">
            {lang === 'de' ? 'Vergleichbares Risiko in Ihrem Stack?' : 'Similar exposure in your stack?'}
          </h2>
          <p className="text-mid-text text-base leading-relaxed mb-6 max-w-prose-wide">
            {lang === 'de'
              ? 'In 48 Stunden ein erstes Lagebild – kostenfrei, vertraulich, ohne Vertriebskette.'
              : 'A first situation report within 48 hours – free, confidential, no sales chain.'}
          </p>
          <button
            onClick={() => onContactClick && onContactClick("general")}
            className="inline-flex items-center justify-center bg-ink text-paper px-7 py-[14px] text-[12px] font-semibold uppercase tracking-label hover:bg-graphite transition-colors"
            data-testid="insight-cta"
          >
            {lang === 'de' ? 'Erstgespräch anfragen' : 'Request initial briefing'}
          </button>
        </section>

        <div className="border-t border-hairline pt-6 space-y-3">
          <p className="font-mono text-[11px] tracking-classification uppercase text-mid-text">
            {lang === 'de' ? 'Originalquelle' : 'Original source'}
          </p>
          <p className="text-sm">
            <a
              href={data.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-ink underline decoration-brass decoration-1 underline-offset-[3px] hover:text-brass-deep transition-colors"
            >
              {data.source} ↗
            </a>
          </p>
          <p className="text-mid-text text-xs italic max-w-prose-wide leading-relaxed">
            {lang === 'de'
              ? 'Diese Seite gibt eine Clavis-Einordnung zu öffentlich berichteten Vorfällen wieder. Die Originalmeldung liegt beim verlinkten Anbieter.'
              : 'This page provides a Clavis perspective on publicly reported incidents. The original story is held by the linked publisher.'}
          </p>
        </div>
      </article>
    </main>
  );
};

// Lagebrief signup — schlanke E-Mail-Anmeldung, schreibt an /api/lagebrief
const LagebriefSignup = ({ variant = "block" }) => {
  const { lang } = useLanguage();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | ok | error

  const submit = async (e) => {
    e.preventDefault();
    if (status === "sending") return;
    setStatus("sending");
    try {
      const res = await fetch("/api/lagebrief", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email, lang }),
      });
      if (res.ok) { setStatus("ok"); setEmail(""); }
      else setStatus("error");
    } catch (err) {
      setStatus("error");
    }
  };

  const wrap = variant === "block"
    ? "border border-hairline bg-paper p-8 md:p-10"
    : "";

  return (
    <div className={wrap} data-testid="lagebrief-signup">
      <span className="font-mono text-[11px] tracking-classification uppercase text-brass-deep block mb-3">
        {lang === 'de' ? 'Clavis Lagebrief' : 'Clavis Situation Brief'}
      </span>
      <h3 className="font-sans text-ink font-bold tracking-tighter leading-snug mb-3"
          style={{ fontSize: 'clamp(1.25rem, 2.2vw, 1.625rem)' }}>
        {lang === 'de'
          ? 'Die Lage – kompakt, eingeordnet, ohne Lärm.'
          : 'The threat picture – concise, assessed, without noise.'}
      </h3>
      <p className="text-mid-text text-sm leading-relaxed mb-6 max-w-prose-wide">
        {lang === 'de'
          ? 'Relevante Vorfälle aus der öffentlichen Lage mit kurzer Clavis-Einordnung. Kein Vertrieb, jederzeit abbestellbar.'
          : 'Relevant incidents from the public threat picture with a brief Clavis assessment. No sales, unsubscribe anytime.'}
      </p>

      {status === 'ok' ? (
        <p className="font-mono text-[12px] tracking-classification uppercase text-ink" data-testid="lagebrief-ok">
          {lang === 'de'
            ? '✓ Eingetragen. Wir melden uns.'
            : '✓ Registered. We will be in touch.'}
        </p>
      ) : (
        <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3 max-w-xl">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => { setEmail(e.target.value); if (status === 'error') setStatus('idle'); }}
            placeholder={lang === 'de' ? 'ihre@firma.de' : 'you@company.com'}
            className="flex-1 bg-paper border border-hairline px-4 py-[13px] text-sm text-ink placeholder:text-ash focus:outline-none focus:border-brass transition-colors"
            data-testid="lagebrief-email"
          />
          <button
            type="submit"
            disabled={status === 'sending'}
            className="shrink-0 inline-flex items-center justify-center bg-ink text-paper px-7 py-[13px] text-[12px] font-semibold uppercase tracking-label hover:bg-graphite transition-colors disabled:opacity-60"
            data-testid="lagebrief-submit"
          >
            {status === 'sending'
              ? (lang === 'de' ? 'Sende…' : 'Sending…')
              : (lang === 'de' ? 'Lagebrief abonnieren' : 'Subscribe')}
          </button>
        </form>
      )}
      {status === 'error' && (
        <p className="mt-3 font-mono text-[11px] tracking-classification uppercase text-red-700" data-testid="lagebrief-error">
          {lang === 'de'
            ? 'Etwas ging schief. Bitte E-Mail prüfen oder später erneut versuchen.'
            : 'Something went wrong. Please check the address or try again later.'}
        </p>
      )}
    </div>
  );
};

// Insights Index — vollständiges Archiv aller veröffentlichten Analysen
const InsightsIndexPage = ({ onContactClick }) => {
  const { lang } = useLanguage();
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let active = true;
    fetch("/data/insights_archive.json", { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!active) return;
        if (d && Array.isArray(d.items)) setItems(d.items);
        setLoaded(true);
      })
      .catch(() => active && setLoaded(true));
    return () => { active = false; };
  }, []);

  const slugBase = lang === 'de' ? '/insights' : '/en/insights';

  return (
    <main className="pt-[88px] pb-section min-h-screen bg-paper" data-testid="insights-index-page">
      <Seo
        title={lang === 'de'
          ? 'Insights — Analysen & Lagebild | clavis/cyber'
          : 'Insights — Analyses & threat picture | clavis/cyber'}
        description={lang === 'de'
          ? 'Relevante Vorfälle aus der öffentlichen Sicherheitslage – mit kurzer Clavis-Einordnung. Täglich aktualisiert.'
          : 'Relevant incidents from the public security landscape – with a brief Clavis assessment. Updated daily.'}
      />
      <div className="max-w-5xl mx-auto px-6">

        <div className="border-t border-hairline pt-6 mb-10 flex items-baseline justify-between gap-6">
          <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text">
            {lang === 'de' ? 'Insights · Archiv' : 'Insights · Archive'}
          </span>
          <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text hidden md:inline">
            {lang === 'de' ? 'Aktualisiert täglich' : 'Updated daily'}
          </span>
        </div>

        <h1 className="font-sans text-ink font-extrabold tracking-tightest leading-[1.05] mb-6"
            style={{ fontSize: 'clamp(2rem, 5vw, 3.25rem)' }}>
          {lang === 'de' ? 'Analysen & Lagebild' : 'Analyses & threat picture'}
        </h1>
        <p className="text-graphite text-lg leading-relaxed mb-14 max-w-prose-wide">
          {lang === 'de'
            ? 'Relevante Vorfälle aus der öffentlichen Sicherheitslage – mit kurzer Clavis-Einordnung. Originalquellen sind auf der jeweiligen Detailseite verlinkt.'
            : 'Relevant incidents from the public security landscape – with a brief Clavis assessment. Original sources are linked on each detail page.'}
        </p>

        <div className="mb-16">
          <LagebriefSignup variant="block" />
        </div>

        {!loaded ? (
          <p className="font-mono text-[11px] tracking-classification uppercase text-mid-text">
            {lang === 'de' ? 'Lade…' : 'Loading…'}
          </p>
        ) : items.length === 0 ? (
          <p className="text-mid-text">
            {lang === 'de' ? 'Noch keine Analysen verfügbar.' : 'No analyses available yet.'}
          </p>
        ) : (
          <div data-testid="insights-archive-list">
            {items.map((item, i) => (
              <Link
                key={item.slug || i}
                to={`${slugBase}/${item.slug}`}
                className="block border-t border-hairline py-8 group"
                data-testid={`archive-row-${i}`}
              >
                <div className="flex items-baseline gap-3 mb-3">
                  <span className="font-mono text-[11px] tracking-classification uppercase text-brass-deep">{item.category}</span>
                  <span className="text-ash text-xs">·</span>
                  <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text">
                    {lang === 'de' ? item.date_de : item.date_en}
                  </span>
                </div>
                <h2 className="font-sans text-ink font-bold leading-snug tracking-tighter mb-2 group-hover:text-brass-deep transition-colors max-w-prose-wide"
                    style={{ fontSize: 'clamp(1.125rem, 2vw, 1.5rem)' }}>
                  {lang === 'de' ? item.title_de : item.title_en}
                </h2>
                <p className="text-mid-text text-sm leading-relaxed max-w-prose-wide">
                  {lang === 'de' ? item.excerpt_de : item.excerpt_en}
                </p>
              </Link>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </main>
  );
};

// ============================================
// NIS2 / DORA Readiness Check — interaktives Self-Assessment
// ============================================
const READINESS_OPTIONS = [
  { value: 2, label: { de: "Ja, etabliert", en: "Yes, established" } },
  { value: 1, label: { de: "Teilweise / in Arbeit", en: "Partially / in progress" } },
  { value: 0, label: { de: "Nein / unbekannt", en: "No / unknown" } },
];

const READINESS_DOMAINS = [
  {
    id: "governance",
    label: { de: "Governance & Risikomanagement", en: "Governance & risk management" },
    questions: [
      { id: "g1", text: { de: "Existiert eine dokumentierte Informationssicherheits-Risikoanalyse, die regelmäßig aktualisiert wird?", en: "Is there a documented information-security risk analysis that is updated regularly?" } },
      { id: "g2", text: { de: "Ist die Geschäftsleitung formal für Cyber-Risiken verantwortlich und entsprechend geschult (NIS2-Leitungspflicht)?", en: "Is executive management formally accountable for cyber risk and trained accordingly (NIS2 management duty)?" } },
      { id: "g3", text: { de: "Sind Richtlinien, Rollen und Verantwortlichkeiten für Informationssicherheit verbindlich dokumentiert?", en: "Are information-security policies, roles and responsibilities formally documented?" } },
    ],
    recommendation: {
      de: "Verankern Sie Cyber-Risiko als Leitungsaufgabe: dokumentierte Risikoanalyse, benannte Verantwortliche und eine von der Geschäftsführung verabschiedete Sicherheitsleitlinie. Das ist die prüfbare Grundlage jeder NIS2-Konformität.",
      en: "Anchor cyber risk as a management responsibility: a documented risk analysis, named owners and a board-approved security policy. This is the auditable basis of any NIS2 conformity.",
    },
  },
  {
    id: "detection",
    label: { de: "Erkennung & Meldewesen", en: "Detection & reporting" },
    questions: [
      { id: "d1", text: { de: "Können Sie sicherheitsrelevante Ereignisse zentral erkennen und protokollieren (Logging/Monitoring)?", en: "Can you centrally detect and log security-relevant events (logging/monitoring)?" } },
      { id: "d2", text: { de: "Haben Sie einen geübten Meldeprozess, der die NIS2-Fristen einhält (Frühwarnung 24 h, Meldung 72 h)?", en: "Do you have a rehearsed reporting process that meets the NIS2 deadlines (early warning 24 h, notification 72 h)?" } },
      { id: "d3", text: { de: "Existiert ein dokumentierter Incident-Response-Plan mit klaren Eskalationswegen?", en: "Is there a documented incident-response plan with clear escalation paths?" } },
    ],
    recommendation: {
      de: "Schließen Sie die Erkennungs- und Meldelücke: zentrales Logging, definierte Schwellwerte und ein geübter Meldeweg für die 24-/72-Stunden-Fristen. Im Ernstfall entscheidet die Vorbereitung über die Meldefähigkeit.",
      en: "Close the detection and reporting gap: central logging, defined thresholds and a rehearsed reporting path for the 24/72-hour deadlines. In a real incident, preparation determines your ability to report.",
    },
  },
  {
    id: "continuity",
    label: { de: "Betriebskontinuität & Wiederanlauf", en: "Continuity & recovery" },
    questions: [
      { id: "c1", text: { de: "Werden Backups regelmäßig erstellt, offline/unveränderlich gehalten UND Wiederherstellungen getestet?", en: "Are backups created regularly, kept offline/immutable AND are restores tested?" } },
      { id: "c2", text: { de: "Gibt es einen geübten Notfall-/Business-Continuity-Plan inklusive Krisenkommunikation?", en: "Is there a rehearsed emergency / business-continuity plan including crisis communication?" } },
    ],
    recommendation: {
      de: "Machen Sie Wiederanlauf belastbar: unveränderliche Backups, regelmäßig getestete Wiederherstellung und ein geübter Notfallplan mit Krisenkommunikation. Ein ungetestetes Backup ist kein Backup.",
      en: "Make recovery resilient: immutable backups, regularly tested restores and a rehearsed emergency plan with crisis communication. An untested backup is not a backup.",
    },
  },
  {
    id: "supplychain",
    label: { de: "Lieferkette & Drittparteien", en: "Supply chain & third parties" },
    questions: [
      { id: "s1", text: { de: "Bewerten und überwachen Sie die Sicherheit kritischer Lieferanten und IT-Dienstleister (DORA-Drittparteienrisiko)?", en: "Do you assess and monitor the security of critical suppliers and ICT providers (DORA third-party risk)?" } },
      { id: "s2", text: { de: "Enthalten Verträge mit IT-Dienstleistern verbindliche Sicherheits- und Meldepflichten?", en: "Do contracts with ICT providers include binding security and notification obligations?" } },
    ],
    recommendation: {
      de: "Bringen Sie Drittparteienrisiko unter Kontrolle: ein Verzeichnis kritischer Dienstleister, Sicherheitsanforderungen im Vertrag und laufende Überwachung. Unter DORA ist die Auslagerung kein Risikotransfer.",
      en: "Get third-party risk under control: a register of critical providers, contractual security requirements and ongoing monitoring. Under DORA, outsourcing is not a transfer of risk.",
    },
  },
  {
    id: "technical",
    label: { de: "Technische Maßnahmen & Awareness", en: "Technical measures & awareness" },
    questions: [
      { id: "t1", text: { de: "Ist Multi-Faktor-Authentifizierung für alle kritischen Zugänge und jeden Remote-Zugriff flächendeckend aktiv?", en: "Is multi-factor authentication enforced across all critical access and every remote connection?" } },
      { id: "t2", text: { de: "Werden Schwachstellen systematisch gemanagt und regelmäßig Penetrationstests durchgeführt?", en: "Are vulnerabilities managed systematically and penetration tests carried out regularly?" } },
      { id: "t3", text: { de: "Erhalten Mitarbeitende regelmäßige, nachweisbare Security-Awareness-Schulungen?", en: "Do employees receive regular, documented security-awareness training?" } },
    ],
    recommendation: {
      de: "Härten Sie die Basis: flächendeckende MFA, systematisches Schwachstellenmanagement mit regelmäßigen Pentests und nachweisbare Awareness-Schulungen. Diese Maßnahmen verhindern die Mehrheit realer Angriffe.",
      en: "Harden the basics: ubiquitous MFA, systematic vulnerability management with regular pentests and documented awareness training. These measures stop the majority of real-world attacks.",
    },
  },
];

const READINESS_BANDS = [
  {
    min: 70, tone: "#1f7a3d",
    label: { de: "Belastbar – Feinschliff", en: "Resilient – refinement" },
    text: {
      de: "Ihre Aufstellung ist im Kern belastbar. Jetzt geht es um Feinschliff, Nachweisführung und kontinuierliche Übung – damit die Reife auch im Ernstfall und im Audit trägt.",
      en: "Your posture is fundamentally resilient. The focus now is refinement, evidence and continuous exercising – so the maturity holds in a real incident and in an audit.",
    },
  },
  {
    min: 40, tone: "#9a6a00",
    label: { de: "Grundlagen vorhanden, Lücken offen", en: "Foundations in place, gaps remain" },
    text: {
      de: "Sie haben tragfähige Grundlagen, aber mehrere prüfungsrelevante Lücken. Priorisierte Nacharbeit an den schwächsten Feldern bringt Sie in kurzer Zeit auf ein belastbares Niveau.",
      en: "You have a workable baseline but several audit-relevant gaps. Prioritised work on the weakest areas moves you to a resilient level in a short time.",
    },
  },
  {
    min: 0, tone: "#a62828",
    label: { de: "Kritischer Handlungsbedarf", en: "Critical action required" },
    text: {
      de: "Wesentliche NIS2/DORA-Grundpflichten sind derzeit nicht belastbar abgedeckt. Ein Sicherheitsvorfall oder ein Audit würde Sie aktuell unvorbereitet treffen – hier sollte zeitnah gehandelt werden.",
      en: "Core NIS2/DORA obligations are currently not reliably covered. A security incident or audit would catch you unprepared right now – this warrants prompt action.",
    },
  },
];

const _readinessBand = (pct) => READINESS_BANDS.find((b) => pct >= b.min) || READINESS_BANDS[READINESS_BANDS.length - 1];

const ReadinessPage = ({ onContactClick }) => {
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const [step, setStep] = useState(0); // 0 = Intro, 1..N = Domains, N+1 = Ergebnis
  const [answers, setAnswers] = useState({});
  const [contact, setContact] = useState({ name: "", email: "", unternehmen: "" });
  const [submit, setSubmit] = useState("idle"); // idle | sending | ok | error

  const N = READINESS_DOMAINS.length;
  const isIntro = step === 0;
  const isResult = step === N + 1;
  const domain = !isIntro && !isResult ? READINESS_DOMAINS[step - 1] : null;

  const domainPct = (d) => {
    const max = d.questions.length * 2;
    const sum = d.questions.reduce((acc, q) => acc + (answers[q.id] ?? 0), 0);
    return Math.round((sum / max) * 100);
  };
  const overall = Math.round(
    READINESS_DOMAINS.reduce((acc, d) => acc + domainPct(d), 0) / N
  );
  const band = _readinessBand(overall);
  const gaps = [...READINESS_DOMAINS]
    .map((d) => ({ d, pct: domainPct(d) }))
    .filter((x) => x.pct < 70)
    .sort((a, b) => a.pct - b.pct)
    .slice(0, 3);

  const currentAnswered = domain
    ? domain.questions.every((q) => answers[q.id] !== undefined)
    : true;

  const setAnswer = (qid, value) => setAnswers((a) => ({ ...a, [qid]: value }));

  const restart = () => { setAnswers({}); setContact({ name: "", email: "", unternehmen: "" }); setSubmit("idle"); setStep(0); window.scrollTo(0, 0); };
  const go = (s) => { setStep(s); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const summaryText = () =>
    `${band.label[lang]} — ${lang === "de" ? "Gesamt" : "Overall"} ${overall}%. ` +
    READINESS_DOMAINS.map((d) => `${d.label[lang]}: ${domainPct(d)}%`).join(" · ");

  const submitLead = async (e) => {
    e.preventDefault();
    if (submit === "sending") return;
    setSubmit("sending");
    try {
      const res = await fetch("/api/readiness", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          name: contact.name,
          email: contact.email,
          unternehmen: contact.unternehmen,
          lang,
          overall,
          band: band.label[lang],
          domains: READINESS_DOMAINS.reduce((o, d) => ({ ...o, [d.label[lang]]: domainPct(d) }), {}),
          answers,
          summary: summaryText(),
        }),
      });
      if (res.ok) setSubmit("ok");
      else setSubmit("error");
    } catch (err) {
      setSubmit("error");
    }
  };

  const seoTitle = lang === "de"
    ? "NIS2/DORA Readiness Check — clavis/cyber"
    : "NIS2/DORA Readiness Check — clavis/cyber";
  const seoDesc = lang === "de"
    ? "Kostenfreies, interaktives Self-Assessment: In wenigen Minuten zum priorisierten Reifegrad Ihrer NIS2- und DORA-Vorbereitung."
    : "Free, interactive self-assessment: get a prioritised maturity picture of your NIS2 and DORA readiness in a few minutes.";

  return (
    <main className="pt-[88px] pb-section min-h-screen bg-paper" data-testid="readiness-page">
      <Seo title={seoTitle} description={seoDesc} />
      <div className="max-w-3xl mx-auto px-6">

        {/* Kopf / Fortschritt */}
        <div className="border-t border-hairline pt-6 mb-10 flex items-baseline justify-between gap-6">
          <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text">
            {lang === "de" ? "Readiness Check · NIS2 / DORA" : "Readiness Check · NIS2 / DORA"}
          </span>
          {!isIntro && (
            <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text">
              {isResult ? (lang === "de" ? "Ergebnis" : "Result") : `${step} / ${N}`}
            </span>
          )}
        </div>

        {!isIntro && !isResult && (
          <div className="h-px bg-hairline mb-12 relative" aria-hidden="true">
            <div className="absolute left-0 top-0 h-px bg-brass transition-all duration-300"
                 style={{ width: `${(step / N) * 100}%` }} />
          </div>
        )}

        {/* ---------- INTRO ---------- */}
        {isIntro && (
          <div data-testid="readiness-intro">
            <h1 className="font-sans text-ink font-extrabold tracking-tightest leading-[1.05] mb-6"
                style={{ fontSize: "clamp(2rem, 5vw, 3.25rem)" }}>
              {lang === "de" ? "Wie weit sind Sie bei NIS2 und DORA?" : "How ready are you for NIS2 and DORA?"}
            </h1>
            <p className="text-graphite text-lg leading-relaxed mb-8 max-w-prose-wide">
              {lang === "de"
                ? "Ein strukturiertes Self-Assessment über fünf Felder, an denen Prüfungen und reale Vorfälle scheitern. Sie erhalten sofort einen priorisierten Reifegrad – keine Anmeldung nötig, um das Ergebnis zu sehen."
                : "A structured self-assessment across the five areas where audits and real incidents fail. You get a prioritised maturity picture immediately – no sign-up needed to see your result."}
            </p>
            <ul className="border-t border-hairline mb-10">
              {READINESS_DOMAINS.map((d, i) => (
                <li key={d.id} className="flex items-baseline gap-4 py-4 border-b border-hairline">
                  <span className="font-mono text-[11px] tracking-classification uppercase text-brass-deep w-8 shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-ink text-[15px]">{d.label[lang]}</span>
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap items-center gap-x-8 gap-y-3 mb-10">
              <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text">
                {lang === "de" ? "≈ 3–5 Minuten" : "≈ 3–5 minutes"}
              </span>
              <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text">
                {lang === "de" ? "13 Fragen" : "13 questions"}
              </span>
              <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text">
                {lang === "de" ? "Sofortiges Ergebnis" : "Instant result"}
              </span>
            </div>
            <button
              onClick={() => go(1)}
              className="inline-flex items-center justify-center bg-ink text-paper px-8 py-[15px] text-[12px] font-semibold uppercase tracking-label hover:bg-graphite transition-colors"
              data-testid="readiness-start"
            >
              {lang === "de" ? "Assessment starten" : "Start assessment"}
            </button>
            <p className="text-mid-text text-xs italic mt-8 max-w-prose-wide leading-relaxed">
              {lang === "de"
                ? "Indikative Selbsteinschätzung, kein formelles Audit. Ihre Antworten verlassen den Browser erst, wenn Sie am Ende aktiv ein Lagebild anfordern."
                : "Indicative self-assessment, not a formal audit. Your answers only leave the browser if you actively request a briefing at the end."}
            </p>
          </div>
        )}

        {/* ---------- FRAGEN ---------- */}
        {domain && (
          <div data-testid={`readiness-domain-${domain.id}`}>
            <span className="font-mono text-[11px] tracking-classification uppercase text-brass-deep block mb-3">
              {lang === "de" ? "Feld" : "Area"} {step} / {N}
            </span>
            <h2 className="font-sans text-ink font-extrabold tracking-tightest leading-[1.1] mb-10"
                style={{ fontSize: "clamp(1.6rem, 3.5vw, 2.25rem)" }}>
              {domain.label[lang]}
            </h2>

            <div className="space-y-10">
              {domain.questions.map((q) => (
                <div key={q.id} className="border-t border-hairline pt-6">
                  <p className="text-ink text-[15px] md:text-base leading-relaxed mb-5 max-w-prose-wide">
                    {q.text[lang]}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    {READINESS_OPTIONS.map((opt) => {
                      const selected = answers[q.id] === opt.value;
                      return (
                        <button
                          key={opt.value}
                          onClick={() => setAnswer(q.id, opt.value)}
                          className={`flex-1 text-left px-4 py-3 border text-[13px] font-medium transition-colors ${
                            selected
                              ? "bg-ink text-paper border-ink"
                              : "bg-paper text-ink border-hairline hover:border-brass"
                          }`}
                          data-testid={`opt-${q.id}-${opt.value}`}
                        >
                          {opt.label[lang]}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between mt-12 border-t border-hairline pt-6">
              <button
                onClick={() => go(step - 1)}
                className="font-mono text-[11px] tracking-classification uppercase text-mid-text hover:text-ink transition-colors"
              >
                ← {lang === "de" ? "Zurück" : "Back"}
              </button>
              <button
                onClick={() => go(step === N ? N + 1 : step + 1)}
                disabled={!currentAnswered}
                className="inline-flex items-center justify-center bg-ink text-paper px-7 py-[13px] text-[12px] font-semibold uppercase tracking-label hover:bg-graphite transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="readiness-next"
              >
                {step === N
                  ? (lang === "de" ? "Auswerten" : "See result")
                  : (lang === "de" ? "Weiter" : "Next")}
              </button>
            </div>
            {!currentAnswered && (
              <p className="text-mid-text text-xs italic mt-3 text-right">
                {lang === "de" ? "Bitte alle Fragen beantworten." : "Please answer all questions."}
              </p>
            )}
          </div>
        )}

        {/* ---------- ERGEBNIS ---------- */}
        {isResult && (
          <div data-testid="readiness-result">
            <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text block mb-4">
              {lang === "de" ? "Ihr Reifegrad" : "Your maturity"}
            </span>
            <div className="flex items-end gap-4 mb-2">
              <span className="font-sans font-extrabold tracking-tightest leading-none text-ink"
                    style={{ fontSize: "clamp(3.5rem, 12vw, 6rem)" }}>
                {overall}%
              </span>
              <span className="font-mono text-[12px] tracking-classification uppercase mb-3"
                    style={{ color: band.tone }}>
                {band.label[lang]}
              </span>
            </div>
            <p className="text-graphite text-base md:text-lg leading-relaxed mb-12 max-w-prose-wide border-t border-hairline pt-6">
              {band.text[lang]}
            </p>

            {/* Domain-Balken */}
            <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text block mb-5">
              {lang === "de" ? "Reifegrad je Feld" : "Maturity by area"}
            </span>
            <div className="space-y-5 mb-14">
              {READINESS_DOMAINS.map((d) => {
                const pct = domainPct(d);
                return (
                  <div key={d.id}>
                    <div className="flex items-baseline justify-between mb-1.5 gap-4">
                      <span className="text-ink text-[14px]">{d.label[lang]}</span>
                      <span className="font-mono text-[12px] tracking-classification text-mid-text shrink-0">{pct}%</span>
                    </div>
                    <div className="h-1.5 bg-paper-mid">
                      <div className="h-1.5 transition-all duration-500"
                           style={{ width: `${pct}%`, backgroundColor: _readinessBand(pct).tone }} />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Priorisierte Empfehlungen */}
            {gaps.length > 0 && (
              <div className="mb-14">
                <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text block mb-6">
                  {lang === "de" ? "Priorisierte nächste Schritte" : "Prioritised next steps"}
                </span>
                <ol className="border-t border-hairline">
                  {gaps.map(({ d, pct }, i) => (
                    <li key={d.id} className="py-6 border-b border-hairline">
                      <div className="flex items-baseline gap-4 mb-2">
                        <span className="font-mono text-[11px] tracking-classification uppercase text-brass-deep shrink-0">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <h3 className="font-sans text-ink font-bold text-base md:text-lg tracking-tighter">
                          {d.label[lang]} <span className="text-mid-text font-mono text-[12px] font-normal">· {pct}%</span>
                        </h3>
                      </div>
                      <p className="text-mid-text text-[15px] leading-relaxed max-w-prose-wide pl-[34px]">
                        {d.recommendation[lang]}
                      </p>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {/* Lead-Capture */}
            <div className="border border-hairline bg-paper p-8 md:p-10 mb-10" data-testid="readiness-lead">
              {submit === "ok" ? (
                <div data-testid="readiness-lead-ok">
                  <span className="font-mono text-[11px] tracking-classification uppercase text-brass-deep block mb-3">
                    {lang === "de" ? "Angefragt" : "Requested"}
                  </span>
                  <h3 className="font-sans text-ink font-bold tracking-tighter leading-snug mb-3"
                      style={{ fontSize: "clamp(1.25rem, 2.2vw, 1.625rem)" }}>
                    {lang === "de" ? "Wir melden uns mit einem priorisierten Lagebild." : "We will be in touch with a prioritised briefing."}
                  </h3>
                  <p className="text-mid-text text-sm leading-relaxed max-w-prose-wide">
                    {lang === "de"
                      ? "Ihr Reifegrad und die schwächsten Felder liegen uns vor. Sie hören kurzfristig von einem Senior-Praktiker – nicht von einem Account-Manager."
                      : "We have your maturity result and the weakest areas. A senior practitioner – not an account manager – will reach out shortly."}
                  </p>
                </div>
              ) : (
                <>
                  <span className="font-mono text-[11px] tracking-classification uppercase text-brass-deep block mb-3">
                    {lang === "de" ? "Nächster Schritt" : "Next step"}
                  </span>
                  <h3 className="font-sans text-ink font-bold tracking-tighter leading-snug mb-3"
                      style={{ fontSize: "clamp(1.25rem, 2.2vw, 1.625rem)" }}>
                    {lang === "de" ? "Priorisiertes Lagebild & Erstgespräch" : "Prioritised briefing & initial call"}
                  </h3>
                  <p className="text-mid-text text-sm leading-relaxed mb-6 max-w-prose-wide">
                    {lang === "de"
                      ? "Wir senden Ihnen Ihre Auswertung mit konkreten, auf Ihre schwächsten Felder zugeschnittenen Maßnahmen und ein Angebot für ein kostenfreies Erstgespräch. Vertraulich, ohne Vertriebskette."
                      : "We send you your assessment with concrete measures tailored to your weakest areas and an offer for a free initial call. Confidential, no sales chain."}
                  </p>
                  <form onSubmit={submitLead} className="space-y-3">
                    <div className="flex flex-col sm:flex-row gap-3">
                      <input
                        type="text"
                        value={contact.name}
                        onChange={(e) => setContact((c) => ({ ...c, name: e.target.value }))}
                        placeholder={lang === "de" ? "Name (optional)" : "Name (optional)"}
                        className="flex-1 bg-paper border border-hairline px-4 py-[13px] text-sm text-ink placeholder:text-ash focus:outline-none focus:border-brass transition-colors"
                        data-testid="readiness-name"
                      />
                      <input
                        type="text"
                        value={contact.unternehmen}
                        onChange={(e) => setContact((c) => ({ ...c, unternehmen: e.target.value }))}
                        placeholder={lang === "de" ? "Unternehmen (optional)" : "Company (optional)"}
                        className="flex-1 bg-paper border border-hairline px-4 py-[13px] text-sm text-ink placeholder:text-ash focus:outline-none focus:border-brass transition-colors"
                        data-testid="readiness-company"
                      />
                    </div>
                    <input
                      type="email"
                      required
                      value={contact.email}
                      onChange={(e) => { setContact((c) => ({ ...c, email: e.target.value })); if (submit === "error") setSubmit("idle"); }}
                      placeholder={lang === "de" ? "E-Mail (geschäftlich)" : "Email (business)"}
                      className="w-full bg-paper border border-hairline px-4 py-[13px] text-sm text-ink placeholder:text-ash focus:outline-none focus:border-brass transition-colors"
                      data-testid="readiness-email"
                    />
                    <button
                      type="submit"
                      disabled={submit === "sending"}
                      className="w-full sm:w-auto inline-flex items-center justify-center bg-ink text-paper px-8 py-[14px] text-[12px] font-semibold uppercase tracking-label hover:bg-graphite transition-colors disabled:opacity-60"
                      data-testid="readiness-submit"
                    >
                      {submit === "sending"
                        ? (lang === "de" ? "Sende…" : "Sending…")
                        : (lang === "de" ? "Lagebild anfordern" : "Request briefing")}
                    </button>
                  </form>
                  {submit === "error" && (
                    <p className="mt-3 font-mono text-[11px] tracking-classification uppercase text-red-700">
                      {lang === "de"
                        ? "Etwas ging schief. Bitte E-Mail prüfen oder später erneut versuchen."
                        : "Something went wrong. Please check the address or try again later."}
                    </p>
                  )}
                </>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
              <button
                onClick={restart}
                className="font-mono text-[11px] tracking-classification uppercase text-mid-text hover:text-ink transition-colors"
                data-testid="readiness-restart"
              >
                {lang === "de" ? "↻ Neu starten" : "↻ Start over"}
              </button>
              <button
                onClick={() => (onContactClick ? onContactClick("general") : navigate(lang === "de" ? "/" : "/en"))}
                className="font-mono text-[11px] tracking-classification uppercase text-ink underline decoration-brass decoration-1 underline-offset-[4px] hover:text-brass-deep transition-colors"
              >
                {lang === "de" ? "Direkt Kontakt aufnehmen →" : "Contact us directly →"}
              </button>
            </div>
            <p className="text-mid-text text-xs italic mt-8 max-w-prose-wide leading-relaxed">
              {lang === "de"
                ? "Diese Auswertung ist eine indikative Selbsteinschätzung und ersetzt kein formelles NIS2-/DORA-Audit."
                : "This assessment is an indicative self-evaluation and does not replace a formal NIS2/DORA audit."}
            </p>
          </div>
        )}
      </div>
      <Footer />
    </main>
  );
};

// Differenzierung Section
const DifferenzierungSection = () => {
  const { t } = useLanguage();

  return (
    <section className="py-section px-6 bg-paper border-t border-hairline" data-testid="differenzierung-section">
      <div className="max-w-6xl mx-auto">

        <div className="border-t border-hairline pt-6 mb-12">
          <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text">
            Position
          </span>
        </div>

        <ScrollReveal>
          <h2 className="font-sans text-ink font-extrabold leading-[1.1] tracking-tightest mb-12 max-w-prose-wide"
              style={{ fontSize: 'clamp(1.875rem, 3.5vw, 2.5rem)' }}>
            {t.differenzierung.title}
          </h2>
        </ScrollReveal>

        {/* Negativ — Tacitus list of explicit refusals. */}
        <ul className="border-t border-hairline mb-20">
          {t.differenzierung.negativ.map((statement, i) => (
            <ScrollReveal key={i} delay={i * 0.04}>
              <li className="flex items-baseline gap-5 py-5 border-b border-hairline">
                <span className="font-mono text-[11px] tracking-classification uppercase text-signal-red-deep w-12 shrink-0">
                  Nein
                </span>
                <p className="text-ink text-base md:text-lg leading-relaxed max-w-prose-wide">{statement}</p>
              </li>
            </ScrollReveal>
          ))}
        </ul>

        {/* Positiv — four columns, hairline rules. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-y-10 lg:gap-x-8 border-t border-hairline pt-10">
          {t.differenzierung.positiv.map((item, i) => (
            <ScrollReveal key={i} delay={i * 0.05}>
              <div className="lg:border-l lg:border-hairline lg:pl-6 first:lg:border-l-0 first:lg:pl-0">
                <span className="font-mono text-[11px] tracking-classification uppercase text-brass-deep">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <h3 className="font-sans text-ink font-bold text-lg mt-3 mb-3">{item.title}</h3>
                <p className="text-mid-text text-sm leading-relaxed max-w-prose">{item.desc}</p>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
};

// Szenarien Section
const SzenarienSection = () => {
  const { lang, t } = useLanguage();
  
  const getLink = (link) => {
    if (lang === "de") {
      return link.replace("/en", "");
    }
    return link;
  };
  
  return (
    <section id="szenarien" className="py-section px-6 scroll-mt-24 bg-paper" data-testid="szenarien-section">
      <div className="max-w-6xl mx-auto">

        <div className="border-t border-hairline pt-6 mb-12 flex items-baseline justify-between gap-6">
          <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text">
            Szenarien
          </span>
          <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text hidden md:inline">
            04 Einstiegspunkte
          </span>
        </div>

        <ScrollReveal>
          <h2 className="font-sans text-ink font-extrabold leading-snug tracking-tightest mb-12 max-w-prose-wide"
              style={{ fontSize: 'clamp(1.875rem, 3.5vw, 2.5rem)' }}>
            {t.szenarien.title}
          </h2>
        </ScrollReveal>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-t border-hairline">
          {t.szenarien.items.map((szenario, i) => {
            const isLastInRow = (i + 1) % 4 === 0;
            return (
              <ScrollReveal key={i} delay={i * 0.05}>
                <Link
                  to={getLink(szenario.link)}
                  className={`group block py-8 md:px-6 md:py-10 border-b border-hairline ${isLastInRow ? '' : 'lg:border-r lg:border-hairline'} hover:bg-paper-mid transition-colors h-full`}
                  data-testid={`szenario-card-${i}`}
                >
                  <span className="font-mono text-[11px] tracking-classification uppercase text-brass-deep">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <h3 className="font-sans text-ink font-bold text-lg md:text-xl mt-3 mb-3 group-hover:text-brass-deep transition-colors">
                    {szenario.title}
                  </h3>
                  <p className="text-mid-text text-sm leading-relaxed mb-6">{szenario.desc}</p>
                  <span className="font-mono text-[11px] tracking-classification uppercase text-ink underline decoration-brass decoration-1 underline-offset-[4px] group-hover:text-brass-deep transition-colors">
                    Mehr erfahren ↗
                  </span>
                </Link>
              </ScrollReveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Team Teaser — editorial Senior-Only statement.
const TeamTeaserSection = () => {
  const { lang, t } = useLanguage();

  return (
    <section className="py-section px-6 bg-paper-mid" data-testid="team-teaser-section">
      <div className="max-w-6xl mx-auto">

        <div className="border-t border-hairline pt-6 mb-12">
          <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text">
            Team
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-x-8 gap-y-8 items-start">
          <ScrollReveal className="md:col-span-7">
            <h2 className="font-sans text-ink font-extrabold leading-[1.1] tracking-tightest mb-6"
                style={{ fontSize: 'clamp(1.875rem, 3.5vw, 2.5rem)' }}>
              {t.teamTeaser.title}
            </h2>
            <p className="text-mid-text text-base md:text-lg leading-relaxed max-w-prose-wide">
              {t.teamTeaser.desc}
            </p>
          </ScrollReveal>

          <ScrollReveal className="md:col-span-5" delay={0.1}>
            <ul className="border-l border-hairline pl-6 mb-8 space-y-3">
              {t.teamTeaser.stats.map((stat, i) => (
                <li key={i} className="text-ink text-sm font-medium leading-relaxed">{stat}</li>
              ))}
            </ul>

            <Link
              to={lang === "de" ? "/team" : "/en/team"}
              className="inline-flex items-center bg-ink text-paper font-semibold uppercase tracking-label text-[12px] px-7 py-[12px] hover:bg-graphite transition-colors"
              data-testid="team-teaser-link"
            >
              {t.teamTeaser.linkText}
              <span aria-hidden="true" className="ml-2">↗</span>
            </Link>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
};

// KI Page — standalone AI-positioning page. Cyber heritage is proof-of-origin,
// not the headline. Incident/emergency layer is intentionally absent here
// (suppressed in Navigation + NotfallBanner for /ki routes).
const KiPage = ({ onContactClick }) => {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const k = t.ki;
  const homePath = lang === "de" ? "/" : "/en";

  // Signature sequence: ENTWICKELN -> BETREIBEN -> ABSICHERN lights up on load.
  const [lit, setLit] = useState(0);
  useEffect(() => {
    const reduce = typeof window !== "undefined" &&
      window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) { setLit(k.hero.sequence.length); return; }
    const timers = k.hero.sequence.map((_, i) =>
      setTimeout(() => setLit((n) => Math.max(n, i + 1)), 350 + i * 650)
    );
    return () => timers.forEach(clearTimeout);
  }, [k.hero.sequence]);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Clavis Cyber GmbH",
    alternateName: "clavis/cyber",
    url: SITE_URL + (lang === "de" ? "/ki" : "/en/ki"),
    logo: SITE_URL + "/favicon-192x192.png",
    email: "info@clavis-cyber.de",
    telephone: "+493075439203",
    address: {
      "@type": "PostalAddress",
      streetAddress: "Kurfürstendamm 194",
      postalCode: "10707",
      addressLocality: "Berlin",
      addressCountry: "DE",
    },
    areaServed: "DACH",
    description: k.seoDesc,
    knowsAbout: ["Artificial Intelligence", "AI Security", "LLM Security", "AI Governance", "Threat Intelligence", "Incident Response"],
  };

  const d = new Date();
  const quarterLabel = `${d.getFullYear()} Q${Math.floor(d.getMonth() / 3) + 1}`;

  return (
    <main className="pt-[68px] bg-paper">
      <Seo title={k.seoTitle} description={k.seoDesc} jsonLd={jsonLd} />

      {/* HERO */}
      <section className="relative bg-paper border-b border-hairline pt-24 pb-20 md:pt-28 md:pb-24 px-6" data-testid="ki-hero">
        <div className="max-w-6xl mx-auto">
          <div className="border-t border-hairline pt-6 mb-12 flex items-baseline justify-between gap-6">
            <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text">
              {k.classification} · {quarterLabel}
            </span>
            <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text hidden md:inline">
              clavis / cyber · KI
            </span>
          </div>

          <p className="font-mono text-[11px] tracking-classification uppercase text-brass-deep mb-6">
            {k.hero.tagline}
          </p>
          <h1
            className="font-sans text-ink font-extrabold leading-[1] tracking-tightest mb-8 max-w-4xl"
            style={{ fontSize: "clamp(2.5rem, 6vw, 4.5rem)" }}
          >
            {k.hero.headline}
          </h1>
          <p className="font-sans text-graphite text-lg md:text-xl leading-relaxed max-w-prose-wide mb-12">
            {k.hero.subline}
          </p>

          {/* Signature sequence */}
          <div className="border-t border-b border-hairline flex flex-col md:flex-row divide-y md:divide-y-0 md:divide-x divide-hairline" data-testid="ki-sequence">
            {k.hero.sequence.map((s, i) => {
              const on = i < lit;
              return (
                <div key={s.key} className="flex-1 px-5 py-6 md:px-6 md:py-8">
                  <div className="flex items-baseline gap-3">
                    <span className={`font-mono text-[11px] tracking-classification transition-colors duration-700 ${on ? "text-brass" : "text-hairline"}`}>
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span
                      className={`font-sans font-extrabold tracking-tightest text-2xl md:text-3xl transition-colors duration-700 ${on ? "text-brass-deep" : "text-hairline"}`}
                    >
                      {s.key}
                    </span>
                  </div>
                  <p className={`mt-3 text-sm leading-relaxed transition-colors duration-700 ${on ? "text-graphite" : "text-ash"}`}>
                    {s.sentence}
                  </p>
                </div>
              );
            })}
          </div>

          <p className="text-mid-text text-base md:text-lg max-w-prose-wide mt-12 mb-10 leading-relaxed">
            {k.hero.lead}
          </p>

          <div className="flex flex-col sm:flex-row sm:items-center gap-5">
            <button
              onClick={() => onContactClick("ki_strategie")}
              className="inline-flex items-center justify-center bg-ink text-paper font-semibold uppercase tracking-label text-[13px] px-8 py-[14px] hover:bg-graphite focus-visible:bg-graphite transition-colors"
              data-testid="ki-hero-cta"
            >
              {k.hero.ctaPrimary}
            </button>
            <button
              onClick={() => navigate(homePath)}
              className="text-mid-text text-[13px] uppercase tracking-label font-semibold underline decoration-hairline decoration-1 underline-offset-[6px] hover:text-ink hover:decoration-brass transition-colors"
              data-testid="ki-hero-secondary"
            >
              {k.hero.ctaSecondary} ↗
            </button>
          </div>
        </div>
      </section>

      {/* HALTUNG */}
      <section className="bg-paper border-b border-hairline py-16 md:py-24 px-6" data-testid="ki-haltung">
        <div className="max-w-6xl mx-auto">
          <div className="border-t border-hairline pt-6 mb-12 flex items-baseline gap-4">
            <span className="font-mono text-[11px] tracking-classification uppercase text-brass-deep">{k.haltung.kicker}</span>
            <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text">{k.haltung.title}</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8">
            {k.haltung.claims.map((c) => (
              <div key={c.n}>
                <span className="font-mono text-[11px] tracking-classification uppercase text-brass">{c.n}</span>
                <h3 className="font-sans text-ink font-bold text-xl tracking-tighter mt-3 mb-3">{c.title}</h3>
                <p className="text-mid-text text-base leading-relaxed">{c.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* KOMPETENZFELDER */}
      {k.fields.map((f, idx) => (
        <section
          key={f.nummer}
          className={`border-b border-hairline py-16 md:py-24 px-6 ${idx % 2 === 1 ? "bg-paper-mid" : "bg-paper"}`}
          data-testid={`ki-field-${f.nummer}`}
        >
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-y-8 md:gap-x-10">
              <div className="md:col-span-4">
                <div className="border-t border-hairline pt-6">
                  <span className="font-mono text-[11px] tracking-classification uppercase text-brass-deep">{f.kicker}</span>
                  <h2 className="font-sans text-ink font-extrabold text-3xl md:text-4xl tracking-tightest mt-4 mb-4 leading-[1.05]">
                    {f.title}
                  </h2>
                  <p className="font-sans text-graphite text-lg leading-snug tracking-tighter">{f.lead}</p>
                  {f.badge && (
                    <span className="inline-flex items-center gap-2 mt-6 border border-brass px-3 py-1.5 rounded-chip">
                      <span className="w-1.5 h-1.5 rounded-full bg-brass" aria-hidden="true" />
                      <span className="font-mono text-[10px] tracking-classification uppercase text-brass-deep">{f.badge}</span>
                    </span>
                  )}
                </div>
              </div>
              <div className="md:col-span-8 md:pt-6">
                <div className="border-t border-hairline divide-y divide-hairline">
                  {f.blocks.map((b, bi) => (
                    <div key={bi} className="py-6 first:pt-6">
                      <div className="flex items-baseline gap-3 flex-wrap mb-2">
                        <h3 className="font-sans text-ink font-bold text-lg tracking-tighter">{b.title}</h3>
                        {b.badge && (
                          <span className="font-mono text-[10px] tracking-classification uppercase text-brass-deep border border-brass px-2 py-0.5 rounded-chip">
                            {b.badge}
                          </span>
                        )}
                      </div>
                      <p className="text-mid-text text-base leading-relaxed max-w-prose">{b.text}</p>
                      {b.note && (
                        <p className="font-mono text-[12px] text-ash mt-3 leading-relaxed">{b.note}</p>
                      )}
                    </div>
                  ))}
                </div>
                {f.note && (
                  <p className="text-graphite text-base leading-relaxed mt-8 pl-4 border-l-2 border-brass max-w-prose">
                    {f.note}
                  </p>
                )}
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* ÖKOSYSTEM */}
      <section className="bg-paper border-b border-hairline py-16 md:py-24 px-6" data-testid="ki-oekosystem">
        <div className="max-w-6xl mx-auto">
          <div className="border-t border-hairline pt-6 mb-10 flex items-baseline gap-4">
            <span className="font-mono text-[11px] tracking-classification uppercase text-brass-deep">{k.oekosystem.kicker}</span>
            <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text">{k.oekosystem.title}</span>
          </div>
          <p className="font-sans text-graphite text-xl md:text-2xl leading-snug tracking-tighter max-w-prose-wide mb-10">
            {k.oekosystem.lead}
          </p>
          <ul className="grid grid-cols-1 md:grid-cols-3 gap-8 border-t border-hairline pt-8">
            {k.oekosystem.points.map((p, i) => (
              <li key={i} className="flex gap-3">
                <span className="font-mono text-[11px] text-brass mt-1" aria-hidden="true">—</span>
                <span className="text-mid-text text-base leading-relaxed">{p}</span>
              </li>
            ))}
          </ul>
          {/* Partner-Logos: erst nach offizieller Ankündigung einsetzen. */}
          {/* Konkrete Zahlen/Kapazitäten/Namen erst nach offizieller Ankündigung. */}
          <p className="font-mono text-[12px] text-ash mt-10 leading-relaxed max-w-prose">{k.oekosystem.partnersNote}</p>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-ink py-20 md:py-28 px-6" data-testid="ki-cta">
        <div className="max-w-6xl mx-auto">
          <div className="border-t border-graphite pt-6 mb-8">
            <span className="font-mono text-[11px] tracking-classification uppercase text-brass">{k.cta.kicker}</span>
          </div>
          <h2 className="font-sans text-paper font-extrabold text-3xl md:text-5xl tracking-tightest leading-[1.05] max-w-3xl mb-6">
            {k.cta.title}
          </h2>
          <p className="text-ash text-base md:text-lg leading-relaxed max-w-prose mb-10">{k.cta.text}</p>
          <div className="flex flex-col sm:flex-row sm:items-center gap-5">
            <button
              onClick={() => onContactClick("ki_strategie")}
              className="inline-flex items-center justify-center bg-brass text-ink font-semibold uppercase tracking-label text-[13px] px-8 py-[14px] hover:bg-brass-deep transition-colors"
              data-testid="ki-cta-primary"
            >
              {k.cta.primary}
            </button>
            <Link
              to={homePath}
              className="text-ash text-[13px] uppercase tracking-label font-semibold underline decoration-graphite decoration-1 underline-offset-[6px] hover:text-paper hover:decoration-brass transition-colors"
              data-testid="ki-cta-secondary"
            >
              {k.cta.secondary} ↗
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

// Home Page — paper canvas, top-padding offsets the fixed nav.
const HomePage = ({ onIncidentClick, onContactClick }) => {
  const { lang } = useLanguage();
  const title = lang === "de"
    ? "clavis/cyber — Cybersecurity für Entscheider"
    : "clavis/cyber — Cybersecurity for decision-makers";
  const description = lang === "de"
    ? "Senior-only-Beratung aus Berlin für Incident Response, NIS2/DORA-Readiness und Threat Intelligence. Erstes Lagebild in 48 Stunden – direkter Draht zu den Operatoren."
    : "Senior-only practice from Berlin for incident response, NIS2/DORA readiness and threat intelligence. First situation report within 48 hours – direct line to the operators.";
  const orgJsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Clavis Cyber GmbH",
    alternateName: "clavis/cyber",
    url: SITE_URL,
    logo: SITE_URL + "/favicon-192x192.png",
    email: "info@clavis-cyber.de",
    telephone: "+493075439203",
    address: {
      "@type": "PostalAddress",
      streetAddress: "Kurfürstendamm 194",
      postalCode: "10707",
      addressLocality: "Berlin",
      addressCountry: "DE",
    },
    areaServed: "DACH",
    description,
  };
  return (
    <main className="pt-[68px] bg-paper">
      <Seo title={title} description={description} jsonLd={orgJsonLd} />
      <HeroSection onContactClick={onContactClick} />
      <ClaritySection />
      <LeadershipSection />
      <TrustedBySection />
      <PillarsSection />
      <CaseStudySection />
      <InsightsSection />
      <DifferenzierungSection />
      <SzenarienSection />
      <Footer />
    </main>
  );
};

// Szenario Page Template - Enhanced with Timeline, Deliverables, Case Study, FAQs
const SzenarioPage = ({ szenarioKey, onIncidentClick, onContactClick }) => {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const szenario = t[szenarioKey];
  const [openFaq, setOpenFaq] = useState(null);
  
  const handleCTA = () => {
    if (szenarioKey === "szenarioIncident") {
      onIncidentClick();
    } else if (szenarioKey === "szenarioNis2") {
      navigate(lang === "de" ? "/readiness" : "/en/readiness");
    } else if (szenarioKey === "szenarioThreat") {
      onContactClick("briefing_call");
    } else {
      onContactClick("scoping_call");
    }
  };
  
  return (
    <main className="pt-[88px] pb-section min-h-screen bg-paper" data-testid={`szenario-page-${szenarioKey}`}>
      <Seo
        title={`${szenario.headline} — clavis/cyber`}
        description={szenario.subline || szenario.risk}
      />
      <div className="max-w-4xl mx-auto px-6">

        <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text">
          {lang === 'de' ? 'Szenario' : 'Scenario'}
        </span>

        <h1 className="font-sans text-ink font-extrabold leading-[1.05] tracking-tightest mt-4 mb-6"
            style={{ fontSize: 'clamp(2rem, 4.5vw, 3.25rem)' }}>
          {szenario.headline}
        </h1>
        {szenario.subline && (
          <p className="text-graphite text-lg md:text-xl leading-relaxed max-w-prose-wide mb-12">
            {szenario.subline}
          </p>
        )}

        <div className="border-l border-signal-red pl-6 mb-16 py-1">
          <span className="font-mono text-[11px] tracking-classification uppercase text-signal-red-deep block mb-2">
            {lang === 'de' ? 'Das Risiko' : 'The risk'}
          </span>
          <p className="text-ink text-base md:text-lg leading-relaxed max-w-prose-wide">{szenario.risk}</p>
        </div>

        <section className="mb-16 border-t border-hairline pt-10">
          <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text block mb-8">
            {szenario.whatWeDo}
          </span>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-8 sm:gap-x-10">
            {szenario.steps.map((step, i) => (
              <div key={i} className="flex items-start gap-5">
                <span className="font-mono text-[11px] tracking-classification uppercase text-brass-deep w-8 shrink-0 pt-[3px]">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <p className="text-ink text-[15px] leading-relaxed">{step}</p>
              </div>
            ))}
          </div>
        </section>

        {szenario.timeline && (
          <section className="mb-16 border-t border-hairline pt-10">
            <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text block mb-8">
              {szenario.timeline.title}
            </span>
            <div className="relative pl-6">
              <div className="absolute left-[7px] top-2 bottom-2 w-px bg-hairline"></div>
              <ul className="space-y-7">
                {szenario.timeline.items.map((item, i) => (
                  <li key={i} className="relative flex items-start gap-6">
                    <span className="absolute -left-6 top-[6px] w-[7px] h-[7px] bg-brass"></span>
                    <span className="font-mono text-[11px] tracking-classification uppercase text-brass-deep w-24 shrink-0 pt-[3px]">
                      {item.time}
                    </span>
                    <p className="text-ink text-[15px] leading-relaxed">{item.desc}</p>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}

        {szenario.deliverables && (
          <section className="mb-16 border-l border-brass pl-6 py-2">
            <span className="font-mono text-[11px] tracking-classification uppercase text-brass-deep block mb-4">
              {szenario.deliverables.title}
            </span>
            <ul className="space-y-3">
              {szenario.deliverables.items.map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="font-mono text-[11px] tracking-classification uppercase text-brass-deep mt-[5px] shrink-0">→</span>
                  <span className="text-ink text-[15px] leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {szenario.caseStudy && (
          <section className="mb-16 border-t border-hairline pt-10">
            <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text block mb-3">
              {szenario.caseStudy.label}
            </span>
            <h3 className="font-sans text-ink font-extrabold tracking-tightest text-2xl md:text-3xl leading-[1.15] mb-4 max-w-prose-wide">
              {szenario.caseStudy.title}
            </h3>
            <p className="text-graphite text-base md:text-lg leading-relaxed mb-6 max-w-prose-wide">
              {szenario.caseStudy.text}
            </p>
            <div className="flex flex-wrap gap-x-8 gap-y-4 border-t border-hairline pt-5">
              {szenario.caseStudy.stats.map((stat, i) => (
                <span key={i} className="font-mono text-[11px] tracking-classification uppercase text-brass-deep">
                  {stat}
                </span>
              ))}
            </div>
          </section>
        )}

        {szenario.faqs && (
          <section className="mb-16 border-t border-hairline pt-10">
            <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text block mb-8">
              {lang === 'de' ? 'Häufige Fragen' : 'Frequently asked questions'}
            </span>
            <ul className="border-t border-hairline">
              {szenario.faqs.map((faq, i) => {
                const isOpen = openFaq === i;
                return (
                  <li key={i} className="border-b border-hairline">
                    <button
                      onClick={() => setOpenFaq(isOpen ? null : i)}
                      className="w-full flex justify-between items-start gap-6 py-5 text-left group"
                      data-testid={`szenario-faq-${i}`}
                    >
                      <span className="text-ink text-[15px] md:text-base font-medium pr-4 group-hover:text-brass-deep transition-colors">
                        {faq.q}
                      </span>
                      <span className={`font-mono text-[11px] tracking-classification uppercase text-mid-text shrink-0 pt-1 transition-transform ${isOpen ? 'rotate-90' : ''}`}>
                        {isOpen ? '−' : '+'}
                      </span>
                    </button>
                    {isOpen && (
                      <div className="pb-6 pr-10 text-mid-text text-[15px] leading-relaxed max-w-prose-wide">
                        {faq.a}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        <div className="border-t border-hairline pt-10 mb-12">
          <button
            onClick={handleCTA}
            className="inline-flex items-center justify-center bg-ink text-paper px-8 py-[14px] text-[13px] font-semibold uppercase tracking-label hover:bg-graphite transition-colors"
            data-testid="szenario-cta"
          >
            {szenario.cta}
          </button>
        </div>

        {szenario.asset && (
          <ScrollReveal delay={0.2}>
            <a
              href={szenario.asset.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group block border-t border-hairline pt-8 mt-4 hover:border-brass transition-colors"
              data-testid="szenario-asset-link"
            >
              <div className="flex items-start gap-8">
                <div className="flex-shrink-0 w-12 h-12 border border-hairline flex items-center justify-center group-hover:border-brass transition-colors">
                  <svg className="w-5 h-5 text-mid-text group-hover:text-brass-deep transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div className="flex-grow">
                  <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text block mb-2">
                    {lang === 'de' ? 'Download · PDF' : 'Download · PDF'}
                  </span>
                  <h4 className="font-sans text-ink font-bold text-lg md:text-xl tracking-tighter leading-snug group-hover:text-brass-deep transition-colors max-w-prose-wide">
                    {szenario.asset.label}
                  </h4>
                  <p className="text-mid-text text-sm mt-2">
                    {lang === 'de' ? 'Sofort verfügbar · ohne Formular' : 'Instantly available · no form'}
                  </p>
                </div>
                <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text group-hover:text-brass-deep transition-colors shrink-0 self-center">
                  ↓
                </span>
              </div>
            </a>
          </ScrollReveal>
        )}
      </div>
      <Footer />
    </main>
  );
};

// Team Member Detail Modal
const TeamMemberModal = ({ member, isOpen, onClose }) => {
  const { lang } = useLanguage();
  
  if (!isOpen || !member) return null;
  
  // Nur Vorname extrahieren
  const firstName = member.name.split(' ')[0];
  
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-ink/60 backdrop-blur-sm"
      data-testid="team-member-modal"
      onClick={onClose}
    >
      <div
        className="bg-paper border border-hairline max-w-3xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-paper/95 backdrop-blur-sm border-b border-hairline px-6 sm:px-8 py-4 flex justify-between items-center">
          <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text">
            {lang === 'de' ? 'Profil' : 'Profile'}
          </span>
          <button
            onClick={onClose}
            className="text-mid-text hover:text-ink p-1 transition-colors"
            data-testid="team-modal-close"
            aria-label="close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-6 sm:p-10 pt-8 sm:pt-10">

          <div className="flex items-start gap-6 mb-10 border-b border-hairline pb-8">
            {member.foto ? (
              <img
                src={member.foto}
                alt={firstName}
                className="w-24 h-24 object-cover border border-hairline grayscale"
              />
            ) : (
              <div className="w-24 h-24 bg-paper-mid border border-hairline flex items-center justify-center">
                <span className="font-mono text-base text-mid-text tracking-[0.18em]">{getTeamInitials(member.name)}</span>
              </div>
            )}
            <div className="pt-1">
              <h2 className="font-sans text-ink font-extrabold tracking-tightest text-2xl md:text-3xl leading-[1.1] mb-2">
                {firstName}
              </h2>
              <p className="font-mono text-[11px] tracking-classification uppercase text-brass-deep mb-2">
                {member.rolle[lang]}
              </p>
              <p className="text-mid-text text-sm flex items-center gap-1.5">
                <MapPin size={12} className="text-mid-text" /> {member.standort}
              </p>
            </div>
          </div>

          <blockquote className="border-l border-brass pl-5 mb-12 italic text-graphite text-base md:text-lg leading-relaxed max-w-prose-wide">
            „{member.zitat[lang]}"
          </blockquote>

          <section className="mb-12">
            <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text block mb-6">
              {lang === 'de' ? 'Berufserfahrung' : 'Experience'}
            </span>
            <div className="space-y-7">
              {member.erfahrung.map((job, i) => (
                <div key={i} className="border-l border-hairline pl-5">
                  <p className="text-ink font-semibold text-[15px] mb-3">{job.rolle}</p>
                  <ul className="space-y-2">
                    {job.beschreibung[lang].map((item, j) => (
                      <li key={j} className="text-mid-text text-sm leading-relaxed flex items-start gap-3">
                        <span className="font-mono text-[11px] text-brass-deep mt-[5px] shrink-0">→</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-12 border-t border-hairline pt-8">
            <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text block mb-6">
              {lang === 'de' ? 'Ausbildung' : 'Education'}
            </span>
            <ul className="space-y-4">
              {member.ausbildung.map((edu, i) => (
                <li key={i}>
                  <p className="text-ink text-[15px]">{edu.titel}</p>
                  <p className="text-mid-text text-sm mt-0.5">{edu.institution}</p>
                </li>
              ))}
            </ul>
          </section>

          <section className="mb-12 border-t border-hairline pt-8">
            <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text block mb-5">
              {lang === 'de' ? 'Zertifizierungen' : 'Certifications'}
            </span>
            <div className="flex flex-wrap gap-x-2 gap-y-2">
              {member.zertifikate.map((cert, i) => (
                <span key={i} className="font-mono text-[11px] tracking-classification uppercase text-ink border border-hairline px-3 py-1.5">
                  {cert}
                </span>
              ))}
            </div>
          </section>

          <section className="border-t border-hairline pt-8">
            <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text block mb-5">
              {lang === 'de' ? 'Sprachen' : 'Languages'}
            </span>
            <div className="flex flex-wrap gap-x-2 gap-y-2">
              {member.sprachen[lang].map((sprache, i) => (
                <span key={i} className="font-mono text-[11px] tracking-classification uppercase text-mid-text border border-hairline px-3 py-1.5">
                  {sprache}
                </span>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

// Philosophie Page
const PhilosophiePage = ({ onContactClick }) => {
  const { t, lang } = useLanguage();
  const p = t.philosophie;
  
  return (
    <main className="pt-[88px] pb-section min-h-screen bg-paper" data-testid="philosophie-page">
      <Seo
        title={`${lang === 'de' ? 'Philosophie' : 'Philosophy'} — clavis/cyber`}
        description={p.subline || p.intro}
      />
      <div className="max-w-4xl mx-auto px-6">

        <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text">
          Philosophie
        </span>

        <h1 className="font-sans text-ink font-extrabold leading-[1.05] tracking-tightest mt-4 mb-6"
            style={{ fontSize: 'clamp(2.25rem, 5vw, 3.5rem)' }}>
          {p.headline}
        </h1>
        <p className="text-graphite text-lg md:text-xl leading-relaxed max-w-prose-wide mb-10">
          {p.subline}
        </p>
        <p className="text-ink text-base md:text-lg leading-relaxed max-w-prose-wide border-t border-hairline pt-8 mb-20">
          {p.intro}
        </p>

        <section className="mb-20 border-t border-hairline pt-10">
          <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text block mb-6">
            {p.sections.whoWeAre.title}
          </span>
          <p className="text-ink text-base md:text-lg leading-relaxed max-w-prose-wide">
            {p.sections.whoWeAre.text}
          </p>
        </section>

        <section className="mb-20 border-t border-hairline pt-10">
          <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text block mb-10">
            {p.sections.whatWeStandFor.title}
          </span>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-12 sm:gap-x-10">
            {p.sections.whatWeStandFor.items.map((item, i) => (
              <div key={i} className="flex items-start gap-5">
                <span className="font-mono text-[11px] tracking-classification uppercase text-brass-deep w-8 shrink-0 pt-[3px]">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div>
                  <h3 className="font-sans text-ink font-bold text-lg tracking-tighter mb-2">{item.title}</h3>
                  <p className="text-mid-text text-[15px] leading-relaxed max-w-prose">{item.text}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-20 border-t border-hairline pt-10">
          <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text block mb-4">
            {p.sections.whatWeDontDo.title}
          </span>
          <p className="text-graphite text-base md:text-lg leading-relaxed max-w-prose-wide mb-10">
            {p.sections.whatWeDontDo.intro}
          </p>
          <ul className="border-t border-hairline">
            {p.sections.whatWeDontDo.items.map((item, i) => (
              <li key={i} className="flex items-baseline gap-5 py-5 border-b border-hairline">
                <span className="font-mono text-[11px] tracking-classification uppercase text-signal-red-deep w-12 shrink-0 pt-[3px]">
                  Nein
                </span>
                <div>
                  <h3 className="font-sans text-ink font-bold text-base md:text-lg tracking-tighter mb-1">{item.title}</h3>
                  <p className="text-mid-text text-[15px] leading-relaxed max-w-prose-wide">{item.text}</p>
                </div>
              </li>
            ))}
          </ul>
        </section>

        <section className="mb-20 border-l border-brass pl-6 py-2">
          <span className="font-mono text-[11px] tracking-classification uppercase text-brass-deep block mb-4">
            {p.sections.partnerApproach.title}
          </span>
          <p className="text-ink text-base md:text-lg leading-relaxed max-w-prose-wide mb-6">
            {p.sections.partnerApproach.text}
          </p>
          <blockquote className="italic text-graphite text-[15px] md:text-base leading-relaxed max-w-prose-wide">
            „{p.sections.partnerApproach.quote}"
          </blockquote>
        </section>

        <section className="mb-20 border-t border-hairline pt-10">
          <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text block mb-10">
            {p.sections.howWeWork.title}
          </span>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-10 sm:gap-x-10">
            {p.sections.howWeWork.items.map((item, i) => (
              <div key={i} className="flex items-start gap-4">
                <span className="font-mono text-[11px] tracking-classification uppercase text-brass-deep mt-[5px] shrink-0">→</span>
                <div>
                  <h3 className="font-sans text-ink font-bold text-base md:text-lg tracking-tighter mb-1">{item.title}</h3>
                  <p className="text-mid-text text-[15px] leading-relaxed max-w-prose">{item.text}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="border-t border-hairline pt-10" data-testid="philosophie-cta">
          <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text block mb-4">
            {lang === 'de' ? 'Gespräch' : 'Conversation'}
          </span>
          <h2 className="font-sans text-ink font-extrabold tracking-tightest leading-[1.1] text-2xl md:text-3xl mb-4 max-w-prose-wide">
            {p.cta.headline}
          </h2>
          <p className="text-mid-text text-base md:text-lg leading-relaxed mb-8 max-w-prose-wide">
            {p.cta.text}
          </p>
          <button
            onClick={onContactClick}
            className="inline-flex items-center justify-center bg-ink text-paper px-8 py-[14px] text-[13px] font-semibold uppercase tracking-label hover:bg-graphite transition-colors"
            data-testid="philosophie-cta-button"
          >
            {p.cta.button}
          </button>
        </section>
      </div>

      <Footer />
    </main>
  );
};

// Team Page — Founder profile + Practice Network
const TeamPage = () => {
  const { lang, t } = useLanguage();
  const founder = teamMembers[0];
  const s = t.team.sections;

  return (
    <main className="pt-[88px] pb-section min-h-screen bg-paper" data-testid="team-page">
      <Seo
        title={`${lang === 'de' ? 'Team & Verbund' : 'Team & Network'} — clavis/cyber`}
        description={t.team.philosophie}
      />
      <div className="max-w-6xl mx-auto px-6">

        <div className="border-t border-hairline pt-6 mb-8 flex items-baseline justify-between gap-6">
          <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text">
            {t.team.kicker}
          </span>
          <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text hidden md:inline">
            clavis / cyber · {lang === 'de' ? 'Berlin' : 'Berlin'}
          </span>
        </div>

        <h1 className="font-sans text-ink font-extrabold leading-[1.05] tracking-tightest mb-6"
            style={{ fontSize: 'clamp(2.25rem, 5vw, 3.5rem)' }}>
          {t.team.title}
        </h1>

        <p className="text-graphite text-lg md:text-xl leading-relaxed max-w-prose-wide mb-16">
          {t.team.philosophie}
        </p>

        {/* FOUNDER IDENTITY — only name, role, location, contact, short statement */}
        <section className="border-t border-hairline pt-10 mb-20" data-testid="founder-identity">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-y-10 lg:gap-x-10">
            <div className="lg:col-span-5">
              <span className="font-mono text-[11px] tracking-classification uppercase text-brass-deep block mb-3">
                {s.founder}
              </span>
              <h2 className="font-sans text-ink font-extrabold tracking-tightest leading-[1.02] mb-6"
                  style={{ fontSize: 'clamp(2rem, 4vw, 3rem)' }}>
                {founder.name}
              </h2>
              <p className="font-mono text-[11px] tracking-classification uppercase text-mid-text flex items-center gap-1.5 mb-8">
                <MapPin size={11} className="text-mid-text" /> {founder.standort}
              </p>
              <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text block mb-3">
                {s.kontakt}
              </span>
              <a
                href={`mailto:${founder.kontakt}`}
                className="font-mono text-[12px] tracking-classification uppercase text-ink underline decoration-brass decoration-1 underline-offset-[5px] hover:text-brass-deep transition-colors"
              >
                {founder.kontakt}
              </a>
            </div>

            <div className="lg:col-span-7 lg:border-l lg:border-hairline lg:pl-10">
              <blockquote className="font-sans text-ink leading-[1.25] tracking-tighter max-w-prose-wide"
                          style={{ fontSize: 'clamp(1.5rem, 2.8vw, 2.125rem)' }}>
                „{founder.zitat[lang]}"
              </blockquote>
            </div>
          </div>
        </section>

        {/* QUALIFIKATION + SPRACHEN */}
        <section className="border-t border-hairline pt-10 mb-24" data-testid="founder-qualifications">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-y-10 md:gap-x-10">
            <div>
              <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text block mb-5">
                {s.zertifikate}
              </span>
              <ul className="space-y-3">
                {founder.zertifikate.map((cert, i) => (
                  <li key={i} className="font-mono text-[12px] tracking-classification uppercase text-ink leading-relaxed">
                    {cert}
                  </li>
                ))}
              </ul>
            </div>
            <div className="md:border-l md:border-hairline md:pl-10">
              <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text block mb-5">
                {s.sprachen}
              </span>
              <ul className="space-y-3">
                {founder.sprachen[lang].map((sp, i) => (
                  <li key={i} className="font-mono text-[12px] tracking-classification uppercase text-ink leading-relaxed">
                    {sp}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* VERBUND */}
        <section className="border-t border-hairline pt-10 mb-16" data-testid="verbund-section">

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-y-10 lg:gap-x-10 mb-12">
            <div className="lg:col-span-4">
              <span className="font-mono text-[11px] tracking-classification uppercase text-brass-deep block mb-3">
                {t.team.verbund.kicker}
              </span>
              <h2 className="font-sans text-ink font-extrabold tracking-tightest leading-[1.05]"
                  style={{ fontSize: 'clamp(1.75rem, 3.4vw, 2.5rem)' }}>
                {t.team.verbund.title}
              </h2>
            </div>
            <div className="lg:col-span-8 lg:border-l lg:border-hairline lg:pl-10">
              <p className="text-graphite text-[15.5px] leading-[1.75] max-w-prose-wide">
                {t.team.verbund.body}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-y-10 lg:gap-x-10 border-t border-hairline pt-10">
            <div className="lg:col-span-7">
              <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text block mb-6">
                {t.team.verbund.kompetenzenLabel}
              </span>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-6 border-t border-hairline pt-4">
                {t.team.verbund.kompetenzen.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-ink text-[14.5px] leading-relaxed">
                    <span className="font-mono text-[11px] tracking-classification uppercase text-brass-deep w-5 shrink-0 pt-[3px]">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:col-span-5 lg:border-l lg:border-hairline lg:pl-10">
              <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text block mb-6">
                {t.team.verbund.prinzipienLabel}
              </span>
              <ul className="space-y-4 border-t border-hairline pt-4">
                {t.team.verbund.prinzipien.map((item, i) => (
                  <li key={i} className="text-graphite text-[14.5px] leading-relaxed">
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

      </div>

      <Footer />
    </main>
  );
};

// Impressum Page
const ImpressumPage = () => {
  const { t, lang } = useLanguage();

  return (
    <main className="pt-[88px] pb-section min-h-screen bg-paper" data-testid="impressum-page">
      <Seo
        title={`${lang === 'de' ? 'Impressum' : 'Imprint'} — clavis/cyber`}
        description={lang === 'de'
          ? 'Pflichtangaben und Anbieterkennzeichnung der Clavis Cyber GmbH, Berlin.'
          : 'Legal notice and provider identification of Clavis Cyber GmbH, Berlin.'}
      />
      <div className="max-w-3xl mx-auto px-6">
        <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text">
          {lang === 'de' ? 'Pflichtangaben' : 'Imprint'}
        </span>
        <h1 className="font-sans text-ink font-extrabold leading-[1.05] tracking-tightest mt-4 mb-10"
            style={{ fontSize: 'clamp(2.25rem, 5vw, 3.5rem)' }}>
          {t.impressum.title}
        </h1>
        <div className="border-t border-hairline pt-8 text-graphite whitespace-pre-line leading-[1.7] text-[15px] max-w-prose-wide">
          {t.impressum.content}
        </div>
      </div>
      <Footer />
    </main>
  );
};

// Datenschutz Page
const DatenschutzPage = () => {
  const { t, lang } = useLanguage();

  return (
    <main className="pt-[88px] pb-section min-h-screen bg-paper" data-testid="datenschutz-page">
      <Seo
        title={`${lang === 'de' ? 'Datenschutz' : 'Privacy'} — clavis/cyber`}
        description={lang === 'de'
          ? 'Datenschutzhinweise der Clavis Cyber GmbH nach DSGVO.'
          : 'Privacy notice of Clavis Cyber GmbH under the GDPR.'}
        noindex
      />
      <div className="max-w-3xl mx-auto px-6">
        <span className="font-mono text-[11px] tracking-classification uppercase text-mid-text">
          {lang === 'de' ? 'Hinweise · DSGVO' : 'Notice · GDPR'}
        </span>
        <h1 className="font-sans text-ink font-extrabold leading-[1.05] tracking-tightest mt-4 mb-10"
            style={{ fontSize: 'clamp(2.25rem, 5vw, 3.5rem)' }}>
          {t.datenschutz.title}
        </h1>
        <div className="border-t border-hairline pt-8 text-graphite whitespace-pre-line leading-[1.7] text-[15px] max-w-prose-wide">
          {t.datenschutz.content}
        </div>
      </div>
      <Footer />
    </main>
  );
};

// Cookie Consent Banner
const CookieBanner = () => {
  const { lang } = useLanguage();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem('cookie-consent')) {
      setVisible(true);
    }
  }, []);

  const accept = () => {
    localStorage.setItem('cookie-consent', 'accepted');
    setVisible(false);
  };

  if (!visible) return null;

  const text = lang === 'de'
    ? 'Diese Website verwendet ausschließlich technisch notwendige Cookies. Es werden keine Tracking- oder Analyse-Cookies eingesetzt.'
    : 'This website only uses technically necessary cookies. No tracking or analytics cookies are used.';
  const linkText = lang === 'de' ? 'Datenschutzerklärung' : 'Privacy Policy';
  const buttonText = lang === 'de' ? 'Verstanden' : 'Got it';
  const linkHref = lang === 'de' ? '/datenschutz' : '/en/datenschutz';

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9999] bg-paper border-t border-hairline px-6 py-5">
      <div className="max-w-6xl mx-auto flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <p className="text-sm text-mid-text leading-relaxed max-w-prose-wide">
          {text}{' '}
          <Link
            to={linkHref}
            className="text-ink underline decoration-brass decoration-1 underline-offset-[3px] hover:text-brass-deep"
          >
            {linkText}
          </Link>
        </p>
        <button
          onClick={accept}
          className="shrink-0 inline-flex items-center justify-center bg-ink text-paper font-semibold uppercase tracking-label text-[12px] px-6 py-3 hover:bg-graphite transition-colors"
        >
          {buttonText}
        </button>
      </div>
    </div>
  );
};

// App Wrapper with Language Context
const AppContent = () => {
  const location = useLocation();
  const [incidentModalOpen, setIncidentModalOpen] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [contactType, setContactType] = useState("general");
  
  const isEnglish = location.pathname.startsWith("/en");
  const isKiPage = location.pathname === "/ki" || location.pathname === "/en/ki";
  const [lang, setLang] = useState(isEnglish ? "en" : "de");
  
  useEffect(() => {
    setLang(location.pathname.startsWith("/en") ? "en" : "de");
  }, [location.pathname]);

  useEffect(() => {
    document.documentElement.lang = lang === "en" ? "en" : "de";
  }, [lang]);
  
  const t = translations[lang];
  
  const handleIncidentClick = () => setIncidentModalOpen(true);
  const handleContactClick = (type = "general") => {
    setContactType(type);
    setContactModalOpen(true);
  };
  
  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      <div className="bg-clavis-black min-h-screen">
        <ScrollToTop />
        <Navigation onIncidentClick={handleIncidentClick} onContactClick={handleContactClick} />
        
        <Routes>
          {/* German Routes */}
          <Route path="/" element={<HomePage onIncidentClick={handleIncidentClick} onContactClick={handleContactClick} />} />
          <Route path="/szenario/incident" element={<SzenarioPage szenarioKey="szenarioIncident" onIncidentClick={handleIncidentClick} onContactClick={handleContactClick} />} />
          <Route path="/szenario/nis2" element={<SzenarioPage szenarioKey="szenarioNis2" onIncidentClick={handleIncidentClick} onContactClick={handleContactClick} />} />
          <Route path="/szenario/threat-intelligence" element={<SzenarioPage szenarioKey="szenarioThreat" onIncidentClick={handleIncidentClick} onContactClick={handleContactClick} />} />
          <Route path="/szenario/pentest" element={<SzenarioPage szenarioKey="szenarioPentest" onIncidentClick={handleIncidentClick} onContactClick={handleContactClick} />} />
          <Route path="/philosophie" element={<PhilosophiePage onContactClick={handleContactClick} />} />
          <Route path="/team" element={<TeamPage />} />
          <Route path="/impressum" element={<ImpressumPage />} />
          <Route path="/datenschutz" element={<DatenschutzPage />} />
          <Route path="/insights" element={<InsightsIndexPage onContactClick={handleContactClick} />} />
          <Route path="/insights/:slug" element={<InsightDetailPage onContactClick={handleContactClick} />} />
          <Route path="/readiness" element={<ReadinessPage onContactClick={handleContactClick} />} />
          <Route path="/ki" element={<KiPage onContactClick={handleContactClick} />} />
          <Route path="/loading-demo" element={<LoadingDemoPage />} />
          
          {/* English Routes */}
          <Route path="/en" element={<HomePage onIncidentClick={handleIncidentClick} onContactClick={handleContactClick} />} />
          <Route path="/en/szenario/incident" element={<SzenarioPage szenarioKey="szenarioIncident" onIncidentClick={handleIncidentClick} onContactClick={handleContactClick} />} />
          <Route path="/en/szenario/nis2" element={<SzenarioPage szenarioKey="szenarioNis2" onIncidentClick={handleIncidentClick} onContactClick={handleContactClick} />} />
          <Route path="/en/szenario/threat-intelligence" element={<SzenarioPage szenarioKey="szenarioThreat" onIncidentClick={handleIncidentClick} onContactClick={handleContactClick} />} />
          <Route path="/en/szenario/pentest" element={<SzenarioPage szenarioKey="szenarioPentest" onIncidentClick={handleIncidentClick} onContactClick={handleContactClick} />} />
          <Route path="/en/philosophie" element={<PhilosophiePage onContactClick={handleContactClick} />} />
          <Route path="/en/team" element={<TeamPage />} />
          <Route path="/en/impressum" element={<ImpressumPage />} />
          <Route path="/en/datenschutz" element={<DatenschutzPage />} />
          <Route path="/en/insights" element={<InsightsIndexPage onContactClick={handleContactClick} />} />
          <Route path="/en/insights/:slug" element={<InsightDetailPage onContactClick={handleContactClick} />} />
          <Route path="/en/readiness" element={<ReadinessPage onContactClick={handleContactClick} />} />
          <Route path="/en/ki" element={<KiPage onContactClick={handleContactClick} />} />
        </Routes>
        
        {!isKiPage && <NotfallBanner onIncidentClick={handleIncidentClick} />}
        <CookieBanner />
        <IncidentIntakeModal isOpen={incidentModalOpen} onClose={() => setIncidentModalOpen(false)} />
        <ContactModal isOpen={contactModalOpen} onClose={() => setContactModalOpen(false)} requestType={contactType} />
      </div>
    </LanguageContext.Provider>
  );
};

function App() {
  return (
    <div className="App">
      <LoadingScreen />
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </div>
  );
}

export default App;
